import { OptionType } from '@/ui/components/Form/Select/types';

import { BillingItemMinimal } from './encounter';
import { PersonMinimal } from './person';

export interface NewRemitOptions {
  type: OptionType<string>[];
  payment_method: OptionType<string>[];
}

export interface RemitAdjustment {
  id?: number; // Read-only
  remit_service_line?: number | null;
  group_code: string; // Required
  reason_code: string; // Required
  amount?: string | null;
  amount_in_cents?: number | null;
  created_at?: string; // Read-only, format: date-time
  updated_at?: string; // Read-only, format: date-time
}

export interface RemitRemark {
  id?: number; // Read-only
  remit_service_line?: number | null;
  qualifier: string; // Required
  code: string; // Required
  created_at?: string; // Read-only, format: date-time
  updated_at?: string; // Read-only, format: date-time
}

export interface RemitServiceLine {
  id: number | null; // Read-only
  adjustments?: RemitAdjustment[]; // Read-only
  remarks?: RemitRemark[]; // Read-only
  billing_items?: BillingItemMinimal[]; // Read-only
  remit_claim: number; // Required
  start_on: string; // Required, format: date
  end_on: string; // Required, format: date
  code: string; // Required, maxLength: 5, minLength: 1
  modifier: string; // Required, maxLength: 20, minLength: 1
  qualifier: string; // Required, maxLength: 2, minLength: 1
  allowed_units: number; // Required, maximum: 2147483647, minimum: -2147483648
  billed_units: number; // Required, maximum: 2147483647, minimum: -2147483648
  charge_amount_in_cents?: number | null; // maximum: 2147483647, minimum: -2147483648
  paid_amount_in_cents?: number | null; // maximum: 2147483647, minimum: -2147483648
  revenue_code?: string | null; // maxLength: 10
  created_at?: string; // Read-only, format: date-time
  updated_at?: string; // Read-only, format: date-time
  claim_rows?: ClaimRow[] | null | undefined;
}

export interface RemitClaim {
  id?: number; // Read-only
  remit?: Remit;
  remit_id?: number | null;
  patient_control_number?: string; // Required, maxLength: 100, minLength: 1
  person?: PersonMinimal; // Read-only
  person_id?: string | null; // Required, format: uuid
  healthie_cms_1500?: string | null; // Required, format: uuid
  payer_classification?: 'primary' | 'secondary' | 'tertiary' | 'unspecified' | null;
  facility_type_code?: string | null; // maxLength: 50
  claim_frequency_code?: string | null; // maxLength: 50
  status?: number | null; // maximum: 2147483647, minimum: -2147483648
  charge_amount_in_cents?: number | null; // maximum: 2147483647, minimum: -2147483648
  paid_amount_in_cents?: number | null; // maximum: 2147483647, minimum: -2147483648
  patient_responsibility_amount_in_cents?: number | null; // maximum: 2147483647, minimum: -2147483648
  claim_filing_indicator_code?: string | null; // maxLength: 50
  payer_claim_control_number?: string | null; // maxLength: 100
  drg_code?: string | null; // maxLength: 50
  claim_received_on?: string | null; // format: date
  claim_statement_period_start_on?: string | null; // format: date
  claim_statement_period_end_on?: string | null; // format: date
  claim_coverage_expiration_on?: string | null; // format: date
  claim_contract_code?: string | null; // maxLength: 50
  claim_coverage_amount_in_cents?: number | null; // maximum: 2147483647, minimum: -2147483648
  corrected_insured_last_name?: string | null; // maxLength: 100
  corrected_insured_first_name?: string | null; // maxLength: 100
  corrected_insured_middle_name?: string | null; // maxLength: 100
  corrected_insured_prefix?: string | null; // maxLength: 50
  corrected_insured_suffix?: string | null; // maxLength: 50
  corrected_insured_id_qualifier?: string | null; // maxLength: 50
  corrected_insured_id?: string | null; // maxLength: 100
  service_lines: RemitServiceLine[]; // Read-only
  provider_entity_type_qualifier?: string | null; // maxLength: 50
  provider_id_qualifier?: string | null; // maxLength: 50
  provider_id?: string | null; // maxLength: 100
  provider_name?: string | null; // maxLength: 100
  provider_first_name?: string | null; // maxLength: 100
  provider_middle_name?: string | null; // maxLength: 100
  provider_suffix?: string | null; // maxLength: 50
  provider_prefix?: string | null; // maxLength: 50
  financial_info?: RemitFinancialInfo | null; // Read-only
  financial_info_id?: number | null; // Read-only
  subscriber_first_name?: string | null;
  subscriber_id?: string | null;
  subscriber_id_qualifier?: string | null;
  subscriber_last_name?: string | null;
  subscriber_middle_name?: string | null;
  subscriber_patient_relationship?: string | null;
  subscriber_prefix?: string | null;
  subscriber_suffix?: string | null;
  patient_first_name?: string | null;
  patient_id?: string | null;
  patient_id_qualifier?: string | null;
  patient_last_name?: string | null;
  patient_middle_name?: string | null;
  patient_name_prefix?: string | null;
  patient_name_suffix?: string | null;
}

export interface RemitFinancialInfo {
  id?: number;
  remit_id?: number | null; // Required
  check_amount?: string | null; // Required
  check_amount_in_cents?: number | null; // maximum: 2147483647, minimum: -2147483648
  check_or_eft_trace_number?: string | null; // maxLength: 100
  paid_at?: string | null; // Required, format: date-time
  transaction_set_control_no?: string | null; // maxLength: 100
  transaction_set_identifier_code?: string | null; // maxLength: 50
  payment_method?: string | null; // maxLength: 50
  routing_number?: string | null; // maxLength: 50
  payer_identifier?: string | null; // maxLength: 100
  originating_company_co_supplemental_code?: string | null; // maxLength: 100
  payer_name?: string | null; // maxLength: 100
  payer_address_line1?: string | null; // maxLength: 100
  payer_address_line2?: string | null; // maxLength: 100
  payer_city?: string | null; // maxLength: 100
  payer_state?: string | null; // maxLength: 50
  payer_zip?: string | null; // maxLength: 50
  payer_country?: string | null; // maxLength: 50
  other_payer_identifier?: string | null; // maxLength: 100
  // Duplicate Checking
  potential_duplicates?: RemitFinancialInfo[]; // Read-only
  amount_difference?: number | null; // Read-only
}

export interface Remit {
  id?: number; // Read-only
  type: string; //'paper' | 'electronic_835'; // Required
  ingested_at: string; // Required, format: date-time
  ingested_from?: string | null; // maxLength: 100
  financial_info?: RemitFinancialInfo[];
  first_financial_info?: RemitFinancialInfo;
  filename?: string | null; // maxLength: 100
  file?: string | null; // Read-only, format: uri
  parent_filename?: string | null; // maxLength: 100
  transmission_on?: string | null; // format: date
  trace_type_code?: string | null; // maxLength: 50
  remit_claims?: RemitClaim[]; // Read-only
  status: string; // 'ingested' | 'processing' | 'processed' | 'error'; // Read-only
  polymorphic_id: string; // uuid
}

export const ClaimStatusRegistry = {
  1: { description: 'processed as primary', level: 'info' },
  2: { description: 'processed as secondary', level: 'info' },
  3: { description: 'processed as tertiary', level: 'info' },
  4: { description: 'denial', level: 'alert' },
  19: { description: 'processed as primary, forwarded to additional payer(s)', level: 'info' },
  20: { description: 'processed as secondary, forwarded to additional payer(s)', level: 'info' },
  21: { description: 'processed as tertiary, forwarded to additional payer(s)', level: 'info' },
  22: { description: 'reversal of previous payment', level: 'alert' }
};

export const ModifierRegistry = {
  A1: {
    modifier: 'A1',
    description: 'Dressing for one wound'
  },
  A2: {
    modifier: 'A2',
    description: 'Dressing for two wounds'
  },
  A3: {
    modifier: 'A3',
    description: 'Dressing for three wounds'
  },
  A4: {
    modifier: 'A4',
    description: 'Dressing for four wounds'
  },
  A5: {
    modifier: 'A5',
    description: 'Dressing for five wounds'
  },
  A6: {
    modifier: 'A6',
    description: 'Dressing for six wounds'
  },
  A7: {
    modifier: 'A7',
    description: 'Dressing for seven wounds'
  },
  A8: {
    modifier: 'A8',
    description: 'Dressing for eight wounds'
  },
  A9: {
    modifier: 'A9',
    description: 'Dressing for nine or more wounds'
  },
  AA: {
    modifier: 'AA',
    description: 'Anesthesia services performed personally by anesthesiologist'
  },
  AB: {
    modifier: 'AB',
    description:
      'Audiology service furnished personally by an audiologist without a physician/npp order for non-acute hearing assessment unrelated to disequilibrium, or hearing aids, or examinations for the purpose of prescribing, fitting, or changing hearing aids; service may be performed once every 12 months, per beneficiary'
  },
  AD: {
    modifier: 'AD',
    description:
      'Medical supervision by a physician: more than four concurrent anesthesia procedures'
  },
  AE: {
    modifier: 'AE',
    description: 'Registered dietician'
  },
  AF: {
    modifier: 'AF',
    description: 'Specialty physician'
  },
  AG: {
    modifier: 'AG',
    description: 'Primary physician'
  },
  AH: {
    modifier: 'AH',
    description: 'Clinical psychologist'
  },
  AI: {
    modifier: 'AI',
    description: 'Principal physician of record'
  },
  AJ: {
    modifier: 'AJ',
    description: 'Clinical social worker'
  },
  AK: {
    modifier: 'AK',
    description: 'Non participating physician'
  },
  AM: {
    modifier: 'AM',
    description: 'Physician, team member service'
  },
  AO: {
    modifier: 'AO',
    description: 'Alternate payment method declined by provider of service'
  },
  AP: {
    modifier: 'AP',
    description:
      'Determination of refractive state was not performed in the course of diagnostic ophthalmological examination'
  },
  AQ: {
    modifier: 'AQ',
    description:
      'Physician providing a service in an unlisted health professional shortage area (hpsa)'
  },
  AR: {
    modifier: 'AR',
    description: 'Physician provider services in a physician scarcity area'
  },
  AS: {
    modifier: 'AS',
    description:
      'Physician assistant, nurse practitioner, or clinical nurse specialist services for assistant at surgery'
  },
  AT: {
    modifier: 'AT',
    description:
      'Acute treatment (this modifier should be used when reporting service 98940, 98941, 98942)'
  },
  AU: {
    modifier: 'AU',
    description: 'Item furnished in conjunction with a urological, ostomy, or tracheostomy supply'
  },
  AV: {
    modifier: 'AV',
    description: 'Item furnished in conjunction with a prosthetic device, prosthetic or orthotic'
  },
  AW: {
    modifier: 'AW',
    description: 'Item furnished in conjunction with a surgical dressing'
  },
  AX: {
    modifier: 'AX',
    description: 'Item furnished in conjunction with dialysis services'
  },
  AY: {
    modifier: 'AY',
    description:
      'Item or service furnished to an esrd patient that is not for the treatment of esrd'
  },
  AZ: {
    modifier: 'AZ',
    description:
      'Physician providing a service in a dental health professional shortage area for the purpose of an electronic health record incentive payment'
  },
  BA: {
    modifier: 'BA',
    description: 'Item furnished in conjunction with parenteral enteral nutrition (pen) services'
  },
  BL: {
    modifier: 'BL',
    description: 'Special acquisition of blood and blood products'
  },
  BO: {
    modifier: 'BO',
    description: 'Orally administered nutrition, not by feeding tube'
  },
  BP: {
    modifier: 'BP',
    description:
      'The beneficiary has been informed of the purchase and rental options and has elected to purchase the item'
  },
  BR: {
    modifier: 'BR',
    description:
      'The beneficiary has been informed of the purchase and rental options and has elected to rent the item'
  },
  BU: {
    modifier: 'BU',
    description:
      'The beneficiary has been informed of the purchase and rental options and after 30 days has not informed the supplier of his/her decision'
  },
  CA: {
    modifier: 'CA',
    description:
      'Procedure payable only in the inpatient setting when performed emergently on an outpatient who expires prior to admission'
  },
  CB: {
    modifier: 'CB',
    description:
      "Service ordered by a renal dialysis facility (rdf) physician as part of the esrd beneficiary's dialysis benefit, is not part of the composite rate, and is separately reimbursable"
  },
  CC: {
    modifier: 'CC',
    description:
      "Procedure code change (use 'cc' when the procedure code submitted was changed either for administrative reasons or because an incorrect code was filed)"
  },
  CD: {
    modifier: 'CD',
    description:
      'Amcc test has been ordered by an esrd facility or mcp physician that is part of the composite rate and is not separately billable'
  },
  CE: {
    modifier: 'CE',
    description:
      'Amcc test has been ordered by an esrd facility or mcp physician that is a composite rate test but is beyond the normal frequency covered under the rate and is separately reimbursable based on medical necessity'
  },
  CF: {
    modifier: 'CF',
    description:
      'Amcc test has been ordered by an esrd facility or mcp physician that is not part of the composite rate and is separately billable'
  },
  CG: {
    modifier: 'CG',
    description: 'Policy criteria applied'
  },
  CH: {
    modifier: 'CH',
    description: '0 percent impaired, limited or restricted'
  },
  CI: {
    modifier: 'CI',
    description: 'At least 1 percent but less than 20 percent impaired, limited or restricted'
  },
  CJ: {
    modifier: 'CJ',
    description: 'At least 20 percent but less than 40 percent impaired, limited or restricted'
  },
  CK: {
    modifier: 'CK',
    description: 'At least 40 percent but less than 60 percent impaired, limited or restricted'
  },
  CL: {
    modifier: 'CL',
    description: 'At least 60 percent but less than 80 percent impaired, limited or restricted'
  },
  CM: {
    modifier: 'CM',
    description: 'At least 80 percent but less than 100 percent impaired, limited or restricted'
  },
  CN: {
    modifier: 'CN',
    description: '100 percent impaired, limited or restricted'
  },
  CO: {
    modifier: 'CO',
    description:
      'Outpatient occupational therapy services furnished in whole or in part by an occupational therapy assistant'
  },
  CP: {
    modifier: 'CP',
    description:
      'Adjunctive service related to a procedure assigned to a comprehensive ambulatory payment classification (c-apc) procedure, but reported on a different claim'
  },
  CQ: {
    modifier: 'CQ',
    description:
      'Outpatient physical therapy services furnished in whole or in part by a physical therapist assistant'
  },
  CR: {
    modifier: 'CR',
    description: 'Catastrophe/disaster related'
  },
  CS: {
    modifier: 'CS',
    description:
      'Cost-sharing waived for specified covid-19 testing-related services that result in and order for or administration of a covid-19 test and/or used for cost-sharing waived preventive services furnished via telehealth in rural health clinics and federally qualified health centers during the covid-19 public health emergency'
  },
  CT: {
    modifier: 'CT',
    description:
      'Computed tomography services furnished using equipment that does not meet each of the attributes of the national electrical manufacturers association (nema) xr-29-2013 standard'
  },
  E1: {
    modifier: 'E1',
    description: 'Upper left, eyelid'
  },
  E2: {
    modifier: 'E2',
    description: 'Lower left, eyelid'
  },
  E3: {
    modifier: 'E3',
    description: 'Upper right, eyelid'
  },
  E4: {
    modifier: 'E4',
    description: 'Lower right, eyelid'
  },
  EA: {
    modifier: 'EA',
    description:
      'Erythropoetic stimulating agent (esa) administered to treat anemia due to anti-cancer chemotherapy'
  },
  EB: {
    modifier: 'EB',
    description:
      'Erythropoetic stimulating agent (esa) administered to treat anemia due to anti-cancer radiotherapy'
  },
  EC: {
    modifier: 'EC',
    description:
      'Erythropoetic stimulating agent (esa) administered to treat anemia not due to anti-cancer radiotherapy or anti-cancer chemotherapy'
  },
  ED: {
    modifier: 'ED',
    description:
      'Hematocrit level has exceeded 39% (or hemoglobin level has exceeded 13.0 g/dl) for 3 or more consecutive billing cycles immediately prior to and including the current cycle'
  },
  EE: {
    modifier: 'EE',
    description:
      'Hematocrit level has not exceeded 39% (or hemoglobin level has not exceeded 13.0 g/dl) for 3 or more consecutive billing cycles immediately prior to and including the current cycle'
  },
  EJ: {
    modifier: 'EJ',
    description:
      'Subsequent claims for a defined course of therapy, e.g., epo, sodium hyaluronate, infliximab'
  },
  EM: {
    modifier: 'EM',
    description: 'Emergency reserve supply (for esrd benefit only)'
  },
  EP: {
    modifier: 'EP',
    description:
      'Service provided as part of medicaid early periodic screening diagnosis and treatment (epsdt) program'
  },
  ER: {
    modifier: 'ER',
    description: 'Items and services furnished by a provider-based, off-campus emergency department'
  },
  ET: {
    modifier: 'ET',
    description: 'Emergency services'
  },
  EX: {
    modifier: 'EX',
    description: 'Expatriate beneficiary'
  },
  EY: {
    modifier: 'EY',
    description:
      'No physician or other licensed health care provider order for this item or service'
  },
  F1: {
    modifier: 'F1',
    description: 'Left hand, second digit'
  },
  F2: {
    modifier: 'F2',
    description: 'Left hand, third digit'
  },
  F3: {
    modifier: 'F3',
    description: 'Left hand, fourth digit'
  },
  F4: {
    modifier: 'F4',
    description: 'Left hand, fifth digit'
  },
  F5: {
    modifier: 'F5',
    description: 'Right hand, thumb'
  },
  F6: {
    modifier: 'F6',
    description: 'Right hand, second digit'
  },
  F7: {
    modifier: 'F7',
    description: 'Right hand, third digit'
  },
  F8: {
    modifier: 'F8',
    description: 'Right hand, fourth digit'
  },
  F9: {
    modifier: 'F9',
    description: 'Right hand, fifth digit'
  },
  FA: {
    modifier: 'FA',
    description: 'Left hand, thumb'
  },
  FB: {
    modifier: 'FB',
    description:
      'Item provided without cost to provider, supplier or practitioner, or full credit received for replaced device (examples, but not limited to, covered under warranty, replaced due to defect, free samples)'
  },
  FC: {
    modifier: 'FC',
    description: 'Partial credit received for replaced device'
  },
  FP: {
    modifier: 'FP',
    description: 'Service provided as part of family planning program'
  },
  FQ: {
    modifier: 'FQ',
    description: 'The service was furnished using audio-only communication technology'
  },
  FR: {
    modifier: 'FR',
    description:
      'The supervising practitioner was present through two-way, audio/video communication technology'
  },
  FS: {
    modifier: 'FS',
    description: 'Split (or shared) evaluation and management visit'
  },
  FT: {
    modifier: 'FT',
    description:
      'Unrelated evaluation and management (e/m) visit on the same day as another e/m visit or during a global procedure (preoperative, postoperative period, or on the same day as the procedure, as applicable). (report when an e/m visit is furnished within the global period but is unrelated, or when one or more additional e/m visits furnished on the same day are unrelated)'
  },
  FX: {
    modifier: 'FX',
    description: 'X-ray taken using film'
  },
  FY: {
    modifier: 'FY',
    description: 'X-ray taken using computed radiography technology/cassette-based imaging'
  },
  G0: {
    modifier: 'G0',
    description:
      'Telehealth services for diagnosis, evaluation, or treatment, of symptoms of an acute stroke'
  },
  G1: {
    modifier: 'G1',
    description: 'Most recent urr reading of less than 60'
  },
  G2: {
    modifier: 'G2',
    description: 'Most recent urr reading of 60 to 64.9'
  },
  G3: {
    modifier: 'G3',
    description: 'Most recent urr reading of 65 to 69.9'
  },
  G4: {
    modifier: 'G4',
    description: 'Most recent urr reading of 70 to 74.9'
  },
  G5: {
    modifier: 'G5',
    description: 'Most recent urr reading of 75 or greater'
  },
  G6: {
    modifier: 'G6',
    description:
      'Esrd patient for whom less than six dialysis sessions have been provided in a month'
  },
  G7: {
    modifier: 'G7',
    description:
      'Pregnancy resulted from rape or incest or pregnancy certified by physician as life threatening'
  },
  G8: {
    modifier: 'G8',
    description:
      'Monitored anesthesia care (mac) for deep complex, complicated, or markedly invasive surgical procedure'
  },
  G9: {
    modifier: 'G9',
    description:
      'Monitored anesthesia care for patient who has history of severe cardio-pulmonary condition'
  },
  GA: {
    modifier: 'GA',
    description: 'Waiver of liability statement issued as required by payer policy, individual case'
  },
  GB: {
    modifier: 'GB',
    description:
      'Claim being re-submitted for payment because it is no longer covered under a global payment demonstration'
  },
  GC: {
    modifier: 'GC',
    description:
      'This service has been performed in part by a resident under the direction of a teaching physician'
  },
  GD: {
    modifier: 'GD',
    description:
      'Units of service exceeds medically unlikely edit value and represents reasonable and necessary services'
  },
  GE: {
    modifier: 'GE',
    description:
      'This service has been performed by a resident without the presence of a teaching physician under the primary care exception'
  },
  GF: {
    modifier: 'GF',
    description:
      'Non-physician (e.g. nurse practitioner (np), certified registered nurse anesthetist (crna), certified registered nurse (crn), clinical nurse specialist (cns), physician assistant (pa)) services in a critical access hospital'
  },
  GG: {
    modifier: 'GG',
    description:
      'Performance and payment of a screening mammogram and diagnostic mammogram on the same patient, same day'
  },
  GH: {
    modifier: 'GH',
    description: 'Diagnostic mammogram converted from screening mammogram on same day'
  },
  GJ: {
    modifier: 'GJ',
    description: '"opt out" physician or practitioner emergency or urgent service'
  },
  GK: {
    modifier: 'GK',
    description: 'Reasonable and necessary item/service associated with a ga or gz modifier'
  },
  GL: {
    modifier: 'GL',
    description:
      'Medically unnecessary upgrade provided instead of non-upgraded item, no charge, no advance beneficiary notice (abn)'
  },
  GM: {
    modifier: 'GM',
    description: 'Multiple patients on one ambulance trip'
  },
  GN: {
    modifier: 'GN',
    description: 'Services delivered under an outpatient speech language pathology plan of care'
  },
  GO: {
    modifier: 'GO',
    description: 'Services delivered under an outpatient occupational therapy plan of care'
  },
  GP: {
    modifier: 'GP',
    description: 'Services delivered under an outpatient physical therapy plan of care'
  },
  GQ: {
    modifier: 'GQ',
    description: 'Via asynchronous telecommunications system'
  },
  GR: {
    modifier: 'GR',
    description:
      'This service was performed in whole or in part by a resident in a department of veterans affairs medical center or clinic, supervised in accordance with va policy'
  },
  GS: {
    modifier: 'GS',
    description:
      'Dosage of erythropoietin stimulating agent has been reduced and maintained in response to hematocrit or hemoglobin level'
  },
  GT: {
    modifier: 'GT',
    description: 'Via interactive audio and video telecommunication systems'
  },
  GU: {
    modifier: 'GU',
    description: 'Waiver of liability statement issued as required by payer policy, routine notice'
  },
  GV: {
    modifier: 'GV',
    description:
      "Attending physician not employed or paid under arrangement by the patient's hospice provider"
  },
  GW: {
    modifier: 'GW',
    description: "Service not related to the hospice patient's terminal condition"
  },
  GX: {
    modifier: 'GX',
    description: 'Notice of liability issued, voluntary under payer policy'
  },
  GY: {
    modifier: 'GY',
    description:
      'Item or service statutorily excluded, does not meet the definition of any medicare benefit or, for non-medicare insurers, is not a contract benefit'
  },
  GZ: {
    modifier: 'GZ',
    description: 'Item or service expected to be denied as not reasonable and necessary'
  },
  H9: {
    modifier: 'H9',
    description: 'Court-ordered'
  },
  HA: {
    modifier: 'HA',
    description: 'Child/adolescent program'
  },
  HB: {
    modifier: 'HB',
    description: 'Adult program, non geriatric'
  },
  HC: {
    modifier: 'HC',
    description: 'Adult program, geriatric'
  },
  HD: {
    modifier: 'HD',
    description: "Pregnant/parenting women's program"
  },
  HE: {
    modifier: 'HE',
    description: 'Mental health program'
  },
  HF: {
    modifier: 'HF',
    description: 'Substance abuse program'
  },
  HG: {
    modifier: 'HG',
    description: 'Opioid addiction treatment program'
  },
  HH: {
    modifier: 'HH',
    description: 'Integrated mental health/substance abuse program'
  },
  HI: {
    modifier: 'HI',
    description:
      'Integrated mental health and intellectual disability/developmental disabilities program'
  },
  HJ: {
    modifier: 'HJ',
    description: 'Employee assistance program'
  },
  HK: {
    modifier: 'HK',
    description: 'Specialized mental health programs for high-risk populations'
  },
  HL: {
    modifier: 'HL',
    description: 'Intern'
  },
  HM: {
    modifier: 'HM',
    description: 'Less than bachelor degree level'
  },
  HN: {
    modifier: 'HN',
    description: 'Bachelors degree level'
  },
  HO: {
    modifier: 'HO',
    description: 'Masters degree level'
  },
  HP: {
    modifier: 'HP',
    description: 'Doctoral level'
  },
  HQ: {
    modifier: 'HQ',
    description: 'Group setting'
  },
  HR: {
    modifier: 'HR',
    description: 'Family/couple with client present'
  },
  HS: {
    modifier: 'HS',
    description: 'Family/couple without client present'
  },
  HT: {
    modifier: 'HT',
    description: 'Multi-disciplinary team'
  },
  HU: {
    modifier: 'HU',
    description: 'Funded by child welfare agency'
  },
  HV: {
    modifier: 'HV',
    description: 'Funded state addictions agency'
  },
  HW: {
    modifier: 'HW',
    description: 'Funded by state mental health agency'
  },
  HX: {
    modifier: 'HX',
    description: 'Funded by county/local agency'
  },
  HY: {
    modifier: 'HY',
    description: 'Funded by juvenile justice agency'
  },
  HZ: {
    modifier: 'HZ',
    description: 'Funded by criminal justice agency'
  },
  J1: {
    modifier: 'J1',
    description: 'Competitive acquisition program no-pay submission for a prescription number'
  },
  J2: {
    modifier: 'J2',
    description:
      'Competitive acquisition program, restocking of emergency drugs after emergency administration'
  },
  J3: {
    modifier: 'J3',
    description:
      'Competitive acquisition program (cap), drug not available through cap as written, reimbursed under average sales price methodology'
  },
  J4: {
    modifier: 'J4',
    description:
      'Dmepos item subject to dmepos competitive bidding program that is furnished by a hospital upon discharge'
  },
  J5: {
    modifier: 'J5',
    description:
      'Off-the-shelf orthotic subject to dmepos competitive bidding program that is furnished as part of a physical therapist or occupational therapist professional service'
  },
  JA: {
    modifier: 'JA',
    description: 'Administered intravenously'
  },
  JB: {
    modifier: 'JB',
    description: 'Administered subcutaneously'
  },
  JC: {
    modifier: 'JC',
    description: 'Skin substitute used as a graft'
  },
  JD: {
    modifier: 'JD',
    description: 'Skin substitute not used as a graft'
  },
  JE: {
    modifier: 'JE',
    description: 'Administered via dialysate'
  },
  JF: {
    modifier: 'JF',
    description: 'Compounded drug'
  },
  JG: {
    modifier: 'JG',
    description:
      'Drug or biological acquired with 340b drug pricing program discount, reported for informational purposes'
  },
  JK: {
    modifier: 'JK',
    description: 'One month supply or less of drug or biological'
  },
  JL: {
    modifier: 'JL',
    description: 'Three month supply of drug or biological'
  },
  JW: {
    modifier: 'JW',
    description: 'Drug amount discarded/not administered to any patient'
  },
  JZ: {
    modifier: 'JZ',
    description: 'Zero drug amount discarded/not administered to any patient'
  },
  K0: {
    modifier: 'K0',
    description:
      'Lower extremity prosthesis functional level 0 - does not have the ability or potential to ambulate or transfer safely with or without assistance and a prosthesis does not enhance their quality of life or mobility.'
  },
  K1: {
    modifier: 'K1',
    description:
      'Lower extremity prosthesis functional level 1 - has the ability or potential to use a prosthesis for transfers or ambulation on level surfaces at fixed cadence. typical of the limited and unlimited household ambulator.'
  },
  K2: {
    modifier: 'K2',
    description:
      'Lower extremity prosthesis functional level 2 - has the ability or potential for ambulation with the ability to traverse low level environmental barriers such as curbs, stairs or uneven surfaces. typical of the limited community ambulator.'
  },
  K3: {
    modifier: 'K3',
    description:
      'Lower extremity prosthesis functional level 3 - has the ability or potential for ambulation with variable cadence. typical of the community ambulator who has the ability to transverse most environmental barriers and may have vocational, therapeutic, or exercise activity that demands prosthetic utilization beyond simple locomotion.'
  },
  K4: {
    modifier: 'K4',
    description:
      'Lower extremity prosthesis functional level 4 - has the ability or potential for prosthetic ambulation that exceeds the basic ambulation skills, exhibiting high impact, stress, or energy levels, typical of the prosthetic demands of the child, active adult, or athlete.'
  },
  KA: {
    modifier: 'KA',
    description: 'Add on option/accessory for wheelchair'
  },
  KB: {
    modifier: 'KB',
    description: 'Beneficiary requested upgrade for abn, more than 4 modifiers identified on claim'
  },
  KC: {
    modifier: 'KC',
    description: 'Replacement of special power wheelchair interface'
  },
  KD: {
    modifier: 'KD',
    description: 'Drug or biological infused through dme'
  },
  KE: {
    modifier: 'KE',
    description:
      'Bid under round one of the dmepos competitive bidding program for use with non-competitive bid base equipment'
  },
  KF: {
    modifier: 'KF',
    description: 'Item designated by fda as class iii device'
  },
  KG: {
    modifier: 'KG',
    description: 'Dmepos item subject to dmepos competitive bidding program number 1'
  },
  KH: {
    modifier: 'KH',
    description: 'Dmepos item, initial claim, purchase or first month rental'
  },
  KI: {
    modifier: 'KI',
    description: 'Dmepos item, second or third month rental'
  },
  KJ: {
    modifier: 'KJ',
    description:
      'Dmepos item, parenteral enteral nutrition (pen) pump or capped rental, months four to fifteen'
  },
  KK: {
    modifier: 'KK',
    description: 'Dmepos item subject to dmepos competitive bidding program number 2'
  },
  KL: {
    modifier: 'KL',
    description: 'Dmepos item delivered via mail'
  },
  KM: {
    modifier: 'KM',
    description: 'Replacement of facial prosthesis including new impression/moulage'
  },
  KN: {
    modifier: 'KN',
    description: 'Replacement of facial prosthesis using previous master model'
  },
  KO: {
    modifier: 'KO',
    description: 'Single drug unit dose formulation'
  },
  KP: {
    modifier: 'KP',
    description: 'First drug of a multiple drug unit dose formulation'
  },
  KQ: {
    modifier: 'KQ',
    description: 'Second or subsequent drug of a multiple drug unit dose formulation'
  },
  KR: {
    modifier: 'KR',
    description: 'Rental item, billing for partial month'
  },
  KS: {
    modifier: 'KS',
    description: 'Glucose monitor supply for diabetic beneficiary not treated with insulin'
  },
  KT: {
    modifier: 'KT',
    description:
      'Beneficiary resides in a competitive bidding area and travels outside that competitive bidding area and receives a competitive bid item'
  },
  KU: {
    modifier: 'KU',
    description: 'Dmepos item subject to dmepos competitive bidding program number 3'
  },
  KV: {
    modifier: 'KV',
    description:
      'Dmepos item subject to dmepos competitive bidding program that is furnished as part of a professional service'
  },
  KW: {
    modifier: 'KW',
    description: 'Dmepos item subject to dmepos competitive bidding program number 4'
  },
  KX: {
    modifier: 'KX',
    description: 'Requirements specified in the medical policy have been met'
  },
  KY: {
    modifier: 'KY',
    description: 'Dmepos item subject to dmepos competitive bidding program number 5'
  },
  KZ: {
    modifier: 'KZ',
    description: 'New coverage not implemented by managed care'
  },
  L1: {
    modifier: 'L1',
    description:
      'Provider attestation that the hospital laboratory test(s) is not packaged under the hospital opps'
  },
  LC: {
    modifier: 'LC',
    description: 'Left circumflex coronary artery'
  },
  LD: {
    modifier: 'LD',
    description: 'Left anterior descending coronary artery'
  },
  LL: {
    modifier: 'LL',
    description:
      "Lease/rental (use the 'll' modifier when dme equipment rental is to be applied against the purchase price)"
  },
  LM: {
    modifier: 'LM',
    description: 'Left main coronary artery'
  },
  LR: {
    modifier: 'LR',
    description: 'Laboratory round trip'
  },
  LS: {
    modifier: 'LS',
    description: 'Fda-monitored intraocular lens implant'
  },
  LT: {
    modifier: 'LT',
    description: 'Left side (used to identify procedures performed on the left side of the body)'
  },
  LU: {
    modifier: 'LU',
    description: 'Fractionated payment'
  },
  M2: {
    modifier: 'M2',
    description: 'Medicare secondary payer (msp)'
  },
  MA: {
    modifier: 'MA',
    description:
      'Ordering professional is not required to consult a clinical decision support mechanism due to service being rendered to a patient with a suspected or confirmed emergency medical condition'
  },
  MB: {
    modifier: 'MB',
    description:
      'Ordering professional is not required to consult a clinical decision support mechanism due to the significant hardship exception of insufficient internet access'
  },
  MC: {
    modifier: 'MC',
    description:
      'Ordering professional is not required to consult a clinical decision support mechanism due to the significant hardship exception of electronic health record or clinical decision support mechanism vendor issues'
  },
  MD: {
    modifier: 'MD',
    description:
      'Ordering professional is not required to consult a clinical decision support mechanism due to the significant hardship exception of extreme and uncontrollable circumstances'
  },
  ME: {
    modifier: 'ME',
    description:
      'The order for this service adheres to appropriate use criteria in the clinical decision support mechanism consulted by the ordering professional'
  },
  MF: {
    modifier: 'MF',
    description:
      'The order for this service does not adhere to the appropriate use criteria in the clinical decision support mechanism consulted by the ordering professional'
  },
  MG: {
    modifier: 'MG',
    description:
      'The order for this service does not have applicable appropriate use criteria in the qualified clinical decision support mechanism consulted by the ordering professional'
  },
  MH: {
    modifier: 'MH',
    description:
      'Unknown if ordering professional consulted a clinical decision support mechanism for this service, related information was not provided to the furnishing professional or provider'
  },
  MS: {
    modifier: 'MS',
    description:
      'Six month maintenance and servicing fee for reasonable and necessary parts and labor which are not covered under any manufacturer or supplier warranty'
  },
  N1: {
    modifier: 'N1',
    description: 'Group 1 oxygen coverage criteria met'
  },
  N2: {
    modifier: 'N2',
    description: 'Group 2 oxygen coverage criteria met'
  },
  N3: {
    modifier: 'N3',
    description: 'Group 3 oxygen coverage criteria met'
  },
  NB: {
    modifier: 'NB',
    description: 'Nebulizer system, any type, fda-cleared for use with specific drug'
  },
  NR: {
    modifier: 'NR',
    description:
      "New when rented (use the 'nr' modifier when dme which was new at the time of rental is subsequently purchased)"
  },
  NU: {
    modifier: 'NU',
    description: 'New equipment'
  },
  P1: {
    modifier: 'P1',
    description: 'A normal healthy patient'
  },
  P2: {
    modifier: 'P2',
    description: 'A patient with mild systemic disease'
  },
  P3: {
    modifier: 'P3',
    description: 'A patient with severe systemic disease'
  },
  P4: {
    modifier: 'P4',
    description: 'A patient with severe systemic disease that is a constant threat to life'
  },
  P5: {
    modifier: 'P5',
    description: 'A moribund patient who is not expected to survive without the operation'
  },
  P6: {
    modifier: 'P6',
    description: 'A declared brain-dead patient whose organs are being removed for donor purposes'
  },
  PA: {
    modifier: 'PA',
    description: 'Surgical or other invasive procedure on wrong body part'
  },
  PB: {
    modifier: 'PB',
    description: 'Surgical or other invasive procedure on wrong patient'
  },
  PC: {
    modifier: 'PC',
    description: 'Wrong surgery or other invasive procedure on patient'
  },
  PD: {
    modifier: 'PD',
    description:
      'Diagnostic or related non diagnostic item or service provided in a wholly owned or operated entity to a patient who is admitted as an inpatient within 3 days'
  },
  PI: {
    modifier: 'PI',
    description:
      'Positron emission tomography (pet) or pet/computed tomography (ct) to inform the initial treatment strategy of tumors that are biopsy proven or strongly suspected of being cancerous based on other diagnostic testing'
  },
  PL: {
    modifier: 'PL',
    description: 'Progressive addition lenses'
  },
  PM: {
    modifier: 'PM',
    description: 'Post mortem'
  },
  PN: {
    modifier: 'PN',
    description:
      'Non-excepted service provided at an off-campus, outpatient, provider-based department of a hospital'
  },
  PO: {
    modifier: 'PO',
    description:
      'Excepted service provided at an off-campus, outpatient, provider-based department of a hospital'
  },
  PS: {
    modifier: 'PS',
    description:
      "Positron emission tomography (pet) or pet/computed tomography (ct) to inform the subsequent treatment strategy of cancerous tumors when the beneficiary's treating physician determines that the pet study is needed to inform subsequent anti-tumor strategy"
  },
  PT: {
    modifier: 'PT',
    description: 'Colorectal cancer screening test; converted to diagnostic test or other procedure'
  },
  Q1: {
    modifier: 'Q1',
    description:
      'Routine clinical service provided in a clinical research study that is in an approved clinical research study'
  },
  Q2: {
    modifier: 'Q2',
    description: 'Demonstration procedure/service'
  },
  Q3: {
    modifier: 'Q3',
    description: 'Live kidney donor surgery and related services'
  },
  Q4: {
    modifier: 'Q4',
    description: 'Service for ordering/referring physician qualifies as a service exemption'
  },
  Q5: {
    modifier: 'Q5',
    description:
      'Service furnished under a reciprocal billing arrangement by a substitute physician or by a substitute physical therapist furnishing outpatient physical therapy services in a health professional shortage area, a medically underserved area, or a rural area'
  },
  Q6: {
    modifier: 'Q6',
    description:
      'Service furnished under a fee-for-time compensation arrangement by a substitute physician or by a substitute physical therapist furnishing outpatient physical therapy services in a health professional shortage area, a medically underserved area, or a rural area'
  },
  Q7: {
    modifier: 'Q7',
    description: 'One class a finding'
  },
  Q8: {
    modifier: 'Q8',
    description: 'Two class b findings'
  },
  Q9: {
    modifier: 'Q9',
    description: 'One class b and two class c findings'
  },
  QA: {
    modifier: 'QA',
    description:
      'Prescribed amounts of stationary oxygen for daytime use while at rest and nighttime use differ and the average of the two amounts is less than 1 liter per minute (lpm)'
  },
  QB: {
    modifier: 'QB',
    description:
      'Prescribed amounts of stationary oxygen for daytime use while at rest and nighttime use differ and the average of the two amounts exceeds 4 liters per minute (lpm) and portable oxygen is prescribed'
  },
  QC: {
    modifier: 'QC',
    description: 'Single channel monitoring'
  },
  QD: {
    modifier: 'QD',
    description: 'Recording and storage in solid state memory by a digital recorder'
  },
  QE: {
    modifier: 'QE',
    description:
      'Prescribed amount of stationary oxygen while at rest is less than 1 liter per minute (lpm)'
  },
  QF: {
    modifier: 'QF',
    description:
      'Prescribed amount of stationary oxygen while at rest exceeds 4 liters per minute (lpm) and portable oxygen is prescribed'
  },
  QG: {
    modifier: 'QG',
    description:
      'Prescribed amount of stationary oxygen while at rest is greater than 4 liters per minute (lpm)'
  },
  QH: {
    modifier: 'QH',
    description: 'Oxygen conserving device is being used with an oxygen delivery system'
  },
  QJ: {
    modifier: 'QJ',
    description:
      'Services/items provided to a prisoner or patient in state or local custody, however the state or local government, as applicable, meets the requirements in 42 cfr 411.4 (b)'
  },
  QK: {
    modifier: 'QK',
    description:
      'Medical direction of two, three, or four concurrent anesthesia procedures involving qualified individuals'
  },
  QL: {
    modifier: 'QL',
    description: 'Patient pronounced dead after ambulance called'
  },
  QM: {
    modifier: 'QM',
    description: 'Ambulance service provided under arrangement by a provider of services'
  },
  QN: {
    modifier: 'QN',
    description: 'Ambulance service furnished directly by a provider of services'
  },
  QP: {
    modifier: 'QP',
    description:
      'Documentation is on file showing that the laboratory test(s) was ordered individually or ordered as a cpt-recognized panel other than automated profile codes 80002-80019, g0058, g0059, and g0060.'
  },
  QQ: {
    modifier: 'QQ',
    description:
      'Ordering professional consulted a qualified clinical decision support mechanism for this service and the related data was provided to the furnishing professional'
  },
  QR: {
    modifier: 'QR',
    description:
      'Prescribed amounts of stationary oxygen for daytime use while at rest and nighttime use differ and the average of the two amounts is greater than 4 liters per minute (lpm)'
  },
  QS: {
    modifier: 'QS',
    description: 'Monitored anesthesia care service'
  },
  QT: {
    modifier: 'QT',
    description: 'Recording and storage on tape by an analog tape recorder'
  },
  QW: {
    modifier: 'QW',
    description: 'Clia waived test'
  },
  QX: {
    modifier: 'QX',
    description: 'Crna service: with medical direction by a physician'
  },
  QY: {
    modifier: 'QY',
    description:
      'Medical direction of one certified registered nurse anesthetist (crna) by an anesthesiologist'
  },
  QZ: {
    modifier: 'QZ',
    description: 'Crna service: without medical direction by a physician'
  },
  RA: {
    modifier: 'RA',
    description: 'Replacement of a dme, orthotic or prosthetic item'
  },
  RB: {
    modifier: 'RB',
    description:
      'Replacement of a part of a dme, orthotic or prosthetic item furnished as part of a repair'
  },
  RC: {
    modifier: 'RC',
    description: 'Right coronary artery'
  },
  RD: {
    modifier: 'RD',
    description: 'Drug provided to beneficiary, but not administered "incident-to"'
  },
  RE: {
    modifier: 'RE',
    description:
      'Furnished in full compliance with fda-mandated risk evaluation and mitigation strategy (rems)'
  },
  RI: {
    modifier: 'RI',
    description: 'Ramus intermedius coronary artery'
  },
  RR: {
    modifier: 'RR',
    description: "Rental (use the 'rr' modifier when dme is to be rented)"
  },
  RT: {
    modifier: 'RT',
    description: 'Right side (used to identify procedures performed on the right side of the body)'
  },
  SA: {
    modifier: 'SA',
    description: 'Nurse practitioner rendering service in collaboration with a physician'
  },
  SB: {
    modifier: 'SB',
    description: 'Nurse midwife'
  },
  SC: {
    modifier: 'SC',
    description: 'Medically necessary service or supply'
  },
  SD: {
    modifier: 'SD',
    description:
      'Services provided by registered nurse with specialized, highly technical home infusion training'
  },
  SE: {
    modifier: 'SE',
    description: 'State and/or federally-funded programs/services'
  },
  SF: {
    modifier: 'SF',
    description:
      'Second opinion ordered by a professional review organization (pro) per section 9401, p.l. 99-272 (100% reimbursement - no medicare deductible or coinsurance)'
  },
  SG: {
    modifier: 'SG',
    description: 'Ambulatory surgical center (asc) facility service'
  },
  SH: {
    modifier: 'SH',
    description: 'Second concurrently administered infusion therapy'
  },
  SJ: {
    modifier: 'SJ',
    description: 'Third or more concurrently administered infusion therapy'
  },
  SK: {
    modifier: 'SK',
    description: 'Member of high risk population (use only with codes for immunization)'
  },
  SL: {
    modifier: 'SL',
    description: 'State supplied vaccine'
  },
  SM: {
    modifier: 'SM',
    description: 'Second surgical opinion'
  },
  SN: {
    modifier: 'SN',
    description: 'Third surgical opinion'
  },
  SQ: {
    modifier: 'SQ',
    description: 'Item ordered by home health'
  },
  SS: {
    modifier: 'SS',
    description: 'Home infusion services provided in the infusion suite of the iv therapy provider'
  },
  ST: {
    modifier: 'ST',
    description: 'Related to trauma or injury'
  },
  SU: {
    modifier: 'SU',
    description:
      "Procedure performed in physician's office (to denote use of facility and equipment)"
  },
  SV: {
    modifier: 'SV',
    description: "Pharmaceuticals delivered to patient's home but not utilized"
  },
  SW: {
    modifier: 'SW',
    description: 'Services provided by a certified diabetic educator'
  },
  SY: {
    modifier: 'SY',
    description:
      'Persons who are in close contact with member of high-risk population (use only with codes for immunization)'
  },
  SZ: {
    modifier: 'SZ',
    description: 'Habilitative services'
  },
  T1: {
    modifier: 'T1',
    description: 'Left foot, second digit'
  },
  T2: {
    modifier: 'T2',
    description: 'Left foot, third digit'
  },
  T3: {
    modifier: 'T3',
    description: 'Left foot, fourth digit'
  },
  T4: {
    modifier: 'T4',
    description: 'Left foot, fifth digit'
  },
  T5: {
    modifier: 'T5',
    description: 'Right foot, great toe'
  },
  T6: {
    modifier: 'T6',
    description: 'Right foot, second digit'
  },
  T7: {
    modifier: 'T7',
    description: 'Right foot, third digit'
  },
  T8: {
    modifier: 'T8',
    description: 'Right foot, fourth digit'
  },
  T9: {
    modifier: 'T9',
    description: 'Right foot, fifth digit'
  },
  TA: {
    modifier: 'TA',
    description: 'Left foot, great toe'
  },
  TB: {
    modifier: 'TB',
    description:
      'Drug or biological acquired with 340b drug pricing program discount, reported for informational purposes for select entities'
  },
  TC: {
    modifier: 'TC',
    description:
      "Technical component; under certain circumstances, a charge may be made for the technical component alone; under those circumstances the technical component charge is identified by adding modifier 'tc' to the usual procedure number; technical component charges are institutional charges and not billed separately by physicians; however, portable x-ray suppliers only bill for technical component and should utilize modifier tc; the charge data from portable x-ray suppliers will then be used to build customary and prevailing profiles"
  },
  TD: {
    modifier: 'TD',
    description: 'Rn'
  },
  TE: {
    modifier: 'TE',
    description: 'Lpn/lvn'
  },
  TF: {
    modifier: 'TF',
    description: 'Intermediate level of care'
  },
  TG: {
    modifier: 'TG',
    description: 'Complex/high tech level of care'
  },
  TH: {
    modifier: 'TH',
    description: 'Obstetrical treatment/services, prenatal or postpartum'
  },
  TJ: {
    modifier: 'TJ',
    description: 'Program group, child and/or adolescent'
  },
  TK: {
    modifier: 'TK',
    description: 'Extra patient or passenger, non-ambulance'
  },
  TL: {
    modifier: 'TL',
    description: 'Early intervention/individualized family service plan (ifsp)'
  },
  TM: {
    modifier: 'TM',
    description: 'Individualized education program (iep)'
  },
  TN: {
    modifier: 'TN',
    description: "Rural/outside providers' customary service area"
  },
  TP: {
    modifier: 'TP',
    description: 'Medical transport, unloaded vehicle'
  },
  TQ: {
    modifier: 'TQ',
    description: 'Basic life support transport by a volunteer ambulance provider'
  },
  TR: {
    modifier: 'TR',
    description:
      'School-based individualized education program (iep) services provided outside the public school district responsible for the student'
  },
  TS: {
    modifier: 'TS',
    description: 'Follow-up service'
  },
  TT: {
    modifier: 'TT',
    description: 'Individualized service provided to more than one patient in same setting'
  },
  TU: {
    modifier: 'TU',
    description: 'Special payment rate, overtime'
  },
  TV: {
    modifier: 'TV',
    description: 'Special payment rates, holidays/weekends'
  },
  TW: {
    modifier: 'TW',
    description: 'Back-up equipment'
  },
  U1: {
    modifier: 'U1',
    description: 'Medicaid level of care 1, as defined by each state'
  },
  U2: {
    modifier: 'U2',
    description: 'Medicaid level of care 2, as defined by each state'
  },
  U3: {
    modifier: 'U3',
    description: 'Medicaid level of care 3, as defined by each state'
  },
  U4: {
    modifier: 'U4',
    description: 'Medicaid level of care 4, as defined by each state'
  },
  U5: {
    modifier: 'U5',
    description: 'Medicaid level of care 5, as defined by each state'
  },
  U6: {
    modifier: 'U6',
    description: 'Medicaid level of care 6, as defined by each state'
  },
  U7: {
    modifier: 'U7',
    description: 'Medicaid level of care 7, as defined by each state'
  },
  U8: {
    modifier: 'U8',
    description: 'Medicaid level of care 8, as defined by each state'
  },
  U9: {
    modifier: 'U9',
    description: 'Medicaid level of care 9, as defined by each state'
  },
  UA: {
    modifier: 'UA',
    description: 'Medicaid level of care 10, as defined by each state'
  },
  UB: {
    modifier: 'UB',
    description: 'Medicaid level of care 11, as defined by each state'
  },
  UC: {
    modifier: 'UC',
    description: 'Medicaid level of care 12, as defined by each state'
  },
  UD: {
    modifier: 'UD',
    description: 'Medicaid level of care 13, as defined by each state'
  },
  UE: {
    modifier: 'UE',
    description: 'Used durable medical equipment'
  },
  UF: {
    modifier: 'UF',
    description: 'Services provided in the morning'
  },
  UG: {
    modifier: 'UG',
    description: 'Services provided in the afternoon'
  },
  UH: {
    modifier: 'UH',
    description: 'Services provided in the evening'
  },
  UJ: {
    modifier: 'UJ',
    description: 'Services provided at night'
  },
  UK: {
    modifier: 'UK',
    description:
      'Services provided on behalf of the client to someone other than the client (collateral relationship)'
  },
  UN: {
    modifier: 'UN',
    description: 'Two patients served'
  },
  UP: {
    modifier: 'UP',
    description: 'Three patients served'
  },
  UQ: {
    modifier: 'UQ',
    description: 'Four patients served'
  },
  UR: {
    modifier: 'UR',
    description: 'Five patients served'
  },
  US: {
    modifier: 'US',
    description: 'Six or more patients served'
  },
  V1: {
    modifier: 'V1',
    description: 'Demonstration modifier 1'
  },
  V2: {
    modifier: 'V2',
    description: 'Demonstration modifier 2'
  },
  V3: {
    modifier: 'V3',
    description: 'Demonstration modifier 3'
  },
  V4: {
    modifier: 'V4',
    description: 'Demonstration modifier 4'
  },
  V5: {
    modifier: 'V5',
    description: 'Vascular catheter (alone or with any other vascular access)'
  },
  V6: {
    modifier: 'V6',
    description: 'Arteriovenous graft (or other vascular access not including a vascular catheter)'
  },
  V7: {
    modifier: 'V7',
    description: 'Arteriovenous fistula only (in use with two needles)'
  },
  V8: {
    modifier: 'V8',
    description: 'Infection present'
  },
  V9: {
    modifier: 'V9',
    description: 'No infection present'
  },
  VM: {
    modifier: 'VM',
    description: 'Medicare diabetes prevention program (mdpp) virtual make-up session'
  },
  VP: {
    modifier: 'VP',
    description: 'Aphakic patient'
  },
  X1: {
    modifier: 'X1',
    description:
      'Continuous/broad services: for reporting services by clinicians, who provide the principal care for a patient, with no planned endpoint of the relationship; services in this category represent comprehensive care, dealing with the entire scope of patient problems, either directly or in a care coordination role; reporting clinician service examples include, but are not limited to: primary care, and clinicians providing comprehensive care to patients in addition to specialty care'
  },
  X2: {
    modifier: 'X2',
    description:
      "Continuous/focused services: for reporting services by clinicians whose expertise is needed for the ongoing management of a chronic disease or a condition that needs to be managed and followed with no planned endpoint to the relationship; reporting clinician service examples include but are not limited to: a rheumatologist taking care of the patient's rheumatoid arthritis longitudinally but not providing general primary care services"
  },
  X3: {
    modifier: 'X3',
    description:
      "Episodic/broad servies: for reporting services by clinicians who have broad responsibility for the comprehensive needs of the patient that is limited to a defined period and circumstance such as a hospitalization; reporting clinician service examples include but are not limited to the hospitalist's services rendered providing comprehensive and general care to a patient while admitted to the hospital"
  },
  X4: {
    modifier: 'X4',
    description:
      'Episodic/focused services: for reporting services by clinicians who provide focused care on particular types of treatment limited to a defined period and circumstance; the patient has a problem, acute or chronic, that will be treated with surgery, radiation, or some other type of generally time-limited intervention; reporting clinician service examples include but are not limited to, the orthopedic surgeon performing a knee replacement and seeing the patient through the postoperative period'
  },
  X5: {
    modifier: 'X5',
    description:
      "Diagnostic services requested by another clinician: for reporting services by a clinician who furnishes care to the patient only as requested by another clinician or subsequent and related services requested by another clinician; this modifier is reported for patient relationships that may not be adequately captured by the above alternative categories; reporting clinician service examples include but are not limited to, the radiologist's interpretation of an imaging study requested by another clinician"
  },
  XE: {
    modifier: 'XE',
    description:
      'Separate encounter, a service that is distinct because it occurred during a separate encounter'
  },
  XP: {
    modifier: 'XP',
    description:
      'Separate practitioner, a service that is distinct because it was performed by a different practitioner'
  },
  XS: {
    modifier: 'XS',
    description:
      'Separate structure, a service that is distinct because it was performed on a separate organ/structure'
  },
  XU: {
    modifier: 'XU',
    description:
      'Unusual non-overlapping service, the use of a service that is distinct because it does not overlap usual components of the main service'
  },
  ZA: {
    modifier: 'ZA',
    description: 'Novartis/sandoz'
  },
  ZB: {
    modifier: 'ZB',
    description: 'Pfizer/hospira'
  },
  ZC: {
    modifier: 'ZC',
    description: 'Merck/samsung bioepis'
  }
};

export const CarcCodeRegistry = {
  '1': {
    code: '1',
    description: 'Deductible Amount'
  },
  '2': {
    code: '2',
    description: 'Coinsurance Amount'
  },
  '3': {
    code: '3',
    description: 'Co-payment Amount'
  },
  '4': {
    code: '4',
    description:
      'The procedure code is inconsistent with the modifier used. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '5': {
    code: '5',
    description:
      'The procedure code/type of bill is inconsistent with the place of service. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '6': {
    code: '6',
    description:
      "The procedure/revenue code is inconsistent with the patient's age. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present."
  },
  '7': {
    code: '7',
    description:
      "The procedure/revenue code is inconsistent with the patient's gender. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present."
  },
  '8': {
    code: '8',
    description:
      'The procedure code is inconsistent with the provider type/specialty (taxonomy). Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '9': {
    code: '9',
    description:
      "The diagnosis is inconsistent with the patient's age. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present."
  },
  '10': {
    code: '10',
    description:
      "The diagnosis is inconsistent with the patient's gender. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present."
  },
  '11': {
    code: '11',
    description:
      'The diagnosis is inconsistent with the procedure. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '12': {
    code: '12',
    description:
      'The diagnosis is inconsistent with the provider type. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '13': {
    code: '13',
    description: 'The date of death precedes the date of service.'
  },
  '14': {
    code: '14',
    description: 'The date of birth follows the date of service.'
  },
  '16': {
    code: '16',
    description:
      'Claim/service lacks information or has submission/billing error(s). Usage: Do not use this code for claims attachment(s)/other documentation. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.) Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '18': {
    code: '18',
    description:
      "Exact duplicate claim/service (Use only with Group Code OA except where state workers' compensation regulations requires CO)"
  },
  '19': {
    code: '19',
    description:
      "This is a work-related injury/illness and thus the liability of the Worker's Compensation Carrier."
  },
  '20': {
    code: '20',
    description: 'This injury/illness is covered by the liability carrier.'
  },
  '21': {
    code: '21',
    description: 'This injury/illness is the liability of the no-fault carrier.'
  },
  '22': {
    code: '22',
    description: 'This care may be covered by another payer per coordination of benefits.'
  },
  '23': {
    code: '23',
    description:
      'The impact of prior payer(s) adjudication including payments and/or adjustments. (Use only with Group Code OA)'
  },
  '24': {
    code: '24',
    description: 'Charges are covered under a capitation agreement/managed care plan.'
  },
  '26': {
    code: '26',
    description: 'Expenses incurred prior to coverage.'
  },
  '27': {
    code: '27',
    description: 'Expenses incurred after coverage terminated.'
  },
  '29': {
    code: '29',
    description: 'The time limit for filing has expired.'
  },
  '31': {
    code: '31',
    description: 'Patient cannot be identified as our insured.'
  },
  '32': {
    code: '32',
    description: 'Our records indicate the patient is not an eligible dependent.'
  },
  '33': {
    code: '33',
    description: 'Insured has no dependent coverage.'
  },
  '34': {
    code: '34',
    description: 'Insured has no coverage for newborns.'
  },
  '35': {
    code: '35',
    description: 'Lifetime benefit maximum has been reached.'
  },
  '39': {
    code: '39',
    description: 'Services denied at the time authorization/pre-certification was requested.'
  },
  '40': {
    code: '40',
    description:
      'Charges do not meet qualifications for emergent/urgent care. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '44': {
    code: '44',
    description: 'Prompt-pay discount.'
  },
  '45': {
    code: '45',
    description:
      'Charge exceeds fee schedule/maximum allowable or contracted/legislated fee arrangement. Usage: This adjustment amount cannot equal the total service or claim charge amount; and must not duplicate provider adjustment amounts (payments and contractual reductions) that have resulted from prior payer(s) adjudication. (Use only with Group Codes PR or CO depending upon liability)'
  },
  '49': {
    code: '49',
    description:
      'This is a non-covered service because it is a routine/preventive exam or a diagnostic/screening procedure done in conjunction with a routine/preventive exam. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '50': {
    code: '50',
    description:
      "These are non-covered services because this is not deemed a 'medical necessity' by the payer. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present."
  },
  '51': {
    code: '51',
    description:
      'These are non-covered services because this is a pre-existing condition. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '53': {
    code: '53',
    description:
      'Services by an immediate relative or a member of the same household are not covered.'
  },
  '54': {
    code: '54',
    description:
      'Multiple physicians/assistants are not covered in this case. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '55': {
    code: '55',
    description:
      'Procedure/treatment/drug is deemed experimental/investigational by the payer. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '56': {
    code: '56',
    description:
      "Procedure/treatment has not been deemed 'proven to be effective' by the payer. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present."
  },
  '58': {
    code: '58',
    description:
      'Treatment was deemed by the payer to have been rendered in an inappropriate or invalid place of service. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '59': {
    code: '59',
    description:
      'Processed based on multiple or concurrent procedure rules. (For example multiple surgery or diagnostic imaging, concurrent anesthesia.) Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '60': {
    code: '60',
    description:
      'Charges for outpatient services are not covered when performed within a period of time prior to or after inpatient services.'
  },
  '61': {
    code: '61',
    description: 'Adjusted for failure to obtain second surgical opinion'
  },
  '66': {
    code: '66',
    description: 'Blood Deductible.'
  },
  '69': {
    code: '69',
    description: 'Day outlier amount.'
  },
  '70': {
    code: '70',
    description: 'Cost outlier - Adjustment to compensate for additional costs.'
  },
  '74': {
    code: '74',
    description: 'Indirect Medical Education Adjustment.'
  },
  '75': {
    code: '75',
    description: 'Direct Medical Education Adjustment.'
  },
  '76': {
    code: '76',
    description: 'Disproportionate Share Adjustment.'
  },
  '78': {
    code: '78',
    description: 'Non-Covered days/Room charge adjustment.'
  },
  '85': {
    code: '85',
    description: 'Patient Interest Adjustment (Use Only Group code PR)'
  },
  '89': {
    code: '89',
    description: 'Professional fees removed from charges.'
  },
  '90': {
    code: '90',
    description: 'Ingredient cost adjustment. Usage: To be used for pharmaceuticals only.'
  },
  '91': {
    code: '91',
    description: 'Dispensing fee adjustment.'
  },
  '94': {
    code: '94',
    description: 'Processed in Excess of charges.'
  },
  '95': {
    code: '95',
    description: 'Plan procedures not followed.'
  },
  '96': {
    code: '96',
    description:
      'Non-covered charge(s). At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.) Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '97': {
    code: '97',
    description:
      'The benefit for this service is included in the payment/allowance for another service/procedure that has already been adjudicated. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '100': {
    code: '100',
    description: 'Payment made to patient/insured/responsible party.'
  },
  '101': {
    code: '101',
    description:
      'Predetermination: anticipated payment upon completion of services or claim adjudication.'
  },
  '102': {
    code: '102',
    description: 'Major Medical Adjustment.'
  },
  '103': {
    code: '103',
    description: 'Provider promotional discount (e.g., Senior citizen discount).'
  },
  '104': {
    code: '104',
    description: 'Managed care withholding.'
  },
  '105': {
    code: '105',
    description: 'Tax withholding.'
  },
  '106': {
    code: '106',
    description: 'Patient payment option/election not in effect.'
  },
  '107': {
    code: '107',
    description:
      'The related or qualifying claim/service was not identified on this claim. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '108': {
    code: '108',
    description:
      'Rent/purchase guidelines were not met. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '109': {
    code: '109',
    description:
      'Claim/service not covered by this payer/contractor. You must send the claim/service to the correct payer/contractor.'
  },
  '110': {
    code: '110',
    description: 'Billing date predates service date.'
  },
  '111': {
    code: '111',
    description: 'Not covered unless the provider accepts assignment.'
  },
  '112': {
    code: '112',
    description: 'Service not furnished directly to the patient and/or not documented.'
  },
  '114': {
    code: '114',
    description: 'Procedure/product not approved by the Food and Drug Administration.'
  },
  '115': {
    code: '115',
    description: 'Procedure postponed, canceled, or delayed.'
  },
  '116': {
    code: '116',
    description:
      'The advance indemnification notice signed by the patient did not comply with requirements.'
  },
  '117': {
    code: '117',
    description:
      'Transportation is only covered to the closest facility that can provide the necessary care.'
  },
  '118': {
    code: '118',
    description: 'ESRD network support adjustment.'
  },
  '119': {
    code: '119',
    description: 'Benefit maximum for this time period or occurrence has been reached.'
  },
  '121': {
    code: '121',
    description: 'Indemnification adjustment - compensation for outstanding member responsibility.'
  },
  '122': {
    code: '122',
    description: 'Psychiatric reduction.'
  },
  '128': {
    code: '128',
    description: "Newborn's services are covered in the mother's Allowance."
  },
  '129': {
    code: '129',
    description:
      'Prior processing information appears incorrect. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.)'
  },
  '130': {
    code: '130',
    description: 'Claim submission fee.'
  },
  '131': {
    code: '131',
    description: 'Claim specific negotiated discount.'
  },
  '132': {
    code: '132',
    description: 'Prearranged demonstration project adjustment.'
  },
  '133': {
    code: '133',
    description:
      'The disposition of this service line is pending further review. (Use only with Group Code OA). Usage: Use of this code requires a reversal and correction when the service line is finalized (use only in Loop 2110 CAS segment of the 835 or Loop 2430 of the 837).'
  },
  '134': {
    code: '134',
    description: 'Technical fees removed from charges.'
  },
  '135': {
    code: '135',
    description: 'Interim bills cannot be processed.'
  },
  '136': {
    code: '136',
    description: "Failure to follow prior payer's coverage rules. (Use only with Group Code OA)"
  },
  '137': {
    code: '137',
    description: 'Regulatory Surcharges, Assessments, Allowances or Health Related Taxes.'
  },
  '139': {
    code: '139',
    description:
      'Contracted funding agreement - Subscriber is employed by the provider of services. Use only with Group Code CO.'
  },
  '140': {
    code: '140',
    description: 'Patient/Insured health identification number and name do not match.'
  },
  '142': {
    code: '142',
    description: 'Monthly Medicaid patient liability amount.'
  },
  '143': {
    code: '143',
    description: 'Portion of payment deferred.'
  },
  '144': {
    code: '144',
    description: 'Incentive adjustment, e.g. preferred product/service.'
  },
  '146': {
    code: '146',
    description: 'Diagnosis was invalid for the date(s) of service reported.'
  },
  '147': {
    code: '147',
    description: 'Provider contracted/negotiated rate expired or not on file.'
  },
  '148': {
    code: '148',
    description:
      'Information from another provider was not provided or was insufficient/incomplete. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.)'
  },
  '149': {
    code: '149',
    description: 'Lifetime benefit maximum has been reached for this service/benefit category.'
  },
  '150': {
    code: '150',
    description: 'Payer deems the information submitted does not support this level of service.'
  },
  '151': {
    code: '151',
    description:
      'Payment adjusted because the payer deems the information submitted does not support this many/frequency of services.'
  },
  '152': {
    code: '152',
    description:
      'Payer deems the information submitted does not support this length of service. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '153': {
    code: '153',
    description: 'Payer deems the information submitted does not support this dosage.'
  },
  '154': {
    code: '154',
    description: "Payer deems the information submitted does not support this day's supply."
  },
  '155': {
    code: '155',
    description: 'Patient refused the service/procedure.'
  },
  '157': {
    code: '157',
    description: 'Service/procedure was provided as a result of an act of war.'
  },
  '158': {
    code: '158',
    description: 'Service/procedure was provided outside of the United States.'
  },
  '159': {
    code: '159',
    description: 'Service/procedure was provided as a result of terrorism.'
  },
  '160': {
    code: '160',
    description: 'Injury/illness was the result of an activity that is a benefit exclusion.'
  },
  '161': {
    code: '161',
    description: 'Provider performance bonus'
  },
  '163': {
    code: '163',
    description: 'Attachment/other documentation referenced on the claim was not received.'
  },
  '164': {
    code: '164',
    description:
      'Attachment/other documentation referenced on the claim was not received in a timely fashion.'
  },
  '166': {
    code: '166',
    description:
      'These services were submitted after this payers responsibility for processing claims under this plan ended.'
  },
  '167': {
    code: '167',
    description:
      'This (these) diagnosis(es) is (are) not covered. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '169': {
    code: '169',
    description: 'Alternate benefit has been provided.'
  },
  '170': {
    code: '170',
    description:
      'Payment is denied when performed/billed by this type of provider. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '171': {
    code: '171',
    description:
      'Payment is denied when performed/billed by this type of provider in this type of facility. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '172': {
    code: '172',
    description:
      'Payment is adjusted when performed/billed by a provider of this specialty. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '173': {
    code: '173',
    description: 'Service/equipment was not prescribed by a physician.'
  },
  '174': {
    code: '174',
    description: 'Service was not prescribed prior to delivery.'
  },
  '175': {
    code: '175',
    description: 'Prescription is incomplete.'
  },
  '176': {
    code: '176',
    description: 'Prescription is not current.'
  },
  '177': {
    code: '177',
    description: 'Patient has not met the required eligibility requirements.'
  },
  '178': {
    code: '178',
    description: 'Patient has not met the required spend down requirements.'
  },
  '179': {
    code: '179',
    description:
      'Patient has not met the required waiting requirements. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '180': {
    code: '180',
    description: 'Patient has not met the required residency requirements.'
  },
  '181': {
    code: '181',
    description: 'Procedure code was invalid on the date of service.'
  },
  '182': {
    code: '182',
    description: 'Procedure modifier was invalid on the date of service.'
  },
  '183': {
    code: '183',
    description:
      'The referring provider is not eligible to refer the service billed. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '184': {
    code: '184',
    description:
      'The prescribing/ordering provider is not eligible to prescribe/order the service billed. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '185': {
    code: '185',
    description:
      'The rendering provider is not eligible to perform the service billed. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '186': {
    code: '186',
    description: 'Level of care change adjustment.'
  },
  '187': {
    code: '187',
    description:
      'Consumer Spending Account payments (includes but is not limited to Flexible Spending Account, Health Savings Account, Health Reimbursement Account, etc.)'
  },
  '188': {
    code: '188',
    description:
      'This product/procedure is only covered when used according to FDA recommendations.'
  },
  '189': {
    code: '189',
    description:
      "Not otherwise classified' or 'unlisted' procedure code (CPT/HCPCS) was billed when there is a specific procedure code for this procedure/service"
  },
  '190': {
    code: '190',
    description:
      'Payment is included in the allowance for a Skilled Nursing Facility (SNF) qualified stay.'
  },
  '192': {
    code: '192',
    description:
      'Non standard adjustment code from paper remittance. Usage: This code is to be used by providers/payers providing Coordination of Benefits information to another payer in the 837 transaction only. This code is only used when the non-standard code cannot be reasonably mapped to an existing Claims Adjustment Reason Code, specifically Deductible, Coinsurance and Co-payment.'
  },
  '193': {
    code: '193',
    description:
      'Original payment decision is being maintained. Upon review, it was determined that this claim was processed properly.'
  },
  '194': {
    code: '194',
    description:
      'Anesthesia performed by the operating physician, the assistant surgeon or the attending physician.'
  },
  '195': {
    code: '195',
    description: 'Refund issued to an erroneous priority payer for this claim/service.'
  },
  '197': {
    code: '197',
    description: 'Precertification/authorization/notification/pre-treatment absent.'
  },
  '198': {
    code: '198',
    description: 'Precertification/notification/authorization/pre-treatment exceeded.'
  },
  '199': {
    code: '199',
    description: 'Revenue code and Procedure code do not match.'
  },
  '200': {
    code: '200',
    description: 'Expenses incurred during lapse in coverage'
  },
  '201': {
    code: '201',
    description:
      "Patient is responsible for amount of this claim/service through 'set aside arrangement' or other agreement. (Use only with Group Code PR) At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.)"
  },
  '202': {
    code: '202',
    description: 'Non-covered personal comfort or convenience services.'
  },
  '203': {
    code: '203',
    description: 'Discontinued or reduced service.'
  },
  '204': {
    code: '204',
    description:
      "This service/equipment/drug is not covered under the patient's current benefit plan"
  },
  '205': {
    code: '205',
    description: 'Pharmacy discount card processing fee'
  },
  '206': {
    code: '206',
    description: 'National Provider Identifier - missing.'
  },
  '207': {
    code: '207',
    description: 'National Provider identifier - Invalid format'
  },
  '208': {
    code: '208',
    description: 'National Provider Identifier - Not matched.'
  },
  '209': {
    code: '209',
    description:
      'Per regulatory or other agreement. The provider cannot collect this amount from the patient. However, this amount may be billed to subsequent payer. Refund to patient if collected. (Use only with Group code OA)'
  },
  '210': {
    code: '210',
    description:
      'Payment adjusted because pre-certification/authorization not received in a timely fashion'
  },
  '211': {
    code: '211',
    description: 'National Drug Codes (NDC) not eligible for rebate, are not covered.'
  },
  '212': {
    code: '212',
    description: 'Administrative surcharges are not covered'
  },
  '213': {
    code: '213',
    description:
      'Non-compliance with the physician self referral prohibition legislation or payer policy.'
  },
  '215': {
    code: '215',
    description: 'Based on subrogation of a third party settlement'
  },
  '216': {
    code: '216',
    description: 'Based on the findings of a review organization'
  },
  '219': {
    code: '219',
    description:
      "Based on extent of injury. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') for the jurisdictional regulation. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF)."
  },
  '222': {
    code: '222',
    description:
      'Exceeds the contracted maximum number of hours/days/units by this provider for this period. This is not patient specific. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '223': {
    code: '223',
    description:
      'Adjustment code for mandated federal, state or local law/regulation that is not already covered by another code and is mandated before a new code can be created.'
  },
  '224': {
    code: '224',
    description:
      'Patient identification compromised by identity theft. Identity verification required for processing this and future claims.'
  },
  '225': {
    code: '225',
    description:
      'Penalty or Interest Payment by Payer (Only used for plan to plan encounter reporting within the 837)'
  },
  '226': {
    code: '226',
    description:
      'Information requested from the Billing/Rendering Provider was not provided or not provided timely or was insufficient/incomplete. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.)'
  },
  '227': {
    code: '227',
    description:
      'Information requested from the patient/insured/responsible party was not provided or was insufficient/incomplete. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.)'
  },
  '228': {
    code: '228',
    description:
      'Denied for failure of this provider, another provider or the subscriber to supply requested information to a previous payer for their adjudication'
  },
  '229': {
    code: '229',
    description:
      "Partial charge amount not considered by Medicare due to the initial claim Type of Bill being 12X. Usage: This code can only be used in the 837 transaction to convey Coordination of Benefits information when the secondary payer's cost avoidance policy allows providers to bypass claim submission to a prior payer. (Use only with Group Code PR)"
  },
  '231': {
    code: '231',
    description:
      'Mutually exclusive procedures cannot be done in the same day/setting. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '232': {
    code: '232',
    description:
      'Institutional Transfer Amount. Usage: Applies to institutional claims only and explains the DRG amount difference when the patient care crosses multiple institutions.'
  },
  '233': {
    code: '233',
    description:
      'Services/charges related to the treatment of a hospital-acquired condition or preventable medical error.'
  },
  '234': {
    code: '234',
    description:
      'This procedure is not paid separately. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.)'
  },
  '235': {
    code: '235',
    description: 'Sales Tax'
  },
  '236': {
    code: '236',
    description:
      'This procedure or procedure/modifier combination is not compatible with another procedure or procedure/modifier combination provided on the same day according to the National Correct Coding Initiative or workers compensation state regulations/ fee schedule requirements.'
  },
  '237': {
    code: '237',
    description:
      'Legislated/Regulatory Penalty. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.)'
  },
  '238': {
    code: '238',
    description:
      'Claim spans eligible and ineligible periods of coverage, this is the reduction for the ineligible period. (Use only with Group Code PR)'
  },
  '239': {
    code: '239',
    description: 'Claim spans eligible and ineligible periods of coverage. Rebill separate claims.'
  },
  '240': {
    code: '240',
    description:
      "The diagnosis is inconsistent with the patient's birth weight. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present."
  },
  '241': {
    code: '241',
    description: 'Low Income Subsidy (LIS) Co-payment Amount'
  },
  '242': {
    code: '242',
    description: 'Services not provided by network/primary care providers.'
  },
  '243': {
    code: '243',
    description: 'Services not authorized by network/primary care providers.'
  },
  '245': {
    code: '245',
    description: 'Provider performance program withhold.'
  },
  '246': {
    code: '246',
    description: 'This non-payable code is for required reporting only.'
  },
  '247': {
    code: '247',
    description:
      'Deductible for Professional service rendered in an Institutional setting and billed on an Institutional claim.'
  },
  '248': {
    code: '248',
    description:
      'Coinsurance for Professional service rendered in an Institutional setting and billed on an Institutional claim.'
  },
  '249': {
    code: '249',
    description: 'This claim has been identified as a readmission. (Use only with Group Code CO)'
  },
  '250': {
    code: '250',
    description:
      'The attachment/other documentation that was received was the incorrect attachment/document. The expected attachment/document is still missing. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT).'
  },
  '251': {
    code: '251',
    description:
      'The attachment/other documentation that was received was incomplete or deficient. The necessary information is still needed to process the claim. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT).'
  },
  '252': {
    code: '252',
    description:
      'An attachment/other documentation is required to adjudicate this claim/service. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT).'
  },
  '253': {
    code: '253',
    description: 'Sequestration - reduction in federal payment'
  },
  '254': {
    code: '254',
    description:
      "Claim received by the dental plan, but benefits not available under this plan. Submit these services to the patient's medical plan for further consideration."
  },
  '256': {
    code: '256',
    description: 'Service not payable per managed care contract.'
  },
  '257': {
    code: '257',
    description:
      'The disposition of the claim/service is undetermined during the premium payment grace period, per Health Insurance Exchange requirements. This claim/service will be reversed and corrected when the grace period ends (due to premium payment or lack of premium payment). (Use only with Group Code OA)'
  },
  '258': {
    code: '258',
    description:
      'Claim/service not covered when patient is in custody/incarcerated. Applicable federal, state or local authority may cover the claim/service.'
  },
  '259': {
    code: '259',
    description: 'Additional payment for Dental/Vision service utilization.'
  },
  '260': {
    code: '260',
    description: 'Processed under Medicaid ACA Enhanced Fee Schedule'
  },
  '261': {
    code: '261',
    description: "The procedure or service is inconsistent with the patient's history."
  },
  '262': {
    code: '262',
    description: 'Adjustment for delivery cost. Usage: To be used for pharmaceuticals only.'
  },
  '263': {
    code: '263',
    description: 'Adjustment for shipping cost. Usage: To be used for pharmaceuticals only.'
  },
  '264': {
    code: '264',
    description: 'Adjustment for postage cost. Usage: To be used for pharmaceuticals only.'
  },
  '265': {
    code: '265',
    description: 'Adjustment for administrative cost. Usage: To be used for pharmaceuticals only.'
  },
  '266': {
    code: '266',
    description:
      'Adjustment for compound preparation cost. Usage: To be used for pharmaceuticals only.'
  },
  '267': {
    code: '267',
    description:
      'Claim/service spans multiple months. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.)'
  },
  '268': {
    code: '268',
    description: 'The Claim spans two calendar years. Please resubmit one claim per calendar year.'
  },
  '269': {
    code: '269',
    description:
      'Anesthesia not covered for this service/procedure. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '270': {
    code: '270',
    description:
      "Claim received by the medical plan, but benefits not available under this plan. Submit these services to the patient's dental plan for further consideration."
  },
  '271': {
    code: '271',
    description:
      'Prior contractual reductions related to a current periodic payment as part of a contractual payment schedule when deferred amounts have been previously reported. (Use only with Group Code OA)'
  },
  '272': {
    code: '272',
    description: 'Coverage/program guidelines were not met.'
  },
  '273': {
    code: '273',
    description: 'Coverage/program guidelines were exceeded.'
  },
  '274': {
    code: '274',
    description: 'Fee/Service not payable per patient Care Coordination arrangement.'
  },
  '275': {
    code: '275',
    description:
      "Prior payer's (or payers') patient responsibility (deductible, coinsurance, co-payment) not covered. (Use only with Group Code PR)"
  },
  '276': {
    code: '276',
    description: 'Services denied by the prior payer(s) are not covered by this payer.'
  },
  '277': {
    code: '277',
    description:
      'The disposition of the claim/service is undetermined during the premium payment grace period, per Health Insurance SHOP Exchange requirements. This claim/service will be reversed and corrected when the grace period ends (due to premium payment or lack of premium payment). (Use only with Group Code OA)'
  },
  '278': {
    code: '278',
    description:
      'Performance program proficiency requirements not met. (Use only with Group Codes CO or PI) Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '279': {
    code: '279',
    description:
      "Services not provided by Preferred network providers. Usage: Use this code when there are member network limitations. For example, using contracted providers not in the member's 'narrow' network."
  },
  '280': {
    code: '280',
    description:
      "Claim received by the medical plan, but benefits not available under this plan. Submit these services to the patient's Pharmacy plan for further consideration."
  },
  '281': {
    code: '281',
    description: 'Deductible waived per contractual agreement. Use only with Group Code CO.'
  },
  '282': {
    code: '282',
    description:
      'The procedure/revenue code is inconsistent with the type of bill. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  '283': {
    code: '283',
    description: 'Attending provider is not eligible to provide direction of care.'
  },
  '284': {
    code: '284',
    description:
      'Precertification/authorization/notification/pre-treatment number may be valid but does not apply to the billed services.'
  },
  '285': {
    code: '285',
    description: 'Appeal procedures not followed'
  },
  '286': {
    code: '286',
    description: 'Appeal time limits not met'
  },
  '287': {
    code: '287',
    description: 'Referral exceeded'
  },
  '288': {
    code: '288',
    description: 'Referral absent'
  },
  '289': {
    code: '289',
    description: 'Services considered under the dental and medical plans, benefits not available.'
  },
  '290': {
    code: '290',
    description:
      "Claim received by the dental plan, but benefits not available under this plan. Claim has been forwarded to the patient's medical plan for further consideration."
  },
  '291': {
    code: '291',
    description:
      "Claim received by the medical plan, but benefits not available under this plan. Claim has been forwarded to the patient's dental plan for further consideration."
  },
  '292': {
    code: '292',
    description:
      "Claim received by the medical plan, but benefits not available under this plan. Claim has been forwarded to the patient's pharmacy plan for further consideration."
  },
  '293': {
    code: '293',
    description: 'Payment made to employer.'
  },
  '294': {
    code: '294',
    description: 'Payment made to attorney.'
  },
  '295': {
    code: '295',
    description: 'Pharmacy Direct/Indirect Remuneration (DIR)'
  },
  '296': {
    code: '296',
    description:
      'Precertification/authorization/notification/pre-treatment number may be valid but does not apply to the provider.'
  },
  '297': {
    code: '297',
    description:
      "Claim received by the medical plan, but benefits not available under this plan. Submit these services to the patient's vision plan for further consideration."
  },
  '298': {
    code: '298',
    description:
      "Claim received by the medical plan, but benefits not available under this plan. Claim has been forwarded to the patient's vision plan for further consideration."
  },
  '299': {
    code: '299',
    description: 'The billing provider is not eligible to receive payment for the service billed.'
  },
  '300': {
    code: '300',
    description:
      "Claim received by the Medical Plan, but benefits not available under this plan. Claim has been forwarded to the patient's Behavioral Health Plan for further consideration."
  },
  '301': {
    code: '301',
    description:
      "Claim received by the Medical Plan, but benefits not available under this plan. Submit these services to the patient's Behavioral Health Plan for further consideration."
  },
  '302': {
    code: '302',
    description: 'Precertification/notification/authorization/pre-treatment time limit has expired.'
  },
  '303': {
    code: '303',
    description:
      "Prior payer's (or payers') patient responsibility (deductible, coinsurance, co-payment) not covered for Qualified Medicare and Medicaid Beneficiaries. (Use only with Group Code CO)"
  },
  '304': {
    code: '304',
    description:
      "Claim received by the medical plan, but benefits not available under this plan. Submit these services to the patient's hearing plan for further consideration."
  },
  '305': {
    code: '305',
    description:
      "Claim received by the medical plan, but benefits not available under this plan. Claim has been forwarded to the patient's hearing plan for further consideration."
  },
  A0: {
    code: 'A0',
    description: 'Patient refund amount.'
  },
  A1: {
    code: 'A1',
    description:
      'Claim/Service denied. At least one Remark Code must be provided (may be comprised of either the NCPDP Reject Reason Code, or Remittance Advice Remark Code that is not an ALERT.) The following will be added to this definition on 7/1/2023, Usage: Use this code only when a more specific Claim Adjustment Reason Code is not available.'
  },
  A5: {
    code: 'A5',
    description: 'Medicare Claim PPS Capital Cost Outlier Amount.'
  },
  A6: {
    code: 'A6',
    description: 'Prior hospitalization or 30 day transfer requirement not met.'
  },
  A8: {
    code: 'A8',
    description: 'Ungroupable DRG.'
  },
  B1: {
    code: 'B1',
    description: 'Non-covered visits.'
  },
  B4: {
    code: 'B4',
    description: 'Late filing penalty.'
  },
  B7: {
    code: 'B7',
    description:
      'This provider was not certified/eligible to be paid for this procedure/service on this date of service. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  B8: {
    code: 'B8',
    description:
      'Alternative services were available, and should have been utilized. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  B9: {
    code: 'B9',
    description: 'Patient is enrolled in a Hospice.'
  },
  B10: {
    code: 'B10',
    description:
      'Allowed amount has been reduced because a component of the basic procedure/test was paid. The beneficiary is not liable for more than the charge limit for the basic procedure/test.'
  },
  B11: {
    code: 'B11',
    description:
      'The claim/service has been transferred to the proper payer/processor for processing. Claim/service not covered by this payer/processor.'
  },
  B12: {
    code: 'B12',
    description: "Services not documented in patient's medical records."
  },
  B13: {
    code: 'B13',
    description:
      'Previously paid. Payment for this claim/service may have been provided in a previous payment.'
  },
  B14: {
    code: 'B14',
    description: 'Only one visit or consultation per physician per day is covered.'
  },
  B15: {
    code: 'B15',
    description:
      'This service/procedure requires that a qualifying service/procedure be received and covered. The qualifying other service/procedure has not been received/adjudicated. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present.'
  },
  B16: {
    code: 'B16',
    description: "New Patient' qualifications were not met."
  },
  B20: {
    code: 'B20',
    description: 'Procedure/service was partially or fully furnished by another provider.'
  },
  B22: {
    code: 'B22',
    description: 'This payment is adjusted based on the diagnosis.'
  },
  B23: {
    code: 'B23',
    description:
      'Procedure billed is not authorized per your Clinical Laboratory Improvement Amendment (CLIA) proficiency test.'
  },
  P1: {
    code: 'P1',
    description:
      'State-mandated Requirement for Property and Casualty, see Claim Payment Remarks Code for specific explanation. To be used for Property and Casualty only.'
  },
  P2: {
    code: 'P2',
    description:
      "Not a work related injury/illness and thus not the liability of the workers' compensation carrier Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') for the jurisdictional regulation. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF). To be used for Workers' Compensation only."
  },
  P3: {
    code: 'P3',
    description:
      "Workers' Compensation case settled. Patient is responsible for amount of this claim/service through WC 'Medicare set aside arrangement' or other agreement. To be used for Workers' Compensation only. (Use only with Group Code PR)"
  },
  P4: {
    code: 'P4',
    description:
      "Workers' Compensation claim adjudicated as non-compensable. This Payer not liable for claim or service/treatment. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') for the jurisdictional regulation. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF). To be used for Workers' Compensation only"
  },
  P5: {
    code: 'P5',
    description:
      'Based on payer reasonable and customary fees. No maximum allowable defined by legislated fee arrangement. To be used for Property and Casualty only.'
  },
  P6: {
    code: 'P6',
    description:
      "Based on entitlement to benefits. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') for the jurisdictional regulation. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF). To be used for Property and Casualty only."
  },
  P7: {
    code: 'P7',
    description:
      'The applicable fee schedule/fee database does not contain the billed code. Please resubmit a bill with the appropriate fee schedule/fee database code(s) that best describe the service(s) provided and supporting documentation if required. To be used for Property and Casualty only.'
  },
  P8: {
    code: 'P8',
    description:
      "Claim is under investigation. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') for the jurisdictional regulation. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF). To be used for Property and Casualty only."
  },
  P9: {
    code: 'P9',
    description:
      'No available or correlating CPT/HCPCS code to describe this service. To be used for Property and Casualty only.'
  },
  P10: {
    code: 'P10',
    description:
      'Payment reduced to zero due to litigation. Additional information will be sent following the conclusion of litigation. To be used for Property and Casualty only.'
  },
  P11: {
    code: 'P11',
    description:
      'The disposition of the related Property & Casualty claim (injury or illness) is pending due to litigation. To be used for Property and Casualty only. (Use only with Group Code OA)'
  },
  P12: {
    code: 'P12',
    description:
      "Workers' compensation jurisdictional fee schedule adjustment. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Class of Contract Code Identification Segment (Loop 2100 Other Claim Related Information REF). If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Workers' Compensation only."
  },
  P13: {
    code: 'P13',
    description:
      "Payment reduced or denied based on workers' compensation jurisdictional regulations or payment policies, use only if no other code is applicable. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') if the jurisdictional regulation applies. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Workers' Compensation only."
  },
  P14: {
    code: 'P14',
    description:
      'The Benefit for this Service is included in the payment/allowance for another service/procedure that has been performed on the same day. Usage: Refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information REF), if present. To be used for Property and Casualty only.'
  },
  P15: {
    code: 'P15',
    description:
      "Workers' Compensation Medical Treatment Guideline Adjustment. To be used for Workers' Compensation only."
  },
  P16: {
    code: 'P16',
    description:
      "Medical provider not authorized/certified to provide treatment to injured workers in this jurisdiction. To be used for Workers' Compensation only. (Use with Group Code CO or OA)"
  },
  P17: {
    code: 'P17',
    description:
      'Referral not authorized by attending physician per regulatory requirement. To be used for Property and Casualty only.'
  },
  P18: {
    code: 'P18',
    description:
      'Procedure is not listed in the jurisdiction fee schedule. An allowance has been made for a comparable service. To be used for Property and Casualty only.'
  },
  P19: {
    code: 'P19',
    description:
      'Procedure has a relative value of zero in the jurisdiction fee schedule, therefore no payment is due. To be used for Property and Casualty only.'
  },
  P20: {
    code: 'P20',
    description:
      'Service not paid under jurisdiction allowed outpatient facility fee schedule. To be used for Property and Casualty only.'
  },
  P21: {
    code: 'P21',
    description:
      "Payment denied based on the Medical Payments Coverage (MPC) and/or Personal Injury Protection (PIP) Benefits jurisdictional regulations, or payment policies. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') if the jurisdictional regulation applies. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty Auto only."
  },
  P22: {
    code: 'P22',
    description:
      "Payment adjusted based on the Medical Payments Coverage (MPC) and/or Personal Injury Protection (PIP) Benefits jurisdictional regulations, or payment policies. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') if the jurisdictional regulation applies. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty Auto only."
  },
  P23: {
    code: 'P23',
    description:
      'Medical Payments Coverage (MPC) or Personal Injury Protection (PIP) Benefits jurisdictional fee schedule adjustment. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Class of Contract Code Identification Segment (Loop 2100 Other Claim Related Information REF). If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty Auto only.'
  },
  P24: {
    code: 'P24',
    description:
      'Payment adjusted based on Preferred Provider Organization (PPO). Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Class of Contract Code Identification Segment (Loop 2100 Other Claim Related Information REF). If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty only. Use only with Group Code CO.'
  },
  P25: {
    code: 'P25',
    description:
      'Payment adjusted based on Medical Provider Network (MPN). Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Class of Contract Code Identification Segment (Loop 2100 Other Claim Related Information REF). If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty only. (Use only with Group Code CO).'
  },
  P26: {
    code: 'P26',
    description:
      'Payment adjusted based on Voluntary Provider network (VPN). Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Class of Contract Code Identification Segment (Loop 2100 Other Claim Related Information REF). If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty only. (Use only with Group Code CO).'
  },
  P27: {
    code: 'P27',
    description:
      "Payment denied based on the Liability Coverage Benefits jurisdictional regulations and/or payment policies. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') if the jurisdictional regulation applies. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty Auto only."
  },
  P28: {
    code: 'P28',
    description:
      "Payment adjusted based on the Liability Coverage Benefits jurisdictional regulations and/or payment policies. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Insurance Policy Number Segment (Loop 2100 Other Claim Related Information REF qualifier 'IG') if the jurisdictional regulation applies. If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty Auto only."
  },
  P29: {
    code: 'P29',
    description:
      'Liability Benefits jurisdictional fee schedule adjustment. Usage: If adjustment is at the Claim Level, the payer must send and the provider should refer to the 835 Class of Contract Code Identification Segment (Loop 2100 Other Claim Related Information REF). If adjustment is at the Line Level, the payer must send and the provider should refer to the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment information REF) if the regulations apply. To be used for Property and Casualty Auto only.'
  },
  P30: {
    code: 'P30',
    description:
      'Payment denied for exacerbation when supporting documentation was not complete. To be used for Property and Casualty only.'
  },
  P31: {
    code: 'P31',
    description:
      'Payment denied for exacerbation when treatment exceeds time allowed. To be used for Property and Casualty only.'
  },
  P32: {
    code: 'P32',
    description: 'Payment adjusted due to Apportionment.'
  }
};

export const CagcCodeRegistry = {
  CO: {
    code: 'CO',
    description: 'Contractual Obligation'
  },
  OA: {
    code: 'OA',
    description: 'Other Adjustment'
  },
  PI: {
    code: 'PI',
    description: 'Payor Initiated Reduction'
  },
  PR: {
    code: 'PR',
    description: 'Patient Responsibility'
  }
};

export const RarcCodeRegistry = {
  M1: {
    code: 'M1',
    description:
      'X-ray not taken within the past 12 months or near enough to the start of treatment.',
    is_alert: false
  },
  M2: {
    code: 'M2',
    description: 'Not paid separately when the patient is an inpatient.',
    is_alert: false
  },
  M3: {
    code: 'M3',
    description: 'Equipment is the same or similar to equipment already being used.',
    is_alert: false
  },
  M4: {
    code: 'M4',
    description:
      'Alert: This is the last monthly installment payment for this durable medical equipment.',
    is_alert: true
  },
  M5: {
    code: 'M5',
    description:
      'Monthly rental payments can continue until the earlier of the 15th month from the first rental month, or the month when the equipment is no longer needed.',
    is_alert: false
  },
  M6: {
    code: 'M6',
    description:
      'Alert: You must furnish and service this item for any period of medical need for the remainder of the reasonable useful lifetime of the equipment.',
    is_alert: true
  },
  M7: {
    code: 'M7',
    description:
      'No rental payments after the item is purchased, returned or after the total of issued rental payments equals the purchase price.',
    is_alert: false
  },
  M8: {
    code: 'M8',
    description:
      'We do not accept blood gas tests results when the test was conducted by a medical supplier or taken while the patient is on oxygen.',
    is_alert: false
  },
  M9: {
    code: 'M9',
    description:
      'Alert: This is the tenth rental month. You must offer the patient the choice of changing the rental to a purchase agreement.',
    is_alert: true
  },
  M10: {
    code: 'M10',
    description:
      'Equipment purchases are limited to the first or the tenth month of medical necessity.',
    is_alert: false
  },
  M11: {
    code: 'M11',
    description:
      "DME, orthotics and prosthetics must be billed to the DME carrier who services the patient's zip code.",
    is_alert: false
  },
  M12: {
    code: 'M12',
    description:
      'Diagnostic tests performed by a physician must indicate whether purchased services are included on the claim.',
    is_alert: false
  },
  M13: {
    code: 'M13',
    description: 'Only one initial visit is covered per specialty per medical group.',
    is_alert: false
  },
  M14: {
    code: 'M14',
    description:
      'No separate payment for an injection administered during an office visit, and no payment for a full office visit if the patient only received an injection.',
    is_alert: false
  },
  M15: {
    code: 'M15',
    description:
      'Separately billed services/tests have been bundled as they are considered components of the same procedure. Separate payment is not allowed.',
    is_alert: false
  },
  M16: {
    code: 'M16',
    description:
      'Alert: Please see our web site, mailings, or bulletins for more details concerning this policy/procedure/decision.',
    is_alert: true
  },
  M17: {
    code: 'M17',
    description:
      'Alert: Payment approved as you did not know, and could not reasonably have been expected to know, that this would not normally have been covered for this patient. In the future, you will be liable for charges for the same service(s) under the same or similar conditions.',
    is_alert: true
  },
  M18: {
    code: 'M18',
    description:
      "Certain services may be approved for home use. Neither a hospital nor a Skilled Nursing Facility (SNF) is considered to be a patient's home.",
    is_alert: false
  },
  M19: {
    code: 'M19',
    description: 'Missing oxygen certification/re-certification.',
    is_alert: false
  },
  M20: {
    code: 'M20',
    description: 'Missing/incomplete/invalid HCPCS.',
    is_alert: false
  },
  M21: {
    code: 'M21',
    description:
      'Missing/incomplete/invalid place of residence for this service/item provided in a home.',
    is_alert: false
  },
  M22: {
    code: 'M22',
    description: 'Missing/incomplete/invalid number of miles traveled.',
    is_alert: false
  },
  M23: {
    code: 'M23',
    description: 'Missing invoice.',
    is_alert: false
  },
  M24: {
    code: 'M24',
    description: 'Missing/incomplete/invalid number of doses per vial.',
    is_alert: false
  },
  M25: {
    code: 'M25',
    description:
      'The information furnished does not substantiate the need for this level of service. If you believe the service should have been fully covered as billed, or if you did not know and could not reasonably have been expected to know that we would not pay for this level of service, or if you notified the patient in writing in advance that we would not pay for this level of service and he/she agreed in writing to pay, ask us to review your claim within 120 days of the date of this notice. If you do not request an appeal, we will, upon application from the patient, reimburse him/her for the amount you have collected from him/her in excess of any deductible and coinsurance amounts. We will recover the reimbursement from you as an overpayment.',
    is_alert: false
  },
  M26: {
    code: 'M26',
    description:
      'The information furnished does not substantiate the need for this level of service. If you have collected any amount from the patient for this level of service/any amount that exceeds the limiting charge for the less extensive service, the law requires you to refund that amount to the patient within 30 days of receiving this notice.',
    is_alert: false
  },
  M27: {
    code: 'M27',
    description:
      "Alert: The patient has been relieved of liability of payment of these items and services under the limitation of liability provision of the law. The provider is ultimately liable for the patient's waived charges, including any charges for coinsurance, since the items or services were not reasonable and necessary or constituted custodial care, and you knew or could reasonably have been expected to know, that they were not covered. You may appeal this determination. You may ask for an appeal regarding both the coverage determination and the issue of whether you exercised due care. The appeal request must be filed within 120 days of the date you receive this notice. You must make the request through this office.",
    is_alert: true
  },
  M28: {
    code: 'M28',
    description:
      'This does not qualify for payment under Part B when Part A coverage is exhausted or not otherwise available.',
    is_alert: false
  },
  M29: {
    code: 'M29',
    description: 'Missing operative note/report.',
    is_alert: false
  },
  M30: {
    code: 'M30',
    description: 'Missing pathology report.',
    is_alert: false
  },
  M31: {
    code: 'M31',
    description: 'Missing radiology report.',
    is_alert: false
  },
  M32: {
    code: 'M32',
    description:
      "Alert: This is a conditional payment made pending a decision on this service by the patient's primary payer. This payment may be subject to refund upon your receipt of any additional payment for this service from another payer. You must contact this office immediately upon receipt of an additional payment for this service.",
    is_alert: true
  },
  M33: {
    code: 'M33',
    description: 'Missing/incomplete/invalid UPIN for the ordering/referring/performing provider.',
    is_alert: false
  },
  M34: {
    code: 'M34',
    description: 'Claim lacks the CLIA certification number.',
    is_alert: false
  },
  M35: {
    code: 'M35',
    description: 'Missing/incomplete/invalid pre-operative photos or visual field results.',
    is_alert: false
  },
  M36: {
    code: 'M36',
    description:
      'This is the 11th rental month. We cannot pay for this until you indicate that the patient has been given the option of changing the rental to a purchase.',
    is_alert: false
  },
  M37: {
    code: 'M37',
    description: 'Not covered when the patient is under age 35.',
    is_alert: false
  },
  M38: {
    code: 'M38',
    description:
      'Alert: The patient is liable for the charges for this service as they were informed in writing before the service was furnished that we would not pay for it and the patient agreed to be responsible for the charges.',
    is_alert: true
  },
  M39: {
    code: 'M39',
    description:
      'Alert: The patient is not liable for payment of this service as the advance notice of non-coverage you provided the patient did not comply with program requirements.',
    is_alert: true
  },
  M40: {
    code: 'M40',
    description: "Claim must be assigned and must be filed by the practitioner's employer.",
    is_alert: false
  },
  M41: {
    code: 'M41',
    description: 'We do not pay for this as the patient has no legal obligation to pay for this.',
    is_alert: false
  },
  M42: {
    code: 'M42',
    description: 'The medical necessity form must be personally signed by the attending physician.',
    is_alert: false
  },
  M43: {
    code: 'M43',
    description:
      'Payment for this service previously issued to you or another provider by another carrier/intermediary.',
    is_alert: false
  },
  M44: {
    code: 'M44',
    description: 'Missing/incomplete/invalid condition code.',
    is_alert: false
  },
  M45: {
    code: 'M45',
    description: 'Missing/incomplete/invalid occurrence code(s).',
    is_alert: false
  },
  M46: {
    code: 'M46',
    description: 'Missing/incomplete/invalid occurrence span code(s).',
    is_alert: false
  },
  M47: {
    code: 'M47',
    description:
      'Missing/incomplete/invalid Payer Claim Control Number. Other terms exist for this element including, but not limited to, Internal Control Number (ICN), Claim Control Number (CCN), Document Control Number (DCN).',
    is_alert: false
  },
  M48: {
    code: 'M48',
    description:
      'Payment for services furnished to hospital inpatients (other than professional services of physicians) can only be made to the hospital. You must request payment from the hospital rather than the patient for this service.',
    is_alert: false
  },
  M49: {
    code: 'M49',
    description: 'Missing/incomplete/invalid value code(s) or amount(s).',
    is_alert: false
  },
  M50: {
    code: 'M50',
    description: 'Missing/incomplete/invalid revenue code(s).',
    is_alert: false
  },
  M51: {
    code: 'M51',
    description: 'Missing/incomplete/invalid procedure code(s).',
    is_alert: false
  },
  M52: {
    code: 'M52',
    description: "Missing/incomplete/invalid 'from' date(s) of service.",
    is_alert: false
  },
  M53: {
    code: 'M53',
    description: 'Missing/incomplete/invalid days or units of service.',
    is_alert: false
  },
  M54: {
    code: 'M54',
    description: 'Missing/incomplete/invalid total charges.',
    is_alert: false
  },
  M55: {
    code: 'M55',
    description:
      'We do not pay for self-administered anti-emetic drugs that are not administered with a covered oral anti-cancer drug.',
    is_alert: false
  },
  M56: {
    code: 'M56',
    description: 'Missing/incomplete/invalid payer identifier.',
    is_alert: false
  },
  M57: {
    code: 'M57',
    description: 'Missing/incomplete/invalid provider identifier.',
    is_alert: false
  },
  M58: {
    code: 'M58',
    description: 'Missing/incomplete/invalid claim information. Resubmit claim after corrections.',
    is_alert: false
  },
  M59: {
    code: 'M59',
    description: "Missing/incomplete/invalid 'to' date(s) of service.",
    is_alert: false
  },
  M60: {
    code: 'M60',
    description: 'Missing Certificate of Medical Necessity.',
    is_alert: false
  },
  M61: {
    code: 'M61',
    description:
      'We cannot pay for this as the approval period for the FDA clinical trial has expired.',
    is_alert: false
  },
  M62: {
    code: 'M62',
    description: 'Missing/incomplete/invalid treatment authorization code.',
    is_alert: false
  },
  M63: {
    code: 'M63',
    description: 'We do not pay for more than one of these on the same day.',
    is_alert: false
  },
  M64: {
    code: 'M64',
    description: 'Missing/incomplete/invalid other diagnosis.',
    is_alert: false
  },
  M65: {
    code: 'M65',
    description:
      'One interpreting physician charge can be submitted per claim when a purchased diagnostic test is indicated. Please submit a separate claim for each interpreting physician.',
    is_alert: false
  },
  M66: {
    code: 'M66',
    description:
      'Our records indicate that you billed diagnostic tests subject to price limitations and the procedure code submitted includes a professional component. Only the technical component is subject to price limitations. Please submit the technical and professional components of this service as separate line items.',
    is_alert: false
  },
  M67: {
    code: 'M67',
    description: 'Missing/incomplete/invalid other procedure code(s).',
    is_alert: false
  },
  M68: {
    code: 'M68',
    description:
      'Missing/incomplete/invalid attending, ordering, rendering, supervising or referring physician identification.',
    is_alert: false
  },
  M69: {
    code: 'M69',
    description:
      'Paid at the regular rate as you did not submit documentation to justify the modified procedure code.',
    is_alert: false
  },
  M70: {
    code: 'M70',
    description:
      'Alert: The NDC code submitted for this service was translated to a HCPCS code for processing, but please continue to submit the NDC on future claims for this item.',
    is_alert: true
  },
  M71: {
    code: 'M71',
    description: 'Total payment reduced due to overlap of tests billed.',
    is_alert: false
  },
  M72: {
    code: 'M72',
    description: 'Did not enter full 8-digit date (MM/DD/CCYY).',
    is_alert: false
  },
  M73: {
    code: 'M73',
    description:
      'The HPSA/Physician Scarcity bonus can only be paid on the professional component of this service. Rebill as separate professional and technical components.',
    is_alert: false
  },
  M74: {
    code: 'M74',
    description: 'This service does not qualify for a HPSA/Physician Scarcity bonus payment.',
    is_alert: false
  },
  M75: {
    code: 'M75',
    description:
      'Multiple automated multichannel tests performed on the same day combined for payment.',
    is_alert: false
  },
  M76: {
    code: 'M76',
    description: 'Missing/incomplete/invalid diagnosis or condition.',
    is_alert: false
  },
  M77: {
    code: 'M77',
    description: 'Missing/incomplete/invalid/inappropriate place of service.',
    is_alert: false
  },
  M78: {
    code: 'M78',
    description: 'Missing/incomplete/invalid HCPCS modifier.',
    is_alert: false
  },
  M79: {
    code: 'M79',
    description: 'Missing/incomplete/invalid charge.',
    is_alert: false
  },
  M80: {
    code: 'M80',
    description:
      'Not covered when performed during the same session/date as a previously processed service for the patient.',
    is_alert: false
  },
  M81: {
    code: 'M81',
    description: 'You are required to code to the highest level of specificity.',
    is_alert: false
  },
  M82: {
    code: 'M82',
    description: 'Service is not covered when patient is under age 50.',
    is_alert: false
  },
  M83: {
    code: 'M83',
    description: 'Service is not covered unless the patient is classified as at high risk.',
    is_alert: false
  },
  M84: {
    code: 'M84',
    description: 'Medical code sets used must be the codes in effect at the time of service.',
    is_alert: false
  },
  M85: {
    code: 'M85',
    description: 'Subjected to review of physician evaluation and management services.',
    is_alert: false
  },
  M86: {
    code: 'M86',
    description:
      'Service denied because payment already made for same/similar procedure within set time frame.',
    is_alert: false
  },
  M87: {
    code: 'M87',
    description: 'Claim/service(s) subjected to CFO-CAP prepayment review.',
    is_alert: false
  },
  M88: {
    code: 'M88',
    description:
      'We cannot pay for laboratory tests unless billed by the laboratory that did the work.',
    is_alert: false
  },
  M89: {
    code: 'M89',
    description: 'Not covered more than once under age 40.',
    is_alert: false
  },
  M90: {
    code: 'M90',
    description: 'Not covered more than once in a 12 month period.',
    is_alert: false
  },
  M91: {
    code: 'M91',
    description:
      'Lab procedures with different CLIA certification numbers must be billed on separate claims.',
    is_alert: false
  },
  M92: {
    code: 'M92',
    description: 'Services subjected to review under the Home Health Medical Review Initiative.',
    is_alert: false
  },
  M93: {
    code: 'M93',
    description:
      'Information supplied supports a break in therapy. A new capped rental period began with delivery of this equipment.',
    is_alert: false
  },
  M94: {
    code: 'M94',
    description:
      'Information supplied does not support a break in therapy. A new capped rental period will not begin.',
    is_alert: false
  },
  M95: {
    code: 'M95',
    description: 'Services subjected to Home Health Initiative medical review/cost report audit.',
    is_alert: false
  },
  M96: {
    code: 'M96',
    description:
      'The technical component of a service furnished to an inpatient may only be billed by that inpatient facility. You must contact the inpatient facility for technical component reimbursement. If not already billed, you should bill us for the professional component only.',
    is_alert: false
  },
  M97: {
    code: 'M97',
    description:
      'Not paid to practitioner when provided to patient in this place of service. Payment included in the reimbursement issued the facility.',
    is_alert: false
  },
  M98: {
    code: 'M98',
    description:
      'Begin to report the Universal Product Number on claims for items of this type. We will soon begin to deny payment for items of this type if billed without the correct UPN.',
    is_alert: false
  },
  M99: {
    code: 'M99',
    description: 'Missing/incomplete/invalid Universal Product Number/Serial Number.',
    is_alert: false
  },
  M100: {
    code: 'M100',
    description:
      'We do not pay for an oral anti-emetic drug that is not administered for use immediately before, at, or within 48 hours of administration of a covered chemotherapy drug.',
    is_alert: false
  },
  M101: {
    code: 'M101',
    description:
      'Begin to report a G1-G5 modifier with this HCPCS. We will soon begin to deny payment for this service if billed without a G1-G5 modifier.',
    is_alert: false
  },
  M102: {
    code: 'M102',
    description: 'Service not performed on equipment approved by the FDA for this purpose.',
    is_alert: false
  },
  M103: {
    code: 'M103',
    description:
      'Information supplied supports a break in therapy. However, the medical information we have for this patient does not support the need for this item as billed. We have approved payment for this item at a reduced level, and a new capped rental period will begin with the delivery of this equipment.',
    is_alert: false
  },
  M104: {
    code: 'M104',
    description:
      'Information supplied supports a break in therapy. A new capped rental period will begin with delivery of the equipment. This is the maximum approved under the fee schedule for this item or service.',
    is_alert: false
  },
  M105: {
    code: 'M105',
    description:
      'Information supplied does not support a break in therapy. The medical information we have for this patient does not support the need for this item as billed. We have approved payment for this item at a reduced level, and a new capped rental period will not begin.',
    is_alert: false
  },
  M106: {
    code: 'M106',
    description:
      'Information supplied does not support a break in therapy. A new capped rental period will not begin. This is the maximum approved under the fee schedule for this item or service.',
    is_alert: false
  },
  M107: {
    code: 'M107',
    description:
      'Payment reduced as 90-day rolling average hematocrit for ESRD patient exceeded 36.5%.',
    is_alert: false
  },
  M108: {
    code: 'M108',
    description:
      'Missing/incomplete/invalid provider identifier for the provider who interpreted the diagnostic test.',
    is_alert: false
  },
  M109: {
    code: 'M109',
    description:
      'We have provided you with a bundled payment for a teleconsultation. You must send 25 percent of the teleconsultation payment to the referring practitioner.',
    is_alert: false
  },
  M110: {
    code: 'M110',
    description:
      'Missing/incomplete/invalid provider identifier for the provider from whom you purchased interpretation services.',
    is_alert: false
  },
  M111: {
    code: 'M111',
    description:
      'We do not pay for chiropractic manipulative treatment when the patient refuses to have an x-ray taken.',
    is_alert: false
  },
  M112: {
    code: 'M112',
    description:
      'Reimbursement for this item is based on the single payment amount required under the DMEPOS Competitive Bidding Program for the area where the patient resides.',
    is_alert: false
  },
  M113: {
    code: 'M113',
    description:
      'Our records indicate that this patient began using this item/service prior to the current contract period for the DMEPOS Competitive Bidding Program.',
    is_alert: false
  },
  M114: {
    code: 'M114',
    description:
      'This service was processed in accordance with rules and guidelines under the DMEPOS Competitive Bidding Program or a Demonstration Project. For more information regarding these projects, contact your local contractor.',
    is_alert: false
  },
  M115: {
    code: 'M115',
    description:
      'This item is denied when provided to this patient by a non-contract or non-demonstration supplier.',
    is_alert: false
  },
  M116: {
    code: 'M116',
    description:
      'Processed under a demonstration project or program. Project or program is ending and additional services may not be paid under this project or program.',
    is_alert: false
  },
  M117: {
    code: 'M117',
    description: 'Not covered unless submitted via electronic claim.',
    is_alert: false
  },
  M118: {
    code: 'M118',
    description: 'Letter to follow containing further information.',
    is_alert: false
  },
  M119: {
    code: 'M119',
    description: 'Missing/incomplete/invalid/ deactivated/withdrawn National Drug Code (NDC).',
    is_alert: false
  },
  M120: {
    code: 'M120',
    description:
      'Missing/incomplete/invalid provider identifier for the substituting physician who furnished the service(s) under a reciprocal billing or locum tenens arrangement.',
    is_alert: false
  },
  M121: {
    code: 'M121',
    description:
      'We pay for this service only when performed with a covered cryosurgical ablation.',
    is_alert: false
  },
  M122: {
    code: 'M122',
    description: 'Missing/incomplete/invalid level of subluxation.',
    is_alert: false
  },
  M123: {
    code: 'M123',
    description: 'Missing/incomplete/invalid name, strength, or dosage of the drug furnished.',
    is_alert: false
  },
  M124: {
    code: 'M124',
    description:
      'Missing indication of whether the patient owns the equipment that requires the part or supply.',
    is_alert: false
  },
  M125: {
    code: 'M125',
    description:
      'Missing/incomplete/invalid information on the period of time for which the service/supply/equipment will be needed.',
    is_alert: false
  },
  M126: {
    code: 'M126',
    description: 'Missing/incomplete/invalid individual lab codes included in the test.',
    is_alert: false
  },
  M127: {
    code: 'M127',
    description: 'Missing patient medical record for this service.',
    is_alert: false
  },
  M128: {
    code: 'M128',
    description: "Missing/incomplete/invalid date of the patient's last physician visit.",
    is_alert: false
  },
  M129: {
    code: 'M129',
    description: 'Missing/incomplete/invalid indicator of x-ray availability for review.',
    is_alert: false
  },
  M130: {
    code: 'M130',
    description:
      'Missing invoice or statement certifying the actual cost of the lens, less discounts, and/or the type of intraocular lens used.',
    is_alert: false
  },
  M131: {
    code: 'M131',
    description: 'Missing physician financial relationship form.',
    is_alert: false
  },
  M132: {
    code: 'M132',
    description: 'Missing pacemaker registration form.',
    is_alert: false
  },
  M133: {
    code: 'M133',
    description:
      'Claim did not identify who performed the purchased diagnostic test or the amount you were charged for the test.',
    is_alert: false
  },
  M134: {
    code: 'M134',
    description: 'Performed by a facility/supplier in which the provider has a financial interest.',
    is_alert: false
  },
  M135: {
    code: 'M135',
    description: 'Missing/incomplete/invalid plan of treatment.',
    is_alert: false
  },
  M136: {
    code: 'M136',
    description:
      'Missing/incomplete/invalid indication that the service was supervised or evaluated by a physician.',
    is_alert: false
  },
  M137: {
    code: 'M137',
    description: 'Part B coinsurance under a demonstration project or pilot program.',
    is_alert: false
  },
  M138: {
    code: 'M138',
    description:
      'Patient identified as a demonstration participant but the patient was not enrolled in the demonstration at the time services were rendered. Coverage is limited to demonstration participants.',
    is_alert: false
  },
  M139: {
    code: 'M139',
    description: 'Denied services exceed the coverage limit for the demonstration.',
    is_alert: false
  },
  M140: {
    code: 'M140',
    description:
      "Service not covered until after the patient's 50th birthday, i.e., no coverage prior to the day after the 50th birthday",
    is_alert: false
  },
  M141: {
    code: 'M141',
    description: 'Missing physician certified plan of care.',
    is_alert: false
  },
  M142: {
    code: 'M142',
    description: 'Missing American Diabetes Association Certificate of Recognition.',
    is_alert: false
  },
  M143: {
    code: 'M143',
    description: 'The provider must update license information with the payer.',
    is_alert: false
  },
  M144: {
    code: 'M144',
    description:
      'Pre-/post-operative care payment is included in the allowance for the surgery/procedure.',
    is_alert: false
  },
  MA01: {
    code: 'MA01',
    description:
      'Alert: If you do not agree with what we approved for these services, you may appeal our decision. To make sure that we are fair to you, we require another individual that did not process your initial claim to conduct the appeal. However, in order to be eligible for an appeal, you must write to us within 120 days of the date you received this notice, unless you have a good reason for being late.',
    is_alert: true
  },
  MA02: {
    code: 'MA02',
    description:
      'Alert: If you do not agree with this determination, you have the right to appeal. You must file a written request for an appeal within 180 days of the date you receive this notice.',
    is_alert: true
  },
  MA03: {
    code: 'MA03',
    description:
      'If you do not agree with the approved amounts and $100 or more is in dispute (less deductible and coinsurance), you may ask for a hearing within six months of the date of this notice. To meet the $100, you may combine amounts on other claims that have been denied, including reopened appeals if you received a revised decision. You must appeal each claim on time.',
    is_alert: false
  },
  MA04: {
    code: 'MA04',
    description:
      'Secondary payment cannot be considered without the identity of or payment information from the primary payer. The information was either not reported or was illegible.',
    is_alert: false
  },
  MA05: {
    code: 'MA05',
    description: 'Incorrect admission date patient status or type of bill entry on claim.',
    is_alert: false
  },
  MA06: {
    code: 'MA06',
    description: 'Missing/incomplete/invalid beginning and/or ending date(s).',
    is_alert: false
  },
  MA07: {
    code: 'MA07',
    description: 'Alert: The claim information has also been forwarded to Medicaid for review.',
    is_alert: true
  },
  MA08: {
    code: 'MA08',
    description:
      'Alert: Claim information was not forwarded because the supplemental coverage is not with a Medigap plan, or you do not participate in Medicare.',
    is_alert: true
  },
  MA09: {
    code: 'MA09',
    description:
      'Alert: Claim submitted as unassigned but processed as assigned in accordance with our current assignment/participation agreement.',
    is_alert: true
  },
  MA10: {
    code: 'MA10',
    description:
      "Alert: The patient's payment was in excess of the amount owed. You must refund the overpayment to the patient.",
    is_alert: true
  },
  MA11: {
    code: 'MA11',
    description:
      "Payment is being issued on a conditional basis. If no-fault insurance, liability insurance, Workers' Compensation, Department of Veterans Affairs, or a group health plan for employees and dependents also covers this claim, a refund may be due us. Please contact us if the patient is covered by any of these sources.",
    is_alert: false
  },
  MA12: {
    code: 'MA12',
    description:
      'You have not established that you have the right under the law to bill for services furnished by the person(s) that furnished this (these) service(s).',
    is_alert: false
  },
  MA13: {
    code: 'MA13',
    description:
      'Alert: You may be subject to penalties if you bill the patient for amounts not reported with the PR (patient responsibility) group code.',
    is_alert: true
  },
  MA14: {
    code: 'MA14',
    description:
      'Alert: The patient is a member of an employer-sponsored prepaid health plan. Services from outside that health plan are not covered. However, as you were not previously notified of this, we are paying this time. In the future, we will not pay you for non-plan services.',
    is_alert: true
  },
  MA15: {
    code: 'MA15',
    description:
      'Alert: Your claim has been separated to expedite handling. You will receive a separate notice for the other services reported.',
    is_alert: true
  },
  MA16: {
    code: 'MA16',
    description:
      'The patient is covered by the Black Lung Program. Send this claim to the Department of Labor, Federal Black Lung Program, P.O. Box 828, Lanham-Seabrook MD 20703.',
    is_alert: false
  },
  MA17: {
    code: 'MA17',
    description:
      "We are the primary payer and have paid at the primary rate. You must contact the patient's other insurer to refund any excess it may have paid due to its erroneous primary payment.",
    is_alert: false
  },
  MA18: {
    code: 'MA18',
    description:
      "Alert: The claim information is also being forwarded to the patient's supplemental insurer. Send any questions regarding supplemental benefits to them.",
    is_alert: true
  },
  MA19: {
    code: 'MA19',
    description:
      'Alert: Information was not sent to the Medigap insurer due to incorrect/invalid information you submitted concerning that insurer. Please verify your information and submit your secondary claim directly to that insurer.',
    is_alert: true
  },
  MA20: {
    code: 'MA20',
    description:
      'Skilled Nursing Facility (SNF) stay not covered when care is primarily related to the use of an urethral catheter for convenience or the control of incontinence.',
    is_alert: false
  },
  MA21: {
    code: 'MA21',
    description: 'SSA records indicate mismatch with name and sex.',
    is_alert: false
  },
  MA22: {
    code: 'MA22',
    description: 'Payment of less than $1.00 suppressed.',
    is_alert: false
  },
  MA23: {
    code: 'MA23',
    description: 'Demand bill approved as result of medical review.',
    is_alert: false
  },
  MA24: {
    code: 'MA24',
    description:
      'Christian Science Sanitarium/ Skilled Nursing Facility (SNF) bill in the same benefit period.',
    is_alert: false
  },
  MA25: {
    code: 'MA25',
    description:
      'A patient may not elect to change a hospice provider more than once in a benefit period.',
    is_alert: false
  },
  MA26: {
    code: 'MA26',
    description: 'Alert: Our records indicate that you were previously informed of this rule.',
    is_alert: true
  },
  MA27: {
    code: 'MA27',
    description: 'Missing/incomplete/invalid entitlement number or name shown on the claim.',
    is_alert: false
  },
  MA28: {
    code: 'MA28',
    description:
      'Alert: Receipt of this notice by a physician or supplier who did not accept assignment is for information only and does not make the physician or supplier a party to the determination. No additional rights to appeal this decision, above those rights already provided for by regulation/instruction, are conferred by receipt of this notice.',
    is_alert: true
  },
  MA29: {
    code: 'MA29',
    description: 'Missing/incomplete/invalid provider name, city, state, or zip code.',
    is_alert: false
  },
  MA30: {
    code: 'MA30',
    description: 'Missing/incomplete/invalid type of bill.',
    is_alert: false
  },
  MA31: {
    code: 'MA31',
    description: 'Missing/incomplete/invalid beginning and ending dates of the period billed.',
    is_alert: false
  },
  MA32: {
    code: 'MA32',
    description: 'Missing/incomplete/invalid number of covered days during the billing period.',
    is_alert: false
  },
  MA33: {
    code: 'MA33',
    description: 'Missing/incomplete/invalid non-covered days during the billing period.',
    is_alert: false
  },
  MA34: {
    code: 'MA34',
    description: 'Missing/incomplete/invalid number of coinsurance days during the billing period.',
    is_alert: false
  },
  MA35: {
    code: 'MA35',
    description: 'Missing/incomplete/invalid number of lifetime reserve days.',
    is_alert: false
  },
  MA36: {
    code: 'MA36',
    description: 'Missing/incomplete/invalid patient name.',
    is_alert: false
  },
  MA37: {
    code: 'MA37',
    description: "Missing/incomplete/invalid patient's address.",
    is_alert: false
  },
  MA38: {
    code: 'MA38',
    description: 'Missing/incomplete/invalid birth date.',
    is_alert: false
  },
  MA39: {
    code: 'MA39',
    description: 'Missing/incomplete/invalid gender.',
    is_alert: false
  },
  MA40: {
    code: 'MA40',
    description: 'Missing/incomplete/invalid admission date.',
    is_alert: false
  },
  MA41: {
    code: 'MA41',
    description: 'Missing/incomplete/invalid admission type.',
    is_alert: false
  },
  MA42: {
    code: 'MA42',
    description: 'Missing/incomplete/invalid admission source.',
    is_alert: false
  },
  MA43: {
    code: 'MA43',
    description: 'Missing/incomplete/invalid patient status.',
    is_alert: false
  },
  MA44: {
    code: 'MA44',
    description: 'Alert: No appeal rights. Adjudicative decision based on law.',
    is_alert: true
  },
  MA45: {
    code: 'MA45',
    description:
      'Alert: As previously advised, a portion or all of your payment is being held in a special account.',
    is_alert: true
  },
  MA46: {
    code: 'MA46',
    description:
      'Alert: The new information was considered but additional payment will not be issued.',
    is_alert: true
  },
  MA47: {
    code: 'MA47',
    description:
      'Our records show you have opted out of Medicare, agreeing with the patient not to bill Medicare for services/tests/supplies furnished. As result, we cannot pay this claim. The patient is responsible for payment.',
    is_alert: false
  },
  MA48: {
    code: 'MA48',
    description:
      'Missing/incomplete/invalid name or address of responsible party or primary payer.',
    is_alert: false
  },
  MA49: {
    code: 'MA49',
    description:
      'Missing/incomplete/invalid six-digit provider identifier for home health agency or hospice for physician(s) performing care plan oversight services.',
    is_alert: false
  },
  MA50: {
    code: 'MA50',
    description:
      'Missing/incomplete/invalid Investigational Device Exemption number or Clinical Trial number.',
    is_alert: false
  },
  MA51: {
    code: 'MA51',
    description:
      'Missing/incomplete/invalid CLIA certification number for laboratory services billed by physician office laboratory.',
    is_alert: false
  },
  MA52: {
    code: 'MA52',
    description: 'Missing/incomplete/invalid date.',
    is_alert: false
  },
  MA53: {
    code: 'MA53',
    description:
      'Missing/incomplete/invalid Competitive Bidding Demonstration Project identification.',
    is_alert: false
  },
  MA54: {
    code: 'MA54',
    description:
      'Physician certification or election consent for hospice care not received timely.',
    is_alert: false
  },
  MA55: {
    code: 'MA55',
    description:
      'Not covered as patient received medical health care services, automatically revoking his/her election to receive religious non-medical health care services.',
    is_alert: false
  },
  MA56: {
    code: 'MA56',
    description:
      'Our records show you have opted out of Medicare, agreeing with the patient not to bill Medicare for services/tests/supplies furnished. As result, we cannot pay this claim. The patient is responsible for payment, but under Federal law, you cannot charge the patient more than the limiting charge amount.',
    is_alert: false
  },
  MA57: {
    code: 'MA57',
    description:
      'Patient submitted written request to revoke his/her election for religious non-medical health care services.',
    is_alert: false
  },
  MA58: {
    code: 'MA58',
    description: 'Missing/incomplete/invalid release of information indicator.',
    is_alert: false
  },
  MA59: {
    code: 'MA59',
    description:
      'Alert: The patient overpaid you for these services. You must issue the patient a refund within 30 days for the difference between his/her payment and the total amount shown as patient responsibility on this notice.',
    is_alert: true
  },
  MA60: {
    code: 'MA60',
    description: 'Missing/incomplete/invalid patient relationship to insured.',
    is_alert: false
  },
  MA61: {
    code: 'MA61',
    description: 'Missing/incomplete/invalid social security number.',
    is_alert: false
  },
  MA62: {
    code: 'MA62',
    description: 'Alert: This is a telephone review decision.',
    is_alert: true
  },
  MA63: {
    code: 'MA63',
    description: 'Missing/incomplete/invalid principal diagnosis.',
    is_alert: false
  },
  MA64: {
    code: 'MA64',
    description:
      'Our records indicate that we should be the third payer for this claim. We cannot process this claim until we have received payment information from the primary and secondary payers.',
    is_alert: false
  },
  MA65: {
    code: 'MA65',
    description: 'Missing/incomplete/invalid admitting diagnosis.',
    is_alert: false
  },
  MA66: {
    code: 'MA66',
    description: 'Missing/incomplete/invalid principal procedure code.',
    is_alert: false
  },
  MA67: {
    code: 'MA67',
    description: 'Alert: Correction to a prior claim.',
    is_alert: true
  },
  MA68: {
    code: 'MA68',
    description:
      'Alert: We did not crossover this claim because the secondary insurance information on the claim was incomplete. Please supply complete information or use the PLANID of the insurer to assure correct and timely routing of the claim.',
    is_alert: true
  },
  MA69: {
    code: 'MA69',
    description: 'Missing/incomplete/invalid remarks.',
    is_alert: false
  },
  MA70: {
    code: 'MA70',
    description: 'Missing/incomplete/invalid provider representative signature.',
    is_alert: false
  },
  MA71: {
    code: 'MA71',
    description: 'Missing/incomplete/invalid provider representative signature date.',
    is_alert: false
  },
  MA72: {
    code: 'MA72',
    description:
      'Alert: The patient overpaid you for these assigned services. You must issue the patient a refund within 30 days for the difference between his/her payment to you and the total of the amount shown as patient responsibility and as paid to the patient on this notice.',
    is_alert: true
  },
  MA73: {
    code: 'MA73',
    description:
      'Informational remittance associated with a Medicare demonstration. No payment issued under fee-for-service Medicare as patient has elected managed care.',
    is_alert: false
  },
  MA74: {
    code: 'MA74',
    description:
      'Alert: This payment replaces an earlier payment for this claim that was either lost, damaged or returned.',
    is_alert: true
  },
  MA75: {
    code: 'MA75',
    description: 'Missing/incomplete/invalid patient or authorized representative signature.',
    is_alert: false
  },
  MA76: {
    code: 'MA76',
    description:
      'Missing/incomplete/invalid provider identifier for home health agency or hospice when physician is performing care plan oversight services.',
    is_alert: false
  },
  MA77: {
    code: 'MA77',
    description:
      "Alert: The patient overpaid you. You must issue the patient a refund within 30 days for the difference between the patient's payment less the total of our and other payer payments and the amount shown as patient responsibility on this notice.",
    is_alert: true
  },
  MA78: {
    code: 'MA78',
    description:
      'The patient overpaid you. You must issue the patient a refund within 30 days for the difference between our allowed amount total and the amount paid by the patient.',
    is_alert: false
  },
  MA79: {
    code: 'MA79',
    description: 'Billed in excess of interim rate.',
    is_alert: false
  },
  MA80: {
    code: 'MA80',
    description:
      'Informational notice. No payment issued for this claim with this notice. Payment issued to the hospital by its intermediary for all services for this encounter under a demonstration project.',
    is_alert: false
  },
  MA81: {
    code: 'MA81',
    description: 'Missing/incomplete/invalid provider/supplier signature.',
    is_alert: false
  },
  MA82: {
    code: 'MA82',
    description:
      'Missing/incomplete/invalid provider/supplier billing number/identifier or billing name, address, city, state, zip code, or phone number.',
    is_alert: false
  },
  MA83: {
    code: 'MA83',
    description: 'Did not indicate whether we are the primary or secondary payer.',
    is_alert: false
  },
  MA84: {
    code: 'MA84',
    description:
      'Patient identified as participating in the National Emphysema Treatment Trial but our records indicate that this patient is either not a participant, or has not yet been approved for this phase of the study. Contact Johns Hopkins University, the study coordinator, to resolve if there was a discrepancy.',
    is_alert: false
  },
  MA85: {
    code: 'MA85',
    description:
      'Our records indicate that a primary payer exists (other than ourselves); however, you did not complete or enter accurately the insurance plan/group/program name or identification number. Enter the PlanID when effective.',
    is_alert: false
  },
  MA86: {
    code: 'MA86',
    description:
      'Missing/incomplete/invalid group or policy number of the insured for the primary coverage.',
    is_alert: false
  },
  MA87: {
    code: 'MA87',
    description: "Missing/incomplete/invalid insured's name for the primary payer.",
    is_alert: false
  },
  MA88: {
    code: 'MA88',
    description:
      "Missing/incomplete/invalid insured's address and/or telephone number for the primary payer.",
    is_alert: false
  },
  MA89: {
    code: 'MA89',
    description:
      "Missing/incomplete/invalid patient's relationship to the insured for the primary payer.",
    is_alert: false
  },
  MA90: {
    code: 'MA90',
    description: 'Missing/incomplete/invalid employment status code for the primary insured.',
    is_alert: false
  },
  MA91: {
    code: 'MA91',
    description: 'Alert: This determination is the result of the appeal you filed.',
    is_alert: true
  },
  MA92: {
    code: 'MA92',
    description: 'Missing plan information for other insurance.',
    is_alert: false
  },
  MA93: {
    code: 'MA93',
    description: 'Non-PIP (Periodic Interim Payment) claim.',
    is_alert: false
  },
  MA94: {
    code: 'MA94',
    description:
      "Did not enter the statement 'Attending physician not hospice employee' on the claim form to certify that the rendering physician is not an employee of the hospice.",
    is_alert: false
  },
  MA95: {
    code: 'MA95',
    description:
      'A not otherwise classified or unlisted procedure code(s) was billed but a narrative description of the procedure was not entered on the claim. Refer to item 19 on the HCFA-1500.',
    is_alert: false
  },
  MA96: {
    code: 'MA96',
    description:
      'Claim rejected. Coded as a Medicare Managed Care Demonstration but patient is not enrolled in a Medicare managed care plan.',
    is_alert: false
  },
  MA97: {
    code: 'MA97',
    description:
      'Missing/incomplete/invalid Medicare Managed Care Demonstration contract number or clinical trial registry number.',
    is_alert: false
  },
  MA98: {
    code: 'MA98',
    description:
      'Claim Rejected. Does not contain the correct Medicare Managed Care Demonstration contract number for this beneficiary.',
    is_alert: false
  },
  MA99: {
    code: 'MA99',
    description: 'Missing/incomplete/invalid Medigap information.',
    is_alert: false
  },
  MA100: {
    code: 'MA100',
    description: 'Missing/incomplete/invalid date of current illness or symptoms.',
    is_alert: false
  },
  MA101: {
    code: 'MA101',
    description:
      'A Skilled Nursing Facility (SNF) is responsible for payment of outside providers who furnish these services/supplies to residents.',
    is_alert: false
  },
  MA102: {
    code: 'MA102',
    description:
      'Missing/incomplete/invalid name or provider identifier for the rendering/referring/ ordering/ supervising provider.',
    is_alert: false
  },
  MA103: {
    code: 'MA103',
    description: 'Hemophilia Add On.',
    is_alert: false
  },
  MA104: {
    code: 'MA104',
    description:
      'Missing/incomplete/invalid date the patient was last seen or the provider identifier of the attending physician.',
    is_alert: false
  },
  MA105: {
    code: 'MA105',
    description: 'Missing/incomplete/invalid provider number for this place of service.',
    is_alert: false
  },
  MA106: {
    code: 'MA106',
    description: 'PIP (Periodic Interim Payment) claim.',
    is_alert: false
  },
  MA107: {
    code: 'MA107',
    description: 'Paper claim contains more than three separate data items in field 19.',
    is_alert: false
  },
  MA108: {
    code: 'MA108',
    description: 'Paper claim contains more than one data item in field 23.',
    is_alert: false
  },
  MA109: {
    code: 'MA109',
    description: 'Claim processed in accordance with ambulatory surgical guidelines.',
    is_alert: false
  },
  MA110: {
    code: 'MA110',
    description:
      'Missing/incomplete/invalid information on whether the diagnostic test(s) were performed by an outside entity or if no purchased tests are included on the claim.',
    is_alert: false
  },
  MA111: {
    code: 'MA111',
    description:
      "Missing/incomplete/invalid purchase price of the test(s) and/or the performing laboratory's name and address.",
    is_alert: false
  },
  MA112: {
    code: 'MA112',
    description: 'Missing/incomplete/invalid group practice information.',
    is_alert: false
  },
  MA113: {
    code: 'MA113',
    description:
      'Incomplete/invalid taxpayer identification number (TIN) submitted by you per the Internal Revenue Service. Your claims cannot be processed without your correct TIN, and you may not bill the patient pending correction of your TIN. There are no appeal rights for unprocessable claims, but you may resubmit this claim after you have notified this office of your correct TIN.',
    is_alert: false
  },
  MA114: {
    code: 'MA114',
    description: 'Missing/incomplete/invalid information on where the services were furnished.',
    is_alert: false
  },
  MA115: {
    code: 'MA115',
    description:
      'Missing/incomplete/invalid physical location (name and address, or PIN) where the service(s) were rendered in a Health Professional Shortage Area (HPSA).',
    is_alert: false
  },
  MA116: {
    code: 'MA116',
    description:
      "Did not complete the statement 'Homebound' on the claim to validate whether laboratory services were performed at home or in an institution.",
    is_alert: false
  },
  MA117: {
    code: 'MA117',
    description: 'This claim has been assessed a $1.00 user fee.',
    is_alert: false
  },
  MA118: {
    code: 'MA118',
    description:
      'Alert: No Medicare payment issued for this claim for services or supplies furnished to a Medicare-eligible veteran through a facility of the Department of Veterans Affairs. Coinsurance and/or deductible are applicable.',
    is_alert: true
  },
  MA119: {
    code: 'MA119',
    description: 'Provider level adjustment for late claim filing applies to this claim.',
    is_alert: false
  },
  MA120: {
    code: 'MA120',
    description: 'Missing/incomplete/invalid CLIA certification number.',
    is_alert: false
  },
  MA121: {
    code: 'MA121',
    description: 'Missing/incomplete/invalid x-ray date.',
    is_alert: false
  },
  MA122: {
    code: 'MA122',
    description: 'Missing/incomplete/invalid initial treatment date.',
    is_alert: false
  },
  MA123: {
    code: 'MA123',
    description:
      'Your center was not selected to participate in this study, therefore, we cannot pay for these services.',
    is_alert: false
  },
  MA124: {
    code: 'MA124',
    description: 'Processed for IME only.',
    is_alert: false
  },
  MA125: {
    code: 'MA125',
    description: 'Per legislation governing this program, payment constitutes payment in full.',
    is_alert: false
  },
  MA126: {
    code: 'MA126',
    description: 'Pancreas transplant not covered unless kidney transplant performed.',
    is_alert: false
  },
  MA127: {
    code: 'MA127',
    description: 'Reserved for future use.',
    is_alert: false
  },
  MA128: {
    code: 'MA128',
    description: 'Missing/incomplete/invalid FDA approval number.',
    is_alert: false
  },
  MA129: {
    code: 'MA129',
    description: 'This provider was not certified for this procedure on this date of service.',
    is_alert: false
  },
  MA130: {
    code: 'MA130',
    description:
      'Your claim contains incomplete and/or invalid information, and no appeal rights are afforded because the claim is unprocessable. Please submit a new claim with the complete/correct information.',
    is_alert: false
  },
  MA131: {
    code: 'MA131',
    description:
      'Physician already paid for services in conjunction with this demonstration claim. You must have the physician withdraw that claim and refund the payment before we can process your claim.',
    is_alert: false
  },
  MA132: {
    code: 'MA132',
    description: 'Adjustment to the pre-demonstration rate.',
    is_alert: false
  },
  MA133: {
    code: 'MA133',
    description:
      'Claim overlaps inpatient stay. Rebill only those services rendered outside the inpatient stay.',
    is_alert: false
  },
  MA134: {
    code: 'MA134',
    description:
      'Missing/incomplete/invalid provider number of the facility where the patient resides.',
    is_alert: false
  },
  N1: {
    code: 'N1',
    description:
      'Alert: You may appeal this decision in writing within the required time limits following receipt of this notice by following the instructions included in your contract, plan benefit documents or jurisdiction statutes. Refer to the URL provided in the ERA for the payer website to access the appeals process guidelines.',
    is_alert: true
  },
  N2: {
    code: 'N2',
    description:
      'This allowance has been made in accordance with the most appropriate course of treatment provision of the plan.',
    is_alert: false
  },
  N3: {
    code: 'N3',
    description: 'Missing consent form.',
    is_alert: false
  },
  N4: {
    code: 'N4',
    description: 'Missing/Incomplete/Invalid prior Insurance Carrier(s) EOB.',
    is_alert: false
  },
  N5: {
    code: 'N5',
    description: 'EOB received from previous payer. Claim not on file.',
    is_alert: false
  },
  N6: {
    code: 'N6',
    description:
      'Under FEHB law (U.S.C. 8904(b)), we cannot pay more for covered care than the amount Medicare would have allowed if the patient were enrolled in Medicare Part A and/or Medicare Part B.',
    is_alert: false
  },
  N7: {
    code: 'N7',
    description:
      'Alert: Processing of this claim/service has included consideration under Major Medical provisions.',
    is_alert: true
  },
  N8: {
    code: 'N8',
    description:
      'Crossover claim denied by previous payer and complete claim data not forwarded. Resubmit this claim to this payer to provide adequate data for adjudication.',
    is_alert: false
  },
  N9: {
    code: 'N9',
    description: 'Adjustment represents the estimated amount a previous payer may pay.',
    is_alert: false
  },
  N10: {
    code: 'N10',
    description:
      'Adjustment based on the findings of a review organization/professional consult/manual adjudication/medical advisor/dental advisor/peer review.',
    is_alert: false
  },
  N11: {
    code: 'N11',
    description: 'Denial reversed because of medical review.',
    is_alert: false
  },
  N12: {
    code: 'N12',
    description:
      'Policy provides coverage supplemental to Medicare. As the member does not appear to be enrolled in the applicable part of Medicare, the member is responsible for payment of the portion of the charge that would have been covered by Medicare.',
    is_alert: false
  },
  N13: {
    code: 'N13',
    description: 'Payment based on professional/technical component modifier(s).',
    is_alert: false
  },
  N14: {
    code: 'N14',
    description:
      'Payment based on a contractual amount or agreement, fee schedule, or maximum allowable amount.',
    is_alert: false
  },
  N15: {
    code: 'N15',
    description: 'Services for a newborn must be billed separately.',
    is_alert: false
  },
  N16: {
    code: 'N16',
    description:
      'Family/member Out-of-Pocket maximum has been met. Payment based on a higher percentage.',
    is_alert: false
  },
  N17: {
    code: 'N17',
    description: 'Per admission deductible.',
    is_alert: false
  },
  N18: {
    code: 'N18',
    description: 'Payment based on the Medicare allowed amount.',
    is_alert: false
  },
  N19: {
    code: 'N19',
    description: 'Procedure code incidental to primary procedure.',
    is_alert: false
  },
  N20: {
    code: 'N20',
    description: 'Service not payable with other service rendered on the same date.',
    is_alert: false
  },
  N21: {
    code: 'N21',
    description:
      'Alert: Your line item has been separated into multiple lines to expedite handling.',
    is_alert: true
  },
  N22: {
    code: 'N22',
    description:
      'Alert: This procedure code was added/changed because it more accurately describes the services rendered.',
    is_alert: true
  },
  N23: {
    code: 'N23',
    description:
      'Alert: Patient liability may be affected due to coordination of benefits with other carriers and/or maximum benefit provisions.',
    is_alert: true
  },
  N24: {
    code: 'N24',
    description: 'Missing/incomplete/invalid Electronic Funds Transfer (EFT) banking information.',
    is_alert: false
  },
  N25: {
    code: 'N25',
    description:
      'This company has been contracted by your benefit plan to provide administrative claims payment services only. This company does not assume financial risk or obligation with respect to claims processed on behalf of your benefit plan.',
    is_alert: false
  },
  N26: {
    code: 'N26',
    description: 'Missing itemized bill/statement.',
    is_alert: false
  },
  N27: {
    code: 'N27',
    description: 'Missing/incomplete/invalid treatment number.',
    is_alert: false
  },
  N28: {
    code: 'N28',
    description: 'Consent form requirements not fulfilled.',
    is_alert: false
  },
  N29: {
    code: 'N29',
    description: 'Missing documentation/orders/notes/summary/report/chart.',
    is_alert: false
  },
  N30: {
    code: 'N30',
    description: 'Patient ineligible for this service.',
    is_alert: false
  },
  N31: {
    code: 'N31',
    description: 'Missing/incomplete/invalid prescribing provider identifier.',
    is_alert: false
  },
  N32: {
    code: 'N32',
    description: 'Claim must be submitted by the provider who rendered the service.',
    is_alert: false
  },
  N33: {
    code: 'N33',
    description: 'No record of health check prior to initiation of treatment.',
    is_alert: false
  },
  N34: {
    code: 'N34',
    description: 'Incorrect claim form/format for this service.',
    is_alert: false
  },
  N35: {
    code: 'N35',
    description: 'Program integrity/utilization review decision.',
    is_alert: false
  },
  N36: {
    code: 'N36',
    description:
      "Claim must meet primary payer's processing requirements before we can consider payment.",
    is_alert: false
  },
  N37: {
    code: 'N37',
    description: 'Missing/incomplete/invalid tooth number/letter.',
    is_alert: false
  },
  N38: {
    code: 'N38',
    description: 'Missing/incomplete/invalid place of service.',
    is_alert: false
  },
  N39: {
    code: 'N39',
    description: 'Procedure code is not compatible with tooth number/letter.',
    is_alert: false
  },
  N40: {
    code: 'N40',
    description: 'Missing radiology film(s)/image(s).',
    is_alert: false
  },
  N41: {
    code: 'N41',
    description: 'Authorization request denied.',
    is_alert: false
  },
  N42: {
    code: 'N42',
    description: 'Missing mental health assessment.',
    is_alert: false
  },
  N43: {
    code: 'N43',
    description: 'Bed hold or leave days exceeded.',
    is_alert: false
  },
  N44: {
    code: 'N44',
    description:
      "Payer's share of regulatory surcharges, assessments, allowances or health care-related taxes paid directly to the regulatory authority.",
    is_alert: false
  },
  N45: {
    code: 'N45',
    description: 'Payment based on authorized amount.',
    is_alert: false
  },
  N46: {
    code: 'N46',
    description: 'Missing/incomplete/invalid admission hour.',
    is_alert: false
  },
  N47: {
    code: 'N47',
    description: 'Claim conflicts with another inpatient stay.',
    is_alert: false
  },
  N48: {
    code: 'N48',
    description:
      'Claim information does not agree with information received from other insurance carrier.',
    is_alert: false
  },
  N49: {
    code: 'N49',
    description: 'Court ordered coverage information needs validation.',
    is_alert: false
  },
  N50: {
    code: 'N50',
    description: 'Missing/incomplete/invalid discharge information.',
    is_alert: false
  },
  N51: {
    code: 'N51',
    description: 'Electronic interchange agreement not on file for provider/submitter.',
    is_alert: false
  },
  N52: {
    code: 'N52',
    description:
      "Patient not enrolled in the billing provider's managed care plan on the date of service.",
    is_alert: false
  },
  N53: {
    code: 'N53',
    description: 'Missing/incomplete/invalid point of pick-up address.',
    is_alert: false
  },
  N54: {
    code: 'N54',
    description: 'Claim information is inconsistent with pre-certified/authorized services.',
    is_alert: false
  },
  N55: {
    code: 'N55',
    description:
      'Procedures for billing with group/referring/performing providers were not followed.',
    is_alert: false
  },
  N56: {
    code: 'N56',
    description:
      'Procedure code billed is not correct/valid for the services billed or the date of service billed.',
    is_alert: false
  },
  N57: {
    code: 'N57',
    description: 'Missing/incomplete/invalid prescribing date.',
    is_alert: false
  },
  N58: {
    code: 'N58',
    description: 'Missing/incomplete/invalid patient liability amount.',
    is_alert: false
  },
  N59: {
    code: 'N59',
    description:
      'Alert: Please refer to your provider manual for additional program and provider information.',
    is_alert: true
  },
  N60: {
    code: 'N60',
    description: 'A valid NDC is required for payment of drug claims effective October 02.',
    is_alert: false
  },
  N61: {
    code: 'N61',
    description: 'Rebill services on separate claims.',
    is_alert: false
  },
  N62: {
    code: 'N62',
    description: 'Dates of service span multiple rate periods. Resubmit separate claims.',
    is_alert: false
  },
  N63: {
    code: 'N63',
    description: 'Rebill services on separate claim lines.',
    is_alert: false
  },
  N64: {
    code: 'N64',
    description: "The 'from' and 'to' dates must be different.",
    is_alert: false
  },
  N65: {
    code: 'N65',
    description:
      'Procedure code or procedure rate count cannot be determined, or was not on file, for the date of service/provider.',
    is_alert: false
  },
  N66: {
    code: 'N66',
    description: 'Missing/incomplete/invalid documentation.',
    is_alert: false
  },
  N67: {
    code: 'N67',
    description:
      "Professional provider services not paid separately. Included in facility payment under a demonstration project. Apply to that facility for payment, or resubmit your claim if: the facility notifies you the patient was excluded from this demonstration; or if you furnished these services in another location on the date of the patient's admission or discharge from a demonstration hospital. If services were furnished in a facility not involved in the demonstration on the same date the patient was discharged from or admitted to a demonstration facility, you must report the provider ID number for the non-demonstration facility on the new claim.",
    is_alert: false
  },
  N68: {
    code: 'N68',
    description:
      'Prior payment being cancelled as we were subsequently notified this patient was covered by a demonstration project in this site of service. Professional services were included in the payment made to the facility. You must contact the facility for your payment. Prior payment made to you by the patient or another insurer for this claim must be refunded to the payer within 30 days.',
    is_alert: false
  },
  N69: {
    code: 'N69',
    description:
      'Alert: PPS (Prospective Payment System) code changed by claims processing system.',
    is_alert: true
  },
  N70: {
    code: 'N70',
    description: 'Consolidated billing and payment applies.',
    is_alert: false
  },
  N71: {
    code: 'N71',
    description:
      'Your unassigned claim for a drug or biological, clinical diagnostic laboratory services or ambulance service was processed as an assigned claim. You are required by law to accept assignment for these types of claims.',
    is_alert: false
  },
  N72: {
    code: 'N72',
    description:
      'PPS (Prospective Payment System) code changed by medical reviewers. Not supported by clinical records.',
    is_alert: false
  },
  N73: {
    code: 'N73',
    description:
      'A Skilled Nursing Facility is responsible for payment of outside providers who furnish these services/supplies under arrangement to its residents.',
    is_alert: false
  },
  N74: {
    code: 'N74',
    description:
      'Resubmit with multiple claims, each claim covering services provided in only one calendar month.',
    is_alert: false
  },
  N75: {
    code: 'N75',
    description: 'Missing/incomplete/invalid tooth surface information.',
    is_alert: false
  },
  N76: {
    code: 'N76',
    description: 'Missing/incomplete/invalid number of riders.',
    is_alert: false
  },
  N77: {
    code: 'N77',
    description: 'Missing/incomplete/invalid designated provider number.',
    is_alert: false
  },
  N78: {
    code: 'N78',
    description:
      'The necessary components of the child and teen checkup (EPSDT) were not completed.',
    is_alert: false
  },
  N79: {
    code: 'N79',
    description: 'Service billed is not compatible with patient location information.',
    is_alert: false
  },
  N80: {
    code: 'N80',
    description: 'Missing/incomplete/invalid prenatal screening information.',
    is_alert: false
  },
  N81: {
    code: 'N81',
    description: 'Procedure billed is not compatible with tooth surface code.',
    is_alert: false
  },
  N82: {
    code: 'N82',
    description:
      'Provider must accept insurance payment as payment in full when a third party payer contract specifies full reimbursement.',
    is_alert: false
  },
  N83: {
    code: 'N83',
    description:
      'No appeal rights. Adjudicative decision based on the provisions of a demonstration project.',
    is_alert: false
  },
  N84: {
    code: 'N84',
    description: 'Alert: Further installment payments are forthcoming.',
    is_alert: true
  },
  N85: {
    code: 'N85',
    description: 'Alert: This is the final installment payment.',
    is_alert: true
  },
  N86: {
    code: 'N86',
    description:
      'A failed trial of pelvic muscle exercise training is required in order for biofeedback training for the treatment of urinary incontinence to be covered.',
    is_alert: false
  },
  N87: {
    code: 'N87',
    description: 'Home use of biofeedback therapy is not covered.',
    is_alert: false
  },
  N88: {
    code: 'N88',
    description:
      "Alert: This payment is being made conditionally. An HHA episode of care notice has been filed for this patient. When a patient is treated under a HHA episode of care, consolidated billing requires that certain therapy services and supplies, such as this, be included in the HHA's payment. This payment will need to be recouped from you if we establish that the patient is concurrently receiving treatment under a HHA episode of care.",
    is_alert: true
  },
  N89: {
    code: 'N89',
    description:
      'Alert: Payment information for this claim has been forwarded to more than one other payer, but format limitations permit only one of the secondary payers to be identified in this remittance advice.',
    is_alert: true
  },
  N90: {
    code: 'N90',
    description: 'Covered only when performed by the attending physician.',
    is_alert: false
  },
  N91: {
    code: 'N91',
    description: 'Services not included in the appeal review.',
    is_alert: false
  },
  N92: {
    code: 'N92',
    description: 'This facility is not certified for digital mammography.',
    is_alert: false
  },
  N93: {
    code: 'N93',
    description:
      'A separate claim must be submitted for each place of service. Services furnished at multiple sites may not be billed in the same claim.',
    is_alert: false
  },
  N94: {
    code: 'N94',
    description:
      'Claim/Service denied because a more specific taxonomy code is required for adjudication.',
    is_alert: false
  },
  N95: {
    code: 'N95',
    description: 'This provider type/provider specialty may not bill this service.',
    is_alert: false
  },
  N96: {
    code: 'N96',
    description:
      'Patient must be refractory to conventional therapy (documented behavioral, pharmacologic and/or surgical corrective therapy) and be an appropriate surgical candidate such that implantation with anesthesia can occur.',
    is_alert: false
  },
  N97: {
    code: 'N97',
    description:
      'Patients with stress incontinence, urinary obstruction, and specific neurologic diseases (e.g., diabetes with peripheral nerve involvement) which are associated with secondary manifestations of the above three indications are excluded.',
    is_alert: false
  },
  N98: {
    code: 'N98',
    description:
      'Patient must have had a successful test stimulation in order to support subsequent implantation. Before a patient is eligible for permanent implantation, he/she must demonstrate a 50 percent or greater improvement through test stimulation. Improvement is measured through voiding diaries.',
    is_alert: false
  },
  N99: {
    code: 'N99',
    description:
      'Patient must be able to demonstrate adequate ability to record voiding diary data such that clinical results of the implant procedure can be properly evaluated.',
    is_alert: false
  },
  N100: {
    code: 'N100',
    description: 'PPS (Prospect Payment System) code corrected during adjudication.',
    is_alert: false
  },
  N101: {
    code: 'N101',
    description:
      "Additional information is needed in order to process this claim. Please resubmit the claim with the identification number of the provider where this service took place. The Medicare number of the site of service provider should be preceded with the letters 'HSP' and entered into item #32 on the claim form. You may bill only one site of service provider number per claim.",
    is_alert: false
  },
  N102: {
    code: 'N102',
    description:
      'This claim has been denied without reviewing the medical/dental record because the requested records were not received or were not received timely.',
    is_alert: false
  },
  N103: {
    code: 'N103',
    description:
      'Records indicate this patient was a prisoner or in custody of a Federal, State, or local authority when the service was rendered. This payer does not cover items and services furnished to an individual while he or she is in custody under a penal statute or rule, unless under State or local law, the individual is personally liable for the cost of his or her health care while in custody and the State or local government pursues the collection of such debt in the same way and with the same vigor as the collection of its other debts. The provider can collect from the Federal/State/ Local Authority as appropriate.',
    is_alert: false
  },
  N104: {
    code: 'N104',
    description:
      'This claim/service is not payable under our claims jurisdiction area. You can identify the correct Medicare contractor to process this claim/service through the CMS website at www.cms.gov.',
    is_alert: false
  },
  N105: {
    code: 'N105',
    description:
      'This is a misdirected claim/service for an RRB beneficiary. Submit paper claims to the RRB carrier: Palmetto GBA, P.O. Box 10066, Augusta, GA 30999. Call 888-355-9165 for RRB EDI information for electronic claims processing.',
    is_alert: false
  },
  N106: {
    code: 'N106',
    description:
      'Payment for services furnished to Skilled Nursing Facility (SNF) inpatients (except for excluded services) can only be made to the SNF. You must request payment from the SNF rather than the patient for this service.',
    is_alert: false
  },
  N107: {
    code: 'N107',
    description:
      'Services furnished to Skilled Nursing Facility (SNF) inpatients must be billed on the inpatient claim. They cannot be billed separately as outpatient services.',
    is_alert: false
  },
  N108: {
    code: 'N108',
    description: 'Missing/incomplete/invalid upgrade information.',
    is_alert: false
  },
  N109: {
    code: 'N109',
    description: 'Alert: This claim/service was chosen for complex review.',
    is_alert: true
  },
  N110: {
    code: 'N110',
    description: 'This facility is not certified for film mammography.',
    is_alert: false
  },
  N111: {
    code: 'N111',
    description:
      'No appeal right except duplicate claim/service issue. This service was included in a claim that has been previously billed and adjudicated.',
    is_alert: false
  },
  N112: {
    code: 'N112',
    description: 'This claim is excluded from your electronic remittance advice.',
    is_alert: false
  },
  N113: {
    code: 'N113',
    description: 'Only one initial visit is covered per physician, group practice or provider.',
    is_alert: false
  },
  N114: {
    code: 'N114',
    description:
      'During the transition to the Ambulance Fee Schedule, payment is based on the lesser of a blended amount calculated using a percentage of the reasonable charge/cost and fee schedule amounts, or the submitted charge for the service. You will be notified yearly what the percentages for the blended payment calculation will be.',
    is_alert: false
  },
  N115: {
    code: 'N115',
    description:
      'This decision was based on a Local Coverage Determination (LCD). An LCD provides a guide to assist in determining whether a particular item or service is covered. A copy of this policy is available at www.cms.gov/mcd, or if you do not have web access, you may contact the contractor to request a copy of the LCD.',
    is_alert: false
  },
  N116: {
    code: 'N116',
    description:
      "Alert: This payment is being made conditionally because the service was provided in the home, and it is possible that the patient is under a home health episode of care. When a patient is treated under a home health episode of care, consolidated billing requires that certain therapy services and supplies, such as this, be included in the home health agency's (HHA's) payment. This payment will need to be recouped from you if we establish that the patient is concurrently receiving treatment under an HHA episode of care.",
    is_alert: true
  },
  N117: {
    code: 'N117',
    description: "This service is paid only once in a patient's lifetime.",
    is_alert: false
  },
  N118: {
    code: 'N118',
    description: 'This service is not paid if billed more than once every 28 days.',
    is_alert: false
  },
  N119: {
    code: 'N119',
    description:
      'This service is not paid if billed once every 28 days, and the patient has spent 5 or more consecutive days in any inpatient or Skilled /nursing Facility (SNF) within those 28 days.',
    is_alert: false
  },
  N120: {
    code: 'N120',
    description:
      'Payment is subject to home health prospective payment system partial episode payment adjustment. Patient was transferred/discharged/readmitted during payment episode.',
    is_alert: false
  },
  N121: {
    code: 'N121',
    description:
      'Medicare Part B does not pay for items or services provided by this type of practitioner for beneficiaries in a Medicare Part A covered Skilled Nursing Facility (SNF) stay.',
    is_alert: false
  },
  N122: {
    code: 'N122',
    description: 'Add-on code cannot be billed by itself.',
    is_alert: false
  },
  N123: {
    code: 'N123',
    description:
      'Alert: This is a split service and represents a portion of the units from the originally submitted service.',
    is_alert: true
  },
  N124: {
    code: 'N124',
    description:
      'Payment has been denied for the/made only for a less extensive service/item because the information furnished does not substantiate the need for the (more extensive) service/item. The patient is liable for the charges for this service/item as you informed the patient in writing before the service/item was furnished that we would not pay for it, and the patient agreed to pay.',
    is_alert: false
  },
  N125: {
    code: 'N125',
    description:
      'Payment has been (denied for the/made only for a less extensive) service/item because the information furnished does not substantiate the need for the (more extensive) service/item. If you have collected any amount from the patient, you must refund that amount to the patient within 30 days of receiving this notice.',
    is_alert: false
  },
  N126: {
    code: 'N126',
    description:
      'Social Security Records indicate that this individual has been deported. This payer does not cover items and services furnished to individuals who have been deported.',
    is_alert: false
  },
  N127: {
    code: 'N127',
    description:
      'This is a misdirected claim/service for a United Mine Workers of America (UMWA) beneficiary. Please submit claims to them.',
    is_alert: false
  },
  N128: {
    code: 'N128',
    description: 'This amount represents the prior to coverage portion of the allowance.',
    is_alert: false
  },
  N129: {
    code: 'N129',
    description: "Not eligible due to the patient's age.",
    is_alert: false
  },
  N130: {
    code: 'N130',
    description:
      'Consult plan benefit documents/guidelines for information about restrictions for this service.',
    is_alert: false
  },
  N131: {
    code: 'N131',
    description:
      'Total payments under multiple contracts cannot exceed the allowance for this service.',
    is_alert: false
  },
  N132: {
    code: 'N132',
    description:
      'Alert: Payments will cease for services rendered by this US Government debarred or excluded provider after the 30 day grace period as previously notified.',
    is_alert: true
  },
  N133: {
    code: 'N133',
    description:
      'Alert: Services for predetermination and services requesting payment are being processed separately.',
    is_alert: true
  },
  N134: {
    code: 'N134',
    description:
      'Alert: This represents your scheduled payment for this service. If treatment has been discontinued, please contact Customer Service.',
    is_alert: true
  },
  N135: {
    code: 'N135',
    description:
      "Record fees are the patient's responsibility and limited to the specified co-payment.",
    is_alert: false
  },
  N136: {
    code: 'N136',
    description:
      "Alert: To obtain information on the process to file an appeal in Arizona, call the Department's Consumer Assistance Office at (602) 912-8444 or (800) 325-2548.",
    is_alert: true
  },
  N137: {
    code: 'N137',
    description:
      "Alert: The provider acting on the Member's behalf, may file an appeal with the Payer. The provider, acting on the Member's behalf, may file a complaint with the State Insurance Regulatory Authority without first filing an appeal, if the coverage decision involves an urgent condition for which care has not been rendered. The address may be obtained from the State Insurance Regulatory Authority.",
    is_alert: true
  },
  N138: {
    code: 'N138',
    description:
      "Alert: In the event you disagree with the Dental Advisor's opinion and have additional information relative to the case, you may submit radiographs to the Dental Advisor Unit at the subscriber's dental insurance carrier for a second Independent Dental Advisor Review.",
    is_alert: true
  },
  N139: {
    code: 'N139',
    description:
      "Alert: Under 32 CFR 199.13, a non-participating provider is not an appropriate appealing party. Therefore, if you disagree with the Dental Advisor's opinion, you may appeal the determination if appointed in writing, by the beneficiary, to act as his/her representative. Should you be appointed as a representative, submit a copy of this letter, a signed statement explaining the matter in which you disagree, and any radiographs and relevant information to the subscriber's Dental insurance carrier within 90 days from the date of this letter.",
    is_alert: true
  },
  N140: {
    code: 'N140',
    description:
      "Alert: You have not been designated as an authorized OCONUS provider therefore are not considered an appropriate appealing party. If the beneficiary has appointed you, in writing, to act as his/her representative and you disagree with the Dental Advisor's opinion, you may appeal by submitting a copy of this letter, a signed statement explaining the matter in which you disagree, and any relevant information to the subscriber's Dental insurance carrier within 90 days from the date of this letter.",
    is_alert: true
  },
  N141: {
    code: 'N141',
    description:
      'The patient was not residing in a long-term care facility during all or part of the service dates billed.',
    is_alert: false
  },
  N142: {
    code: 'N142',
    description: 'The original claim was denied. Resubmit a new claim, not a replacement claim.',
    is_alert: false
  },
  N143: {
    code: 'N143',
    description:
      'The patient was not in a hospice program during all or part of the service dates billed.',
    is_alert: false
  },
  N144: {
    code: 'N144',
    description: 'The rate changed during the dates of service billed.',
    is_alert: false
  },
  N145: {
    code: 'N145',
    description: 'Missing/incomplete/invalid provider identifier for this place of service.',
    is_alert: false
  },
  N146: {
    code: 'N146',
    description: 'Missing screening document.',
    is_alert: false
  },
  N147: {
    code: 'N147',
    description:
      'Long term care case mix or per diem rate cannot be determined because the patient ID number is missing, incomplete, or invalid on the assignment request.',
    is_alert: false
  },
  N148: {
    code: 'N148',
    description: 'Missing/incomplete/invalid date of last menstrual period.',
    is_alert: false
  },
  N149: {
    code: 'N149',
    description: 'Rebill all applicable services on a single claim.',
    is_alert: false
  },
  N150: {
    code: 'N150',
    description: 'Missing/incomplete/invalid model number.',
    is_alert: false
  },
  N151: {
    code: 'N151',
    description:
      'Telephone contact services will not be paid until the face-to-face contact requirement has been met.',
    is_alert: false
  },
  N152: {
    code: 'N152',
    description: 'Missing/incomplete/invalid replacement claim information.',
    is_alert: false
  },
  N153: {
    code: 'N153',
    description: 'Missing/incomplete/invalid room and board rate.',
    is_alert: false
  },
  N154: {
    code: 'N154',
    description: "Alert: This payment was delayed for correction of provider's mailing address.",
    is_alert: true
  },
  N155: {
    code: 'N155',
    description:
      'Alert: Our records do not indicate that other insurance is on file. Please submit other insurance information for our records.',
    is_alert: true
  },
  N156: {
    code: 'N156',
    description:
      'Alert: The patient is responsible for the difference between the approved treatment and the elective treatment.',
    is_alert: true
  },
  N157: {
    code: 'N157',
    description: 'Transportation to/from this destination is not covered.',
    is_alert: false
  },
  N158: {
    code: 'N158',
    description: 'Transportation in a vehicle other than an ambulance is not covered.',
    is_alert: false
  },
  N159: {
    code: 'N159',
    description:
      'Payment denied/reduced because mileage is not covered when the patient is not in the ambulance.',
    is_alert: false
  },
  N160: {
    code: 'N160',
    description:
      'The patient must choose an option before a payment can be made for this procedure/ equipment/ supply/ service.',
    is_alert: false
  },
  N161: {
    code: 'N161',
    description: 'This drug/service/supply is covered only when the associated service is covered.',
    is_alert: false
  },
  N162: {
    code: 'N162',
    description:
      'Alert: Although your claim was paid, you have billed for a test/specialty not included in your Laboratory Certification. Your failure to correct the laboratory certification information will result in a denial of payment in the near future.',
    is_alert: true
  },
  N163: {
    code: 'N163',
    description: 'Medical record does not support code billed per the code definition.',
    is_alert: false
  },
  N164: {
    code: 'N164',
    description: 'Transportation to/from this destination is not covered.',
    is_alert: false
  },
  N165: {
    code: 'N165',
    description: 'Transportation in a vehicle other than an ambulance is not covered.',
    is_alert: false
  },
  N166: {
    code: 'N166',
    description:
      'Payment denied/reduced because mileage is not covered when the patient is not in the ambulance.',
    is_alert: false
  },
  N167: {
    code: 'N167',
    description: 'Charges exceed the post-transplant coverage limit.',
    is_alert: false
  },
  N168: {
    code: 'N168',
    description:
      'The patient must choose an option before a payment can be made for this procedure/ equipment/ supply/ service.',
    is_alert: false
  },
  N169: {
    code: 'N169',
    description: 'This drug/service/supply is covered only when the associated service is covered.',
    is_alert: false
  },
  N170: {
    code: 'N170',
    description: 'A new/revised/renewed certificate of medical necessity is needed.',
    is_alert: false
  },
  N171: {
    code: 'N171',
    description:
      'Payment for repair or replacement is not covered or has exceeded the purchase price.',
    is_alert: false
  },
  N172: {
    code: 'N172',
    description:
      'The patient is not liable for the denied/adjusted charge(s) for receiving any updated service/item.',
    is_alert: false
  },
  N173: {
    code: 'N173',
    description: 'No qualifying hospital stay dates were provided for this episode of care.',
    is_alert: false
  },
  N174: {
    code: 'N174',
    description:
      "This is not a covered service/procedure/ equipment/bed, however patient liability is limited to amounts shown in the adjustments under group 'PR'.",
    is_alert: false
  },
  N175: {
    code: 'N175',
    description: 'Missing review organization approval.',
    is_alert: false
  },
  N176: {
    code: 'N176',
    description:
      'Services provided aboard a ship are covered only when the ship is of United States registry and is in United States waters. In addition, a doctor licensed to practice in the United States must provide the service.',
    is_alert: false
  },
  N177: {
    code: 'N177',
    description:
      "Alert: We did not send this claim to patient's other insurer. They have indicated no additional payment can be made.",
    is_alert: true
  },
  N178: {
    code: 'N178',
    description: 'Missing pre-operative images/visual field results.',
    is_alert: false
  },
  N179: {
    code: 'N179',
    description:
      'Additional information has been requested from the member. The charges will be reconsidered upon receipt of that information.',
    is_alert: false
  },
  N180: {
    code: 'N180',
    description:
      'This item or service does not meet the criteria for the category under which it was billed.',
    is_alert: false
  },
  N181: {
    code: 'N181',
    description:
      'Additional information is required from another provider involved in this service.',
    is_alert: false
  },
  N182: {
    code: 'N182',
    description: 'This claim/service must be billed according to the schedule for this plan.',
    is_alert: false
  },
  N183: {
    code: 'N183',
    description:
      'Alert: This is a predetermination advisory message, when this service is submitted for payment additional documentation as specified in plan documents will be required to process benefits.',
    is_alert: true
  },
  N184: {
    code: 'N184',
    description: 'Rebill technical and professional components separately.',
    is_alert: false
  },
  N185: {
    code: 'N185',
    description: 'Alert: Do not resubmit this claim/service.',
    is_alert: true
  },
  N186: {
    code: 'N186',
    description:
      'Non-Availability Statement (NAS) required for this service. Contact the nearest Military Treatment Facility (MTF) for assistance.',
    is_alert: false
  },
  N187: {
    code: 'N187',
    description:
      'Alert: You may request a review in writing within the required time limits following receipt of this notice by following the instructions included in your contract or plan benefit documents.',
    is_alert: true
  },
  N188: {
    code: 'N188',
    description: 'The approved level of care does not match the procedure code submitted.',
    is_alert: false
  },
  N189: {
    code: 'N189',
    description:
      "Alert: This service has been paid as a one-time exception to the plan's benefit restrictions.",
    is_alert: true
  },
  N190: {
    code: 'N190',
    description: 'Missing contract indicator.',
    is_alert: false
  },
  N191: {
    code: 'N191',
    description: 'The provider must update insurance information directly with payer.',
    is_alert: false
  },
  N192: {
    code: 'N192',
    description: 'Alert: Patient is a Medicaid/Qualified Medicare Beneficiary.',
    is_alert: true
  },
  N193: {
    code: 'N193',
    description:
      'Alert: Specific federal/state/local program may cover this service through another payer.',
    is_alert: true
  },
  N194: {
    code: 'N194',
    description: 'Technical component not paid if provider does not own the equipment used.',
    is_alert: false
  },
  N195: {
    code: 'N195',
    description: 'The technical component must be billed separately.',
    is_alert: false
  },
  N196: {
    code: 'N196',
    description: 'Alert: Patient eligible to apply for other coverage which may be primary.',
    is_alert: true
  },
  N197: {
    code: 'N197',
    description: 'The subscriber must update insurance information directly with payer.',
    is_alert: false
  },
  N198: {
    code: 'N198',
    description: 'Rendering provider must be affiliated with the pay-to provider.',
    is_alert: false
  },
  N199: {
    code: 'N199',
    description: 'Additional payment/recoupment approved based on payer-initiated review/audit.',
    is_alert: false
  },
  N200: {
    code: 'N200',
    description: 'The professional component must be billed separately.',
    is_alert: false
  },
  N201: {
    code: 'N201',
    description:
      'A mental health facility is responsible for payment of outside providers who furnish these services/supplies to residents.',
    is_alert: false
  },
  N202: {
    code: 'N202',
    description: 'Alert: Additional information/explanation will be sent separately.',
    is_alert: true
  },
  N203: {
    code: 'N203',
    description: 'Missing/incomplete/invalid anesthesia time/units.',
    is_alert: false
  },
  N204: {
    code: 'N204',
    description:
      'Services under review for possible pre-existing condition. Send medical records for prior 12 months',
    is_alert: false
  },
  N205: {
    code: 'N205',
    description: 'Information provided was illegible.',
    is_alert: false
  },
  N206: {
    code: 'N206',
    description: 'The supporting documentation does not match the information sent on the claim.',
    is_alert: false
  },
  N207: {
    code: 'N207',
    description: 'Missing/incomplete/invalid weight.',
    is_alert: false
  },
  N208: {
    code: 'N208',
    description: 'Missing/incomplete/invalid DRG code.',
    is_alert: false
  },
  N209: {
    code: 'N209',
    description: 'Missing/incomplete/invalid taxpayer identification number (TIN).',
    is_alert: false
  },
  N210: {
    code: 'N210',
    description: 'Alert: You may appeal this decision.',
    is_alert: true
  },
  N211: {
    code: 'N211',
    description: 'Alert: You may not appeal this decision.',
    is_alert: true
  },
  N212: {
    code: 'N212',
    description: 'Charges processed under a Point of Service benefit.',
    is_alert: false
  },
  N213: {
    code: 'N213',
    description:
      'Missing/incomplete/invalid facility/discrete unit DRG/DRG exempt status information.',
    is_alert: false
  },
  N214: {
    code: 'N214',
    description: 'Missing/incomplete/invalid history of the related initial surgical procedure(s).',
    is_alert: false
  },
  N215: {
    code: 'N215',
    description:
      'Alert: A payer providing supplemental or secondary coverage shall not require a claims determination for this service from a primary payer as a condition of making its own claims determination.',
    is_alert: true
  },
  N216: {
    code: 'N216',
    description:
      'We do not offer coverage for this type of service or the patient is not enrolled in this portion of our benefit package.',
    is_alert: false
  },
  N217: {
    code: 'N217',
    description: 'We pay only one site of service per provider per claim.',
    is_alert: false
  },
  N218: {
    code: 'N218',
    description:
      'You must furnish and service this item for as long as the patient continues to need it. We can pay for maintenance and/or servicing for the time period specified in the contract or coverage manual.',
    is_alert: false
  },
  N219: {
    code: 'N219',
    description: "Payment based on previous payer's allowed amount.",
    is_alert: false
  },
  N220: {
    code: 'N220',
    description:
      "Alert: See the payer's web site or contact the payer's Customer Service department to obtain forms and instructions for filing a provider dispute.",
    is_alert: true
  },
  N221: {
    code: 'N221',
    description: 'Missing Admitting History and Physical report.',
    is_alert: false
  },
  N222: {
    code: 'N222',
    description: 'Incomplete/invalid Admitting History and Physical report.',
    is_alert: false
  },
  N223: {
    code: 'N223',
    description: 'Missing documentation of benefit to the patient during initial treatment period.',
    is_alert: false
  },
  N224: {
    code: 'N224',
    description:
      'Incomplete/invalid documentation of benefit to the patient during initial treatment period.',
    is_alert: false
  },
  N225: {
    code: 'N225',
    description: 'Incomplete/invalid documentation/orders/notes/summary/report/chart.',
    is_alert: false
  },
  N226: {
    code: 'N226',
    description: 'Incomplete/invalid American Diabetes Association Certificate of Recognition.',
    is_alert: false
  },
  N227: {
    code: 'N227',
    description: 'Incomplete/invalid Certificate of Medical Necessity.',
    is_alert: false
  },
  N228: {
    code: 'N228',
    description: 'Incomplete/invalid consent form.',
    is_alert: false
  },
  N229: {
    code: 'N229',
    description: 'Incomplete/invalid contract indicator.',
    is_alert: false
  },
  N230: {
    code: 'N230',
    description:
      'Incomplete/invalid indication of whether the patient owns the equipment that requires the part or supply.',
    is_alert: false
  },
  N231: {
    code: 'N231',
    description:
      'Incomplete/invalid invoice or statement certifying the actual cost of the lens, less discounts, and/or the type of intraocular lens used.',
    is_alert: false
  },
  N232: {
    code: 'N232',
    description: 'Incomplete/invalid itemized bill/statement.',
    is_alert: false
  },
  N233: {
    code: 'N233',
    description: 'Incomplete/invalid operative note/report.',
    is_alert: false
  },
  N234: {
    code: 'N234',
    description: 'Incomplete/invalid oxygen certification/re-certification.',
    is_alert: false
  },
  N235: {
    code: 'N235',
    description: 'Incomplete/invalid pacemaker registration form.',
    is_alert: false
  },
  N236: {
    code: 'N236',
    description: 'Incomplete/invalid pathology report.',
    is_alert: false
  },
  N237: {
    code: 'N237',
    description: 'Incomplete/invalid patient medical record for this service.',
    is_alert: false
  },
  N238: {
    code: 'N238',
    description: 'Incomplete/invalid physician certified plan of care.',
    is_alert: false
  },
  N239: {
    code: 'N239',
    description: 'Incomplete/invalid physician financial relationship form.',
    is_alert: false
  },
  N240: {
    code: 'N240',
    description: 'Incomplete/invalid radiology report.',
    is_alert: false
  },
  N241: {
    code: 'N241',
    description: 'Incomplete/invalid review organization approval.',
    is_alert: false
  },
  N242: {
    code: 'N242',
    description: 'Incomplete/invalid radiology film(s)/image(s).',
    is_alert: false
  },
  N243: {
    code: 'N243',
    description: 'Incomplete/invalid/not approved screening document.',
    is_alert: false
  },
  N244: {
    code: 'N244',
    description: 'Incomplete/Invalid pre-operative images/visual field results.',
    is_alert: false
  },
  N245: {
    code: 'N245',
    description: 'Incomplete/invalid plan information for other insurance.',
    is_alert: false
  },
  N246: {
    code: 'N246',
    description: 'State regulated patient payment limitations apply to this service.',
    is_alert: false
  },
  N247: {
    code: 'N247',
    description: 'Missing/incomplete/invalid assistant surgeon taxonomy.',
    is_alert: false
  },
  N248: {
    code: 'N248',
    description: 'Missing/incomplete/invalid assistant surgeon name.',
    is_alert: false
  },
  N249: {
    code: 'N249',
    description: 'Missing/incomplete/invalid assistant surgeon primary identifier.',
    is_alert: false
  },
  N250: {
    code: 'N250',
    description: 'Missing/incomplete/invalid assistant surgeon secondary identifier.',
    is_alert: false
  },
  N251: {
    code: 'N251',
    description: 'Missing/incomplete/invalid attending provider taxonomy.',
    is_alert: false
  },
  N252: {
    code: 'N252',
    description: 'Missing/incomplete/invalid attending provider name.',
    is_alert: false
  },
  N253: {
    code: 'N253',
    description: 'Missing/incomplete/invalid attending provider primary identifier.',
    is_alert: false
  },
  N254: {
    code: 'N254',
    description: 'Missing/incomplete/invalid attending provider secondary identifier.',
    is_alert: false
  },
  N255: {
    code: 'N255',
    description: 'Missing/incomplete/invalid billing provider taxonomy.',
    is_alert: false
  },
  N256: {
    code: 'N256',
    description: 'Missing/incomplete/invalid billing provider/supplier name.',
    is_alert: false
  },
  N257: {
    code: 'N257',
    description: 'Missing/incomplete/invalid billing provider/supplier primary identifier.',
    is_alert: false
  },
  N258: {
    code: 'N258',
    description: 'Missing/incomplete/invalid billing provider/supplier address.',
    is_alert: false
  },
  N259: {
    code: 'N259',
    description: 'Missing/incomplete/invalid billing provider/supplier secondary identifier.',
    is_alert: false
  },
  N260: {
    code: 'N260',
    description: 'Missing/incomplete/invalid billing provider/supplier contact information.',
    is_alert: false
  },
  N261: {
    code: 'N261',
    description: 'Missing/incomplete/invalid operating provider name.',
    is_alert: false
  },
  N262: {
    code: 'N262',
    description: 'Missing/incomplete/invalid operating provider primary identifier.',
    is_alert: false
  },
  N263: {
    code: 'N263',
    description: 'Missing/incomplete/invalid operating provider secondary identifier.',
    is_alert: false
  },
  N264: {
    code: 'N264',
    description: 'Missing/incomplete/invalid ordering provider name.',
    is_alert: false
  },
  N265: {
    code: 'N265',
    description: 'Missing/incomplete/invalid ordering provider primary identifier.',
    is_alert: false
  },
  N266: {
    code: 'N266',
    description: 'Missing/incomplete/invalid ordering provider address.',
    is_alert: false
  },
  N267: {
    code: 'N267',
    description: 'Missing/incomplete/invalid ordering provider secondary identifier.',
    is_alert: false
  },
  N268: {
    code: 'N268',
    description: 'Missing/incomplete/invalid ordering provider contact information.',
    is_alert: false
  },
  N269: {
    code: 'N269',
    description: 'Missing/incomplete/invalid other provider name.',
    is_alert: false
  },
  N270: {
    code: 'N270',
    description: 'Missing/incomplete/invalid other provider primary identifier.',
    is_alert: false
  },
  N271: {
    code: 'N271',
    description: 'Missing/incomplete/invalid other provider secondary identifier.',
    is_alert: false
  },
  N272: {
    code: 'N272',
    description: 'Missing/incomplete/invalid other payer attending provider identifier.',
    is_alert: false
  },
  N273: {
    code: 'N273',
    description: 'Missing/incomplete/invalid other payer operating provider identifier.',
    is_alert: false
  },
  N274: {
    code: 'N274',
    description: 'Missing/incomplete/invalid other payer other provider identifier.',
    is_alert: false
  },
  N275: {
    code: 'N275',
    description: 'Missing/incomplete/invalid other payer purchased service provider identifier.',
    is_alert: false
  },
  N276: {
    code: 'N276',
    description: 'Missing/incomplete/invalid other payer referring provider identifier.',
    is_alert: false
  },
  N277: {
    code: 'N277',
    description: 'Missing/incomplete/invalid other payer rendering provider identifier.',
    is_alert: false
  },
  N278: {
    code: 'N278',
    description: 'Missing/incomplete/invalid other payer service facility provider identifier.',
    is_alert: false
  },
  N279: {
    code: 'N279',
    description: 'Missing/incomplete/invalid pay-to provider name.',
    is_alert: false
  },
  N280: {
    code: 'N280',
    description: 'Missing/incomplete/invalid pay-to provider primary identifier.',
    is_alert: false
  },
  N281: {
    code: 'N281',
    description: 'Missing/incomplete/invalid pay-to provider address.',
    is_alert: false
  },
  N282: {
    code: 'N282',
    description: 'Missing/incomplete/invalid pay-to provider secondary identifier.',
    is_alert: false
  },
  N283: {
    code: 'N283',
    description: 'Missing/incomplete/invalid purchased service provider identifier.',
    is_alert: false
  },
  N284: {
    code: 'N284',
    description: 'Missing/incomplete/invalid referring provider taxonomy.',
    is_alert: false
  },
  N285: {
    code: 'N285',
    description: 'Missing/incomplete/invalid referring provider name.',
    is_alert: false
  },
  N286: {
    code: 'N286',
    description: 'Missing/incomplete/invalid referring provider primary identifier.',
    is_alert: false
  },
  N287: {
    code: 'N287',
    description: 'Missing/incomplete/invalid referring provider secondary identifier.',
    is_alert: false
  },
  N288: {
    code: 'N288',
    description: 'Missing/incomplete/invalid rendering provider taxonomy.',
    is_alert: false
  },
  N289: {
    code: 'N289',
    description: 'Missing/incomplete/invalid rendering provider name.',
    is_alert: false
  },
  N290: {
    code: 'N290',
    description: 'Missing/incomplete/invalid rendering provider primary identifier.',
    is_alert: false
  },
  N291: {
    code: 'N291',
    description: 'Missing/incomplete/invalid rendering provider secondary identifier.',
    is_alert: false
  },
  N292: {
    code: 'N292',
    description: 'Missing/incomplete/invalid service facility name.',
    is_alert: false
  },
  N293: {
    code: 'N293',
    description: 'Missing/incomplete/invalid service facility primary identifier.',
    is_alert: false
  },
  N294: {
    code: 'N294',
    description: 'Missing/incomplete/invalid service facility primary address.',
    is_alert: false
  },
  N295: {
    code: 'N295',
    description: 'Missing/incomplete/invalid service facility secondary identifier.',
    is_alert: false
  },
  N296: {
    code: 'N296',
    description: 'Missing/incomplete/invalid supervising provider name.',
    is_alert: false
  },
  N297: {
    code: 'N297',
    description: 'Missing/incomplete/invalid supervising provider primary identifier.',
    is_alert: false
  },
  N298: {
    code: 'N298',
    description: 'Missing/incomplete/invalid supervising provider secondary identifier.',
    is_alert: false
  },
  N299: {
    code: 'N299',
    description: 'Missing/incomplete/invalid occurrence date(s).',
    is_alert: false
  },
  N300: {
    code: 'N300',
    description: 'Missing/incomplete/invalid occurrence span date(s).',
    is_alert: false
  },
  N301: {
    code: 'N301',
    description: 'Missing/incomplete/invalid procedure date(s).',
    is_alert: false
  },
  N302: {
    code: 'N302',
    description: 'Missing/incomplete/invalid other procedure date(s).',
    is_alert: false
  },
  N303: {
    code: 'N303',
    description: 'Missing/incomplete/invalid principal procedure date.',
    is_alert: false
  },
  N304: {
    code: 'N304',
    description: 'Missing/incomplete/invalid dispensed date.',
    is_alert: false
  },
  N305: {
    code: 'N305',
    description: 'Missing/incomplete/invalid injury/accident date.',
    is_alert: false
  },
  N306: {
    code: 'N306',
    description: 'Missing/incomplete/invalid acute manifestation date.',
    is_alert: false
  },
  N307: {
    code: 'N307',
    description: 'Missing/incomplete/invalid adjudication or payment date.',
    is_alert: false
  },
  N308: {
    code: 'N308',
    description: 'Missing/incomplete/invalid appliance placement date.',
    is_alert: false
  },
  N309: {
    code: 'N309',
    description: 'Missing/incomplete/invalid assessment date.',
    is_alert: false
  },
  N310: {
    code: 'N310',
    description: 'Missing/incomplete/invalid assumed or relinquished care date.',
    is_alert: false
  },
  N311: {
    code: 'N311',
    description: 'Missing/incomplete/invalid authorized to return to work date.',
    is_alert: false
  },
  N312: {
    code: 'N312',
    description: 'Missing/incomplete/invalid begin therapy date.',
    is_alert: false
  },
  N313: {
    code: 'N313',
    description: 'Missing/incomplete/invalid certification revision date.',
    is_alert: false
  },
  N314: {
    code: 'N314',
    description: 'Missing/incomplete/invalid diagnosis date.',
    is_alert: false
  },
  N315: {
    code: 'N315',
    description: 'Missing/incomplete/invalid disability from date.',
    is_alert: false
  },
  N316: {
    code: 'N316',
    description: 'Missing/incomplete/invalid disability to date.',
    is_alert: false
  },
  N317: {
    code: 'N317',
    description: 'Missing/incomplete/invalid discharge hour.',
    is_alert: false
  },
  N318: {
    code: 'N318',
    description: 'Missing/incomplete/invalid discharge or end of care date.',
    is_alert: false
  },
  N319: {
    code: 'N319',
    description: 'Missing/incomplete/invalid hearing or vision prescription date.',
    is_alert: false
  },
  N320: {
    code: 'N320',
    description: 'Missing/incomplete/invalid Home Health Certification Period.',
    is_alert: false
  },
  N321: {
    code: 'N321',
    description: 'Missing/incomplete/invalid last admission period.',
    is_alert: false
  },
  N322: {
    code: 'N322',
    description: 'Missing/incomplete/invalid last certification date.',
    is_alert: false
  },
  N323: {
    code: 'N323',
    description: 'Missing/incomplete/invalid last contact date.',
    is_alert: false
  },
  N324: {
    code: 'N324',
    description: 'Missing/incomplete/invalid last seen/visit date.',
    is_alert: false
  },
  N325: {
    code: 'N325',
    description: 'Missing/incomplete/invalid last worked date.',
    is_alert: false
  },
  N326: {
    code: 'N326',
    description: 'Missing/incomplete/invalid last x-ray date.',
    is_alert: false
  },
  N327: {
    code: 'N327',
    description: 'Missing/incomplete/invalid other insured birth date.',
    is_alert: false
  },
  N328: {
    code: 'N328',
    description: 'Missing/incomplete/invalid Oxygen Saturation Test date.',
    is_alert: false
  },
  N329: {
    code: 'N329',
    description: 'Missing/incomplete/invalid patient birth date.',
    is_alert: false
  },
  N330: {
    code: 'N330',
    description: 'Missing/incomplete/invalid patient death date.',
    is_alert: false
  },
  N331: {
    code: 'N331',
    description: 'Missing/incomplete/invalid physician order date.',
    is_alert: false
  },
  N332: {
    code: 'N332',
    description: 'Missing/incomplete/invalid prior hospital discharge date.',
    is_alert: false
  },
  N333: {
    code: 'N333',
    description: 'Missing/incomplete/invalid prior placement date.',
    is_alert: false
  },
  N334: {
    code: 'N334',
    description: 'Missing/incomplete/invalid re-evaluation date.',
    is_alert: false
  },
  N335: {
    code: 'N335',
    description: 'Missing/incomplete/invalid referral date.',
    is_alert: false
  },
  N336: {
    code: 'N336',
    description: 'Missing/incomplete/invalid replacement date.',
    is_alert: false
  },
  N337: {
    code: 'N337',
    description: 'Missing/incomplete/invalid secondary diagnosis date.',
    is_alert: false
  },
  N338: {
    code: 'N338',
    description: 'Missing/incomplete/invalid shipped date.',
    is_alert: false
  },
  N339: {
    code: 'N339',
    description: 'Missing/incomplete/invalid similar illness or symptom date.',
    is_alert: false
  },
  N340: {
    code: 'N340',
    description: 'Missing/incomplete/invalid subscriber birth date.',
    is_alert: false
  },
  N341: {
    code: 'N341',
    description: 'Missing/incomplete/invalid surgery date.',
    is_alert: false
  },
  N342: {
    code: 'N342',
    description: 'Missing/incomplete/invalid test performed date.',
    is_alert: false
  },
  N343: {
    code: 'N343',
    description:
      'Missing/incomplete/invalid Transcutaneous Electrical Nerve Stimulator (TENS) trial start date.',
    is_alert: false
  },
  N344: {
    code: 'N344',
    description:
      'Missing/incomplete/invalid Transcutaneous Electrical Nerve Stimulator (TENS) trial end date.',
    is_alert: false
  },
  N345: {
    code: 'N345',
    description: 'Date range not valid with units submitted.',
    is_alert: false
  },
  N346: {
    code: 'N346',
    description: 'Missing/incomplete/invalid oral cavity designation code.',
    is_alert: false
  },
  N347: {
    code: 'N347',
    description:
      'Your claim for a referred or purchased service cannot be paid because payment has already been made for this same service to another provider by a payment contractor representing the payer.',
    is_alert: false
  },
  N348: {
    code: 'N348',
    description:
      'You chose that this service/supply/drug would be rendered/supplied and billed by a different practitioner/supplier.',
    is_alert: false
  },
  N349: {
    code: 'N349',
    description: 'The administration method and drug must be reported to adjudicate this service.',
    is_alert: false
  },
  N350: {
    code: 'N350',
    description:
      'Missing/incomplete/invalid description of service for a Not Otherwise Classified (NOC) code or for an Unlisted/By Report procedure.',
    is_alert: false
  },
  N351: {
    code: 'N351',
    description: 'Service date outside of the approved treatment plan service dates.',
    is_alert: false
  },
  N352: {
    code: 'N352',
    description:
      'Alert: There are no scheduled payments for this service. Submit a claim for each patient visit.',
    is_alert: true
  },
  N353: {
    code: 'N353',
    description:
      'Alert: Benefits have been estimated, when the actual services have been rendered, additional payment will be considered based on the submitted claim.',
    is_alert: true
  },
  N354: {
    code: 'N354',
    description: 'Incomplete/invalid invoice.',
    is_alert: false
  },
  N355: {
    code: 'N355',
    description:
      'Alert: The law permits exceptions to the refund requirement in two cases: - If you did not know, and could not have reasonably been expected to know, that we would not pay for this service; or - If you notified the patient in writing before providing the service that you believed that we were likely to deny the service, and the patient signed a statement agreeing to pay for the service.',
    is_alert: true
  },
  N356: {
    code: 'N356',
    description: 'Not covered when performed with, or subsequent to, a non-covered service.',
    is_alert: false
  },
  N357: {
    code: 'N357',
    description:
      'Time frame requirements between this service/procedure/supply and a related service/procedure/supply have not been met.',
    is_alert: false
  },
  N358: {
    code: 'N358',
    description:
      'Alert: This decision may be reviewed if additional documentation as described in the contract or plan benefit documents is submitted.',
    is_alert: true
  },
  N359: {
    code: 'N359',
    description: 'Missing/incomplete/invalid height.',
    is_alert: false
  },
  N360: {
    code: 'N360',
    description:
      'Alert: Coordination of benefits has not been calculated when estimating benefits for this pre-determination. Submit payment information from the primary payer with the secondary claim.',
    is_alert: true
  },
  N361: {
    code: 'N361',
    description: 'Payment adjusted based on multiple diagnostic imaging procedure rules',
    is_alert: false
  },
  N362: {
    code: 'N362',
    description: 'The number of Days or Units of Service exceeds our acceptable maximum.',
    is_alert: false
  },
  N363: {
    code: 'N363',
    description:
      'Alert: in the near future we are implementing new policies/procedures that would affect this determination.',
    is_alert: true
  },
  N364: {
    code: 'N364',
    description:
      'Alert: According to our agreement, you must waive the deductible and/or coinsurance amounts.',
    is_alert: true
  },
  N365: {
    code: 'N365',
    description:
      'This procedure code is not payable. It is for reporting/information purposes only.',
    is_alert: false
  },
  N366: {
    code: 'N366',
    description:
      'Requested information not provided. The claim will be reopened if the information previously requested is submitted within one year after the date of this denial notice.',
    is_alert: false
  },
  N367: {
    code: 'N367',
    description:
      'Alert: The claim information has been forwarded to a Consumer Spending Account processor for review; for example, flexible spending account or health savings account.',
    is_alert: true
  },
  N368: {
    code: 'N368',
    description: 'You must appeal the determination of the previously adjudicated claim.',
    is_alert: false
  },
  N369: {
    code: 'N369',
    description:
      'Alert: Although this claim has been processed, it is deficient according to state legislation/regulation.',
    is_alert: true
  },
  N370: {
    code: 'N370',
    description: 'Billing exceeds the rental months covered/approved by the payer.',
    is_alert: false
  },
  N371: {
    code: 'N371',
    description: 'Alert: title of this equipment must be transferred to the patient.',
    is_alert: true
  },
  N372: {
    code: 'N372',
    description: 'Only reasonable and necessary maintenance/service charges are covered.',
    is_alert: false
  },
  N373: {
    code: 'N373',
    description:
      'It has been determined that another payer paid the services as primary when they were not the primary payer. Therefore, we are refunding to the payer that paid as primary on your behalf.',
    is_alert: false
  },
  N374: {
    code: 'N374',
    description:
      'Primary Medicare Part A insurance has been exhausted and a Part B Remittance Advice is required.',
    is_alert: false
  },
  N375: {
    code: 'N375',
    description:
      'Missing/incomplete/invalid questionnaire/information required to determine dependent eligibility.',
    is_alert: false
  },
  N376: {
    code: 'N376',
    description:
      'Subscriber/patient is assigned to active military duty, therefore primary coverage may be TRICARE.',
    is_alert: false
  },
  N377: {
    code: 'N377',
    description: 'Payment based on a processed replacement claim.',
    is_alert: false
  },
  N378: {
    code: 'N378',
    description: 'Missing/incomplete/invalid prescription quantity.',
    is_alert: false
  },
  N379: {
    code: 'N379',
    description: 'Claim level information does not match line level information.',
    is_alert: false
  },
  N380: {
    code: 'N380',
    description: 'The original claim has been processed, submit a corrected claim.',
    is_alert: false
  },
  N381: {
    code: 'N381',
    description:
      'Alert: Consult our contractual agreement for restrictions/billing/payment information related to these charges.',
    is_alert: true
  },
  N382: {
    code: 'N382',
    description: 'Missing/incomplete/invalid patient identifier.',
    is_alert: false
  },
  N383: {
    code: 'N383',
    description: 'Not covered when deemed cosmetic.',
    is_alert: false
  },
  N384: {
    code: 'N384',
    description:
      'Records indicate that the referenced body part/tooth has been removed in a previous procedure.',
    is_alert: false
  },
  N385: {
    code: 'N385',
    description: 'Notification of admission was not timely according to published plan procedures.',
    is_alert: false
  },
  N386: {
    code: 'N386',
    description:
      'This decision was based on a National Coverage Determination (NCD). An NCD provides a coverage determination as to whether a particular item or service is covered. A copy of this policy is available at www.cms.gov/mcd/search.asp. If you do not have web access, you may contact the contractor to request a copy of the NCD.',
    is_alert: false
  },
  N387: {
    code: 'N387',
    description:
      "Alert: Submit this claim to the patient's other insurer for potential payment of supplemental benefits. We did not forward the claim information.",
    is_alert: true
  },
  N388: {
    code: 'N388',
    description: 'Missing/incomplete/invalid prescription number.',
    is_alert: false
  },
  N389: {
    code: 'N389',
    description: 'Duplicate prescription number submitted.',
    is_alert: false
  },
  N390: {
    code: 'N390',
    description: 'This service/report cannot be billed separately.',
    is_alert: false
  },
  N391: {
    code: 'N391',
    description: 'Missing emergency department records.',
    is_alert: false
  },
  N392: {
    code: 'N392',
    description: 'Incomplete/invalid emergency department records.',
    is_alert: false
  },
  N393: {
    code: 'N393',
    description: 'Missing progress notes/report.',
    is_alert: false
  },
  N394: {
    code: 'N394',
    description: 'Incomplete/invalid progress notes/report.',
    is_alert: false
  },
  N395: {
    code: 'N395',
    description: 'Missing laboratory report.',
    is_alert: false
  },
  N396: {
    code: 'N396',
    description: 'Incomplete/invalid laboratory report.',
    is_alert: false
  },
  N397: {
    code: 'N397',
    description: 'Benefits are not available for incomplete service(s)/undelivered item(s).',
    is_alert: false
  },
  N398: {
    code: 'N398',
    description: 'Missing elective consent form.',
    is_alert: false
  },
  N399: {
    code: 'N399',
    description: 'Incomplete/invalid elective consent form.',
    is_alert: false
  },
  N400: {
    code: 'N400',
    description: 'Alert: Electronically enabled providers should submit claims electronically.',
    is_alert: true
  },
  N401: {
    code: 'N401',
    description: 'Missing periodontal charting.',
    is_alert: false
  },
  N402: {
    code: 'N402',
    description: 'Incomplete/invalid periodontal charting.',
    is_alert: false
  },
  N403: {
    code: 'N403',
    description: 'Missing facility certification.',
    is_alert: false
  },
  N404: {
    code: 'N404',
    description: 'Incomplete/invalid facility certification.',
    is_alert: false
  },
  N405: {
    code: 'N405',
    description:
      "This service is only covered when the donor's insurer(s) do not provide coverage for the service.",
    is_alert: false
  },
  N406: {
    code: 'N406',
    description:
      "This service is only covered when the recipient's insurer(s) do not provide coverage for the service.",
    is_alert: false
  },
  N407: {
    code: 'N407',
    description: 'You are not an approved submitter for this transmission format.',
    is_alert: false
  },
  N408: {
    code: 'N408',
    description: 'This payer does not cover deductibles assessed by a previous payer.',
    is_alert: false
  },
  N409: {
    code: 'N409',
    description:
      'This service is related to an accidental injury and is not covered unless provided within a specific time frame from the date of the accident.',
    is_alert: false
  },
  N410: {
    code: 'N410',
    description: 'Not covered unless the prescription changes.',
    is_alert: false
  },
  N411: {
    code: 'N411',
    description: 'This service is allowed one time in a 6-month period.',
    is_alert: false
  },
  N412: {
    code: 'N412',
    description: 'This service is allowed 2 times in a 12-month period.',
    is_alert: false
  },
  N413: {
    code: 'N413',
    description: 'This service is allowed 2 times in a benefit year.',
    is_alert: false
  },
  N414: {
    code: 'N414',
    description: 'This service is allowed 4 times in a 12-month period.',
    is_alert: false
  },
  N415: {
    code: 'N415',
    description: 'This service is allowed 1 time in an 18-month period.',
    is_alert: false
  },
  N416: {
    code: 'N416',
    description: 'This service is allowed 1 time in a 3-year period.',
    is_alert: false
  },
  N417: {
    code: 'N417',
    description: 'This service is allowed 1 time in a 5-year period.',
    is_alert: false
  },
  N418: {
    code: 'N418',
    description: "Misrouted claim. See the payer's claim submission instructions.",
    is_alert: false
  },
  N419: {
    code: 'N419',
    description:
      "Claim payment was the result of a payer's retroactive adjustment due to a retroactive rate change.",
    is_alert: false
  },
  N420: {
    code: 'N420',
    description:
      "Claim payment was the result of a payer's retroactive adjustment due to a Coordination of Benefits or Third Party Liability Recovery.",
    is_alert: false
  },
  N421: {
    code: 'N421',
    description:
      "Claim payment was the result of a payer's retroactive adjustment due to a review organization decision.",
    is_alert: false
  },
  N422: {
    code: 'N422',
    description:
      "Claim payment was the result of a payer's retroactive adjustment due to a payer's contract incentive program.",
    is_alert: false
  },
  N423: {
    code: 'N423',
    description:
      "Claim payment was the result of a payer's retroactive adjustment due to a non standard program.",
    is_alert: false
  },
  N424: {
    code: 'N424',
    description:
      'Patient does not reside in the geographic area required for this type of payment.',
    is_alert: false
  },
  N425: {
    code: 'N425',
    description: 'Statutorily excluded service(s).',
    is_alert: false
  },
  N426: {
    code: 'N426',
    description: 'No coverage when self-administered.',
    is_alert: false
  },
  N427: {
    code: 'N427',
    description:
      'Payment for eyeglasses or contact lenses can be made only after cataract surgery.',
    is_alert: false
  },
  N428: {
    code: 'N428',
    description: 'Not covered when performed in this place of service.',
    is_alert: false
  },
  N429: {
    code: 'N429',
    description: 'Not covered when considered routine.',
    is_alert: false
  },
  N430: {
    code: 'N430',
    description: 'Procedure code is inconsistent with the units billed.',
    is_alert: false
  },
  N431: {
    code: 'N431',
    description: 'Not covered with this procedure.',
    is_alert: false
  },
  N432: {
    code: 'N432',
    description: 'Alert: Adjustment based on a Recovery Audit.',
    is_alert: true
  },
  N433: {
    code: 'N433',
    description: 'Resubmit this claim using only your National Provider Identifier (NPI).',
    is_alert: false
  },
  N434: {
    code: 'N434',
    description: 'Missing/Incomplete/Invalid Present on Admission indicator.',
    is_alert: false
  },
  N435: {
    code: 'N435',
    description:
      'Exceeds number/frequency approved /allowed within time period without support documentation.',
    is_alert: false
  },
  N436: {
    code: 'N436',
    description:
      'The injury claim has not been accepted and a mandatory medical reimbursement has been made.',
    is_alert: false
  },
  N437: {
    code: 'N437',
    description: 'Alert: If the injury claim is accepted, these charges will be reconsidered.',
    is_alert: true
  },
  N438: {
    code: 'N438',
    description: 'This jurisdiction only accepts paper claims.',
    is_alert: false
  },
  N439: {
    code: 'N439',
    description: 'Missing anesthesia physical status report/indicators.',
    is_alert: false
  },
  N440: {
    code: 'N440',
    description: 'Incomplete/invalid anesthesia physical status report/indicators.',
    is_alert: false
  },
  N441: {
    code: 'N441',
    description: 'This missed/cancelled appointment is not covered.',
    is_alert: false
  },
  N442: {
    code: 'N442',
    description: 'Payment based on an alternate fee schedule.',
    is_alert: false
  },
  N443: {
    code: 'N443',
    description: 'Missing/incomplete/invalid total time or begin/end time.',
    is_alert: false
  },
  N444: {
    code: 'N444',
    description:
      "Alert: This facility has not filed the Election for High Cost Outlier form with the Division of Workers' Compensation.",
    is_alert: true
  },
  N445: {
    code: 'N445',
    description: 'Missing document for actual cost or paid amount.',
    is_alert: false
  },
  N446: {
    code: 'N446',
    description: 'Incomplete/invalid document for actual cost or paid amount.',
    is_alert: false
  },
  N447: {
    code: 'N447',
    description:
      'Payment is based on a generic equivalent as required documentation was not provided.',
    is_alert: false
  },
  N448: {
    code: 'N448',
    description:
      'This drug/service/supply is not included in the fee schedule or contracted/legislated fee arrangement.',
    is_alert: false
  },
  N449: {
    code: 'N449',
    description: 'Payment based on a comparable drug/service/supply.',
    is_alert: false
  },
  N450: {
    code: 'N450',
    description: 'Covered only when performed by the primary treating physician or the designee.',
    is_alert: false
  },
  N451: {
    code: 'N451',
    description: 'Missing Admission Summary Report.',
    is_alert: false
  },
  N452: {
    code: 'N452',
    description: 'Incomplete/invalid Admission Summary Report.',
    is_alert: false
  },
  N453: {
    code: 'N453',
    description: 'Missing Consultation Report.',
    is_alert: false
  },
  N454: {
    code: 'N454',
    description: 'Incomplete/invalid Consultation Report.',
    is_alert: false
  },
  N455: {
    code: 'N455',
    description: 'Missing Physician Order.',
    is_alert: false
  },
  N456: {
    code: 'N456',
    description: 'Incomplete/invalid Physician Order.',
    is_alert: false
  },
  N457: {
    code: 'N457',
    description: 'Missing Diagnostic Report.',
    is_alert: false
  },
  N458: {
    code: 'N458',
    description: 'Incomplete/invalid Diagnostic Report.',
    is_alert: false
  },
  N459: {
    code: 'N459',
    description: 'Missing Discharge Summary.',
    is_alert: false
  },
  N460: {
    code: 'N460',
    description: 'Incomplete/invalid Discharge Summary.',
    is_alert: false
  },
  N461: {
    code: 'N461',
    description: 'Missing Nursing Notes.',
    is_alert: false
  },
  N462: {
    code: 'N462',
    description: 'Incomplete/invalid Nursing Notes.',
    is_alert: false
  },
  N463: {
    code: 'N463',
    description: 'Missing support data for claim.',
    is_alert: false
  },
  N464: {
    code: 'N464',
    description: 'Incomplete/invalid support data for claim.',
    is_alert: false
  },
  N465: {
    code: 'N465',
    description: 'Missing Physical Therapy Notes/Report.',
    is_alert: false
  },
  N466: {
    code: 'N466',
    description: 'Incomplete/invalid Physical Therapy Notes/Report.',
    is_alert: false
  },
  N467: {
    code: 'N467',
    description: 'Missing Tests and Analysis Report.',
    is_alert: false
  },
  N468: {
    code: 'N468',
    description: 'Incomplete/invalid Report of Tests and Analysis Report.',
    is_alert: false
  },
  N469: {
    code: 'N469',
    description:
      'Alert: Claim/Service(s) subject to appeal process, see section 935 of Medicare Prescription Drug, Improvement, and Modernization Act of 2003 (MMA).',
    is_alert: true
  },
  N470: {
    code: 'N470',
    description: 'This payment will complete the mandatory medical reimbursement limit.',
    is_alert: false
  },
  N471: {
    code: 'N471',
    description: 'Missing/incomplete/invalid HIPPS Rate Code.',
    is_alert: false
  },
  N472: {
    code: 'N472',
    description: 'Payment for this service has been issued to another provider.',
    is_alert: false
  },
  N473: {
    code: 'N473',
    description: 'Missing certification.',
    is_alert: false
  },
  N474: {
    code: 'N474',
    description: 'Incomplete/invalid certification.',
    is_alert: false
  },
  N475: {
    code: 'N475',
    description: 'Missing completed referral form.',
    is_alert: false
  },
  N476: {
    code: 'N476',
    description: 'Incomplete/invalid completed referral form.',
    is_alert: false
  },
  N477: {
    code: 'N477',
    description: 'Missing Dental Models.',
    is_alert: false
  },
  N478: {
    code: 'N478',
    description: 'Incomplete/invalid Dental Models.',
    is_alert: false
  },
  N479: {
    code: 'N479',
    description:
      'Missing Explanation of Benefits (Coordination of Benefits or Medicare Secondary Payer).',
    is_alert: false
  },
  N480: {
    code: 'N480',
    description:
      'Incomplete/invalid Explanation of Benefits (Coordination of Benefits or Medicare Secondary Payer).',
    is_alert: false
  },
  N481: {
    code: 'N481',
    description: 'Missing Models.',
    is_alert: false
  },
  N482: {
    code: 'N482',
    description: 'Incomplete/invalid Models.',
    is_alert: false
  },
  N483: {
    code: 'N483',
    description: 'Missing Periodontal Charts.',
    is_alert: false
  },
  N484: {
    code: 'N484',
    description: 'Incomplete/invalid Periodontal Charts.',
    is_alert: false
  },
  N485: {
    code: 'N485',
    description: 'Missing Physical Therapy Certification.',
    is_alert: false
  },
  N486: {
    code: 'N486',
    description: 'Incomplete/invalid Physical Therapy Certification.',
    is_alert: false
  },
  N487: {
    code: 'N487',
    description: 'Missing Prosthetics or Orthotics Certification.',
    is_alert: false
  },
  N488: {
    code: 'N488',
    description: 'Incomplete/invalid Prosthetics or Orthotics Certification.',
    is_alert: false
  },
  N489: {
    code: 'N489',
    description: 'Missing referral form.',
    is_alert: false
  },
  N490: {
    code: 'N490',
    description: 'Incomplete/invalid referral form.',
    is_alert: false
  },
  N491: {
    code: 'N491',
    description: 'Missing/Incomplete/Invalid Exclusionary Rider Condition.',
    is_alert: false
  },
  N492: {
    code: 'N492',
    description:
      'Alert: A network provider may bill the member for this service if the member requested the service and agreed in writing, prior to receiving the service, to be financially responsible for the billed charge.',
    is_alert: true
  },
  N493: {
    code: 'N493',
    description: 'Missing Doctor First Report of Injury.',
    is_alert: false
  },
  N494: {
    code: 'N494',
    description: 'Incomplete/invalid Doctor First Report of Injury.',
    is_alert: false
  },
  N495: {
    code: 'N495',
    description: 'Missing Supplemental Medical Report.',
    is_alert: false
  },
  N496: {
    code: 'N496',
    description: 'Incomplete/invalid Supplemental Medical Report.',
    is_alert: false
  },
  N497: {
    code: 'N497',
    description: 'Missing Medical Permanent Impairment or Disability Report.',
    is_alert: false
  },
  N498: {
    code: 'N498',
    description: 'Incomplete/invalid Medical Permanent Impairment or Disability Report.',
    is_alert: false
  },
  N499: {
    code: 'N499',
    description: 'Missing Medical Legal Report.',
    is_alert: false
  },
  N500: {
    code: 'N500',
    description: 'Incomplete/invalid Medical Legal Report.',
    is_alert: false
  },
  N501: {
    code: 'N501',
    description: 'Missing Vocational Report.',
    is_alert: false
  },
  N502: {
    code: 'N502',
    description: 'Incomplete/invalid Vocational Report.',
    is_alert: false
  },
  N503: {
    code: 'N503',
    description: 'Missing Work Status Report.',
    is_alert: false
  },
  N504: {
    code: 'N504',
    description: 'Incomplete/invalid Work Status Report.',
    is_alert: false
  },
  N505: {
    code: 'N505',
    description:
      'Alert: This response includes only services that could be estimated in real-time. No estimate will be provided for the services that could not be estimated in real-time.',
    is_alert: true
  },
  N506: {
    code: 'N506',
    description:
      "Alert: This is an estimate of the member's liability based on the information available at the time the estimate was processed. Actual coverage and member liability amounts will be determined when the claim is processed. This is not a pre-authorization or a guarantee of payment.",
    is_alert: true
  },
  N507: {
    code: 'N507',
    description: 'Plan distance requirements have not been met.',
    is_alert: false
  },
  N508: {
    code: 'N508',
    description:
      'Alert: This real-time claim adjudication response represents the member responsibility to the provider for services reported. The member will receive an Explanation of Benefits electronically or in the mail. Contact the insurer if there are any questions.',
    is_alert: true
  },
  N509: {
    code: 'N509',
    description:
      "Alert: A current inquiry shows the member's Consumer Spending Account contains sufficient funds to cover the member liability for this claim/service. Actual payment from the Consumer Spending Account will depend on the availability of funds and determination of eligible services at the time of payment processing.",
    is_alert: true
  },
  N510: {
    code: 'N510',
    description:
      "Alert: A current inquiry shows the member's Consumer Spending Account does not contain sufficient funds to cover the member's liability for this claim/service. Actual payment from the Consumer Spending Account will depend on the availability of funds and determination of eligible services at the time of payment processing.",
    is_alert: true
  },
  N511: {
    code: 'N511',
    description:
      'Alert: Information on the availability of Consumer Spending Account funds to cover the member liability on this claim/service is not available at this time.',
    is_alert: true
  },
  N512: {
    code: 'N512',
    description:
      'Alert: This is the initial remit of a non-NCPDP claim originally submitted real-time without change to the adjudication.',
    is_alert: true
  },
  N513: {
    code: 'N513',
    description:
      'Alert: This is the initial remit of a non-NCPDP claim originally submitted real-time with a change to the adjudication.',
    is_alert: true
  },
  N514: {
    code: 'N514',
    description:
      'Consult plan benefit documents/guidelines for information about restrictions for this service.',
    is_alert: false
  },
  N515: {
    code: 'N515',
    description:
      "Alert: Submit this claim to the patient's other insurer for potential payment of supplemental benefits. We did not forward the claim information. (use N387 instead)",
    is_alert: true
  },
  N516: {
    code: 'N516',
    description: 'Records indicate a mismatch between the submitted NPI and EIN.',
    is_alert: false
  },
  N517: {
    code: 'N517',
    description: 'Resubmit a new claim with the requested information.',
    is_alert: false
  },
  N518: {
    code: 'N518',
    description:
      'No separate payment for accessories when furnished for use with oxygen equipment.',
    is_alert: false
  },
  N519: {
    code: 'N519',
    description: 'Invalid combination of HCPCS modifiers.',
    is_alert: false
  },
  N520: {
    code: 'N520',
    description: 'Alert: Payment made from a Consumer Spending Account.',
    is_alert: true
  },
  N521: {
    code: 'N521',
    description:
      'Mismatch between the submitted provider information and the provider information stored in our system.',
    is_alert: false
  },
  N522: {
    code: 'N522',
    description: 'Duplicate of a claim processed, or to be processed, as a crossover claim.',
    is_alert: false
  },
  N523: {
    code: 'N523',
    description:
      'The limitation on outlier payments defined by this payer for this service period has been met. The outlier payment otherwise applicable to this claim has not been paid.',
    is_alert: false
  },
  N524: {
    code: 'N524',
    description: 'Based on policy this payment constitutes payment in full.',
    is_alert: false
  },
  N525: {
    code: 'N525',
    description:
      'These services are not covered when performed within the global period of another service.',
    is_alert: false
  },
  N526: {
    code: 'N526',
    description: 'Not qualified for recovery based on employer size.',
    is_alert: false
  },
  N527: {
    code: 'N527',
    description:
      'We processed this claim as the primary payer prior to receiving the recovery demand.',
    is_alert: false
  },
  N528: {
    code: 'N528',
    description: 'Patient is entitled to benefits for Institutional Services only.',
    is_alert: false
  },
  N529: {
    code: 'N529',
    description: 'Patient is entitled to benefits for Professional Services only.',
    is_alert: false
  },
  N530: {
    code: 'N530',
    description: 'Not Qualified for Recovery based on enrollment information.',
    is_alert: false
  },
  N531: {
    code: 'N531',
    description: 'Not qualified for recovery based on direct payment of premium.',
    is_alert: false
  },
  N532: {
    code: 'N532',
    description: 'Not qualified for recovery based on disability and working status.',
    is_alert: false
  },
  N533: {
    code: 'N533',
    description:
      'Services performed in an Indian Health Services facility under a self-insured tribal Group Health Plan.',
    is_alert: false
  },
  N534: {
    code: 'N534',
    description:
      'This is an individual policy, the employer does not participate in plan sponsorship.',
    is_alert: false
  },
  N535: {
    code: 'N535',
    description:
      'Payment is adjusted when procedure is performed in this place of service based on the submitted procedure code and place of service.',
    is_alert: false
  },
  N536: {
    code: 'N536',
    description:
      "We are not changing the prior payer's determination of patient responsibility, which you may collect, as this service is not covered by us.",
    is_alert: false
  },
  N537: {
    code: 'N537',
    description: 'We have examined claims history and no records of the services have been found.',
    is_alert: false
  },
  N538: {
    code: 'N538',
    description:
      'A facility is responsible for payment to outside providers who furnish these services/supplies/drugs to its patients/residents.',
    is_alert: false
  },
  N539: {
    code: 'N539',
    description:
      'Alert: We processed appeals/waiver requests on your behalf and that request has been denied.',
    is_alert: true
  },
  N540: {
    code: 'N540',
    description: 'Payment adjusted based on the interrupted stay policy.',
    is_alert: false
  },
  N541: {
    code: 'N541',
    description:
      'Mismatch between the submitted insurance type code and the information stored in our system.',
    is_alert: false
  },
  N542: {
    code: 'N542',
    description: 'Missing income verification.',
    is_alert: false
  },
  N543: {
    code: 'N543',
    description: 'Incomplete/invalid income verification.',
    is_alert: false
  },
  N544: {
    code: 'N544',
    description:
      'Alert: Although this was paid, you have billed with a referring/ordering provider that does not match our system record. Unless corrected this will not be paid in the future.',
    is_alert: true
  },
  N545: {
    code: 'N545',
    description:
      'Payment reduced based on status as an unsuccessful eprescriber per the Electronic Prescribing (eRx) Incentive Program.',
    is_alert: false
  },
  N546: {
    code: 'N546',
    description:
      'Payment represents a previous reduction based on the Electronic Prescribing (eRx) Incentive Program.',
    is_alert: false
  },
  N547: {
    code: 'N547',
    description: 'A refund request (Frequency Type Code 8) was processed previously.',
    is_alert: false
  },
  N548: {
    code: 'N548',
    description: "Alert: Patient's calendar year deductible has been met.",
    is_alert: true
  },
  N549: {
    code: 'N549',
    description: "Alert: Patient's calendar year out-of-pocket maximum has been met.",
    is_alert: true
  },
  N550: {
    code: 'N550',
    description:
      'Alert: You have not responded to requests to revalidate your provider/supplier enrollment information. Your failure to revalidate your enrollment information will result in a payment hold in the near future.',
    is_alert: true
  },
  N551: {
    code: 'N551',
    description:
      'Payment adjusted based on the Ambulatory Surgical Center (ASC) Quality Reporting Program.',
    is_alert: false
  },
  N552: {
    code: 'N552',
    description: 'Payment adjusted to reverse a previous withhold/bonus amount.',
    is_alert: false
  },
  N553: {
    code: 'N553',
    description:
      'Payment adjusted based on a Low Income Subsidy (LIS) retroactive coverage or status change.',
    is_alert: false
  },
  N554: {
    code: 'N554',
    description: 'Missing/Incomplete/Invalid Family Planning Indicator.',
    is_alert: false
  },
  N555: {
    code: 'N555',
    description: 'Missing medication list.',
    is_alert: false
  },
  N556: {
    code: 'N556',
    description: 'Incomplete/invalid medication list.',
    is_alert: false
  },
  N557: {
    code: 'N557',
    description:
      'This claim/service is not payable under our service area. The claim must be filed to the Payer/Plan in whose service area the specimen was collected.',
    is_alert: false
  },
  N558: {
    code: 'N558',
    description:
      'This claim/service is not payable under our service area. The claim must be filed to the Payer/Plan in whose service area the equipment was received.',
    is_alert: false
  },
  N559: {
    code: 'N559',
    description:
      'This claim/service is not payable under our service area. The claim must be filed to the Payer/Plan in whose service area the Ordering Physician is located.',
    is_alert: false
  },
  N560: {
    code: 'N560',
    description:
      'The pilot program requires an interim or final claim within 60 days of the Notice of Admission. A claim was not received.',
    is_alert: false
  },
  N561: {
    code: 'N561',
    description:
      'The bundled claim originally submitted for this episode of care includes related readmissions. You may resubmit the original claim to receive a corrected payment based on this readmission.',
    is_alert: false
  },
  N562: {
    code: 'N562',
    description:
      'The provider number of your incoming claim does not match the provider number on the processed Notice of Admission (NOA) for this bundled payment.',
    is_alert: false
  },
  N563: {
    code: 'N563',
    description:
      'Alert: Missing required provider/supplier issuance of advance patient notice of non-coverage. The patient is not liable for payment for this service.',
    is_alert: true
  },
  N564: {
    code: 'N564',
    description:
      'Patient did not meet the inclusion criteria for the demonstration project or pilot program.',
    is_alert: false
  },
  N565: {
    code: 'N565',
    description:
      'Alert: This non-payable reporting code requires a modifier. Future claims containing this non-payable reporting code must include an appropriate modifier for the claim to be processed.',
    is_alert: true
  },
  N566: {
    code: 'N566',
    description:
      'Alert: This procedure code requires functional reporting. Future claims containing this procedure code must include an applicable non-payable code and appropriate modifiers for the claim to be processed.',
    is_alert: true
  },
  N567: {
    code: 'N567',
    description: 'Not covered when considered preventative.',
    is_alert: false
  },
  N568: {
    code: 'N568',
    description:
      'Alert: Initial payment based on the Notice of Admission (NOA) under the Bundled Payment Model IV initiative.',
    is_alert: true
  },
  N569: {
    code: 'N569',
    description: 'Not covered when performed for the reported diagnosis.',
    is_alert: false
  },
  N570: {
    code: 'N570',
    description: 'Missing/incomplete/invalid credentialing data.',
    is_alert: false
  },
  N571: {
    code: 'N571',
    description: 'Alert: Payment will be issued quarterly by another payer/contractor.',
    is_alert: true
  },
  N572: {
    code: 'N572',
    description:
      'This procedure is not payable unless appropriate non-payable reporting codes and associated modifiers are submitted.',
    is_alert: false
  },
  N573: {
    code: 'N573',
    description:
      'Alert: You have been overpaid and must refund the overpayment. The refund will be requested separately by another payer/contractor.',
    is_alert: true
  },
  N574: {
    code: 'N574',
    description:
      'Our records indicate the ordering/referring provider is of a type/specialty that cannot order or refer. Please verify that the claim ordering/referring provider information is accurate or contact the ordering/referring provider.',
    is_alert: false
  },
  N575: {
    code: 'N575',
    description:
      'Mismatch between the submitted ordering/referring provider name and the ordering/referring provider name stored in our records.',
    is_alert: false
  },
  N576: {
    code: 'N576',
    description:
      'Services not related to the specific incident/claim/accident/loss being reported.',
    is_alert: false
  },
  N577: {
    code: 'N577',
    description: 'Personal Injury Protection (PIP) Coverage.',
    is_alert: false
  },
  N578: {
    code: 'N578',
    description: 'Coverages do not apply to this loss.',
    is_alert: false
  },
  N579: {
    code: 'N579',
    description: 'Medical Payments Coverage (MPC).',
    is_alert: false
  },
  N580: {
    code: 'N580',
    description: 'Determination based on the provisions of the insurance policy.',
    is_alert: false
  },
  N581: {
    code: 'N581',
    description: 'Investigation of coverage eligibility is pending.',
    is_alert: false
  },
  N582: {
    code: 'N582',
    description: "Benefits suspended pending the patient's cooperation.",
    is_alert: false
  },
  N583: {
    code: 'N583',
    description:
      'Patient was not an occupant of our insured vehicle and therefore, is not an eligible injured person.',
    is_alert: false
  },
  N584: {
    code: 'N584',
    description:
      "Not covered based on the insured's noncompliance with policy or statutory conditions.",
    is_alert: false
  },
  N585: {
    code: 'N585',
    description: 'Benefits are no longer available based on a final injury settlement.',
    is_alert: false
  },
  N586: {
    code: 'N586',
    description: 'The injured party does not qualify for benefits.',
    is_alert: false
  },
  N587: {
    code: 'N587',
    description: 'Policy benefits have been exhausted.',
    is_alert: false
  },
  N588: {
    code: 'N588',
    description: 'The patient has instructed that medical claims/bills are not to be paid.',
    is_alert: false
  },
  N589: {
    code: 'N589',
    description:
      'Coverage is excluded to any person injured as a result of operating a motor vehicle while in an intoxicated condition or while the ability to operate such a vehicle is impaired by the use of a drug.',
    is_alert: false
  },
  N590: {
    code: 'N590',
    description:
      'Missing independent medical exam detailing the cause of injuries sustained and medical necessity of services rendered.',
    is_alert: false
  },
  N591: {
    code: 'N591',
    description:
      'Payment based on an Independent Medical Examination (IME) or Utilization Review (UR).',
    is_alert: false
  },
  N592: {
    code: 'N592',
    description:
      'Adjusted because this is not the initial prescription or exceeds the amount allowed for the initial prescription.',
    is_alert: false
  },
  N593: {
    code: 'N593',
    description:
      'Not covered based on failure to attend a scheduled Independent Medical Exam (IME).',
    is_alert: false
  },
  N594: {
    code: 'N594',
    description:
      'Records reflect the injured party did not complete an Application for Benefits for this loss.',
    is_alert: false
  },
  N595: {
    code: 'N595',
    description:
      'Records reflect the injured party did not complete an Assignment of Benefits for this loss.',
    is_alert: false
  },
  N596: {
    code: 'N596',
    description:
      'Records reflect the injured party did not complete a Medical Authorization for this loss.',
    is_alert: false
  },
  N597: {
    code: 'N597',
    description:
      "Adjusted based on a medical/dental provider's apportionment of care between related injuries and other unrelated medical/dental conditions/injuries.",
    is_alert: false
  },
  N598: {
    code: 'N598',
    description: 'Health care policy coverage is primary.',
    is_alert: false
  },
  N599: {
    code: 'N599',
    description:
      'Our payment for this service is based upon a reasonable amount pursuant to both the terms and conditions of the policy of insurance under which the subject claim is being made as well as the Florida No-Fault Statute, which permits, when determining a reasonable charge for a service, an insurer to consider usual and customary charges and payments accepted by the provider, reimbursement levels in the community and various federal and state fee schedules applicable to automobile and other insurance coverages, and other information relevant to the reasonableness of the reimbursement for the service. The payment for this service is based upon 200% of the Participating Level of Medicare Part B fee schedule for the locale in which the services were rendered.',
    is_alert: false
  },
  N600: {
    code: 'N600',
    description:
      'Adjusted based on the applicable fee schedule for the region in which the service was rendered.',
    is_alert: false
  },
  N601: {
    code: 'N601',
    description:
      'In accordance with Hawaii Administrative Rules, Title 16, Chapter 23 Motor Vehicle Insurance Law payment is recommended based on Medicare Resource Based Relative Value Scale System applicable to Hawaii.',
    is_alert: false
  },
  N602: {
    code: 'N602',
    description: 'Adjusted based on the Redbook maximum allowance.',
    is_alert: false
  },
  N603: {
    code: 'N603',
    description:
      'This fee is calculated according to the New Jersey medical fee schedules for Automobile Personal Injury Protection and Motor Bus Medical Expense Insurance Coverage.',
    is_alert: false
  },
  N604: {
    code: 'N604',
    description:
      "In accordance with New York No-Fault Law, Regulation 68, this base fee was calculated according to the New York Workers' Compensation Board Schedule of Medical Fees, pursuant to Regulation 83 and / or Appendix 17-C of 11 NYCRR.",
    is_alert: false
  },
  N605: {
    code: 'N605',
    description:
      'This fee was calculated based upon New York All Patients Refined Diagnosis Related Groups (APR-DRG), pursuant to Regulation 68.',
    is_alert: false
  },
  N606: {
    code: 'N606',
    description:
      'The Oregon allowed amount for this procedure is based upon the Workers Compensation Fee Schedule (OAR 436-009). The allowed amount has been calculated in accordance with Section 4 of ORS 742.524.',
    is_alert: false
  },
  N607: {
    code: 'N607',
    description: 'Service provided for non-compensable condition(s).',
    is_alert: false
  },
  N608: {
    code: 'N608',
    description:
      'The fee schedule amount allowed is calculated at 110% of the Medicare Fee Schedule for this region, specialty and type of service. This fee is calculated in compliance with Act 6.',
    is_alert: false
  },
  N609: {
    code: 'N609',
    description:
      "80% of the provider's billed amount is being recommended for payment according to Act 6.",
    is_alert: false
  },
  N610: {
    code: 'N610',
    description: 'Alert: Payment based on an appropriate level of care.',
    is_alert: true
  },
  N611: {
    code: 'N611',
    description: 'Claim in litigation. Contact insurer for more information.',
    is_alert: false
  },
  N612: {
    code: 'N612',
    description:
      'Medical provider not authorized/certified to provide treatment to injured workers in this jurisdiction.',
    is_alert: false
  },
  N613: {
    code: 'N613',
    description:
      'Alert: Although this was paid, you have billed with an ordering provider that needs to update their enrollment record. Please verify that the ordering provider information you submitted on the claim is accurate and if it is, contact the ordering provider instructing them to update their enrollment record. Unless corrected, a claim with this ordering provider will not be paid in the future.',
    is_alert: true
  },
  N614: {
    code: 'N614',
    description:
      'Alert: Additional information is included in the 835 Healthcare Policy Identification Segment (loop 2110 Service Payment Information).',
    is_alert: true
  },
  N615: {
    code: 'N615',
    description:
      'Alert: This enrollee receiving advance payments of the premium tax credit is in the grace period of three consecutive months for non-payment of premium. Under 45 CFR 156.270, a Qualified Health Plan issuer must pay all appropriate claims for services rendered to the enrollee during the first month of the grace period and may pend claims for services rendered to the enrollee in the second and third months of the grace period.',
    is_alert: true
  },
  N616: {
    code: 'N616',
    description:
      'Alert: This enrollee is in the first month of the advance premium tax credit grace period.',
    is_alert: true
  },
  N617: {
    code: 'N617',
    description:
      'This enrollee is in the second or third month of the advance premium tax credit grace period.',
    is_alert: false
  },
  N618: {
    code: 'N618',
    description:
      'Alert: This claim will automatically be reprocessed if the enrollee pays their premiums.',
    is_alert: true
  },
  N619: {
    code: 'N619',
    description: 'Coverage terminated for non-payment of premium.',
    is_alert: false
  },
  N620: {
    code: 'N620',
    description: 'Alert: This procedure code is for quality reporting/informational purposes only.',
    is_alert: true
  },
  N621: {
    code: 'N621',
    description:
      'Charges for Jurisdiction required forms, reports, or chart notes are not payable.',
    is_alert: false
  },
  N622: {
    code: 'N622',
    description: 'Not covered based on the date of injury/accident.',
    is_alert: false
  },
  N623: {
    code: 'N623',
    description:
      'Not covered when deemed unscientific/unproven/outmoded/experimental/excessive/inappropriate.',
    is_alert: false
  },
  N624: {
    code: 'N624',
    description: "The associated Workers' Compensation claim has been withdrawn.",
    is_alert: false
  },
  N625: {
    code: 'N625',
    description: "Missing/Incomplete/Invalid Workers' Compensation Claim Number.",
    is_alert: false
  },
  N626: {
    code: 'N626',
    description:
      'New or established patient E/M codes are not payable with chiropractic care codes.',
    is_alert: false
  },
  N627: {
    code: 'N627',
    description: 'Service not payable per managed care contract.',
    is_alert: false
  },
  N628: {
    code: 'N628',
    description:
      'Out-patient follow up visits on the same date of service as a scheduled test or treatment is disallowed.',
    is_alert: false
  },
  N629: {
    code: 'N629',
    description: 'Reviews/documentation/notes/summaries/reports/charts not requested.',
    is_alert: false
  },
  N630: {
    code: 'N630',
    description: 'Referral not authorized by attending physician.',
    is_alert: false
  },
  N631: {
    code: 'N631',
    description:
      'Medical Fee Schedule does not list this code. An allowance was made for a comparable service.',
    is_alert: false
  },
  N632: {
    code: 'N632',
    description:
      'According to the Official Medical Fee Schedule this service has a relative value of zero and therefore no payment is due.',
    is_alert: false
  },
  N633: {
    code: 'N633',
    description: 'Additional anesthesia time units are not allowed.',
    is_alert: false
  },
  N634: {
    code: 'N634',
    description: 'The allowance is calculated based on anesthesia time units.',
    is_alert: false
  },
  N635: {
    code: 'N635',
    description: 'The Allowance is calculated based on the anesthesia base units plus time.',
    is_alert: false
  },
  N636: {
    code: 'N636',
    description: 'Adjusted because this is reimbursable only once per injury.',
    is_alert: false
  },
  N637: {
    code: 'N637',
    description:
      'Consultations are not allowed once treatment has been rendered by the same provider.',
    is_alert: false
  },
  N638: {
    code: 'N638',
    description: 'Reimbursement has been made according to the home health fee schedule.',
    is_alert: false
  },
  N639: {
    code: 'N639',
    description:
      'Reimbursement has been made according to the inpatient rehabilitation facilities fee schedule.',
    is_alert: false
  },
  N640: {
    code: 'N640',
    description: 'Exceeds number/frequency approved/allowed within time period.',
    is_alert: false
  },
  N641: {
    code: 'N641',
    description: 'Reimbursement has been based on the number of body areas rated.',
    is_alert: false
  },
  N642: {
    code: 'N642',
    description: 'Adjusted when billed as individual tests instead of as a panel.',
    is_alert: false
  },
  N643: {
    code: 'N643',
    description:
      'The services billed are considered Not Covered or Non-Covered (NC) in the applicable state fee schedule.',
    is_alert: false
  },
  N644: {
    code: 'N644',
    description: 'Reimbursement has been made according to the bilateral procedure rule.',
    is_alert: false
  },
  N645: {
    code: 'N645',
    description: 'Mark-up allowance.',
    is_alert: false
  },
  N646: {
    code: 'N646',
    description: 'Reimbursement has been adjusted based on the guidelines for an assistant.',
    is_alert: false
  },
  N647: {
    code: 'N647',
    description: 'Adjusted based on diagnosis-related group (DRG).',
    is_alert: false
  },
  N648: {
    code: 'N648',
    description: 'Adjusted based on Stop Loss.',
    is_alert: false
  },
  N649: {
    code: 'N649',
    description: 'Payment based on invoice.',
    is_alert: false
  },
  N650: {
    code: 'N650',
    description: 'This policy was not in effect for this date of loss. No coverage is available.',
    is_alert: false
  },
  N651: {
    code: 'N651',
    description:
      'No Personal Injury Protection/Medical Payments Coverage on the policy at the time of the loss.',
    is_alert: false
  },
  N652: {
    code: 'N652',
    description: 'The date of service is before the date of loss.',
    is_alert: false
  },
  N653: {
    code: 'N653',
    description: 'The date of injury does not match the reported date of loss.',
    is_alert: false
  },
  N654: {
    code: 'N654',
    description: 'Adjusted based on achievement of maximum medical improvement (MMI).',
    is_alert: false
  },
  N655: {
    code: 'N655',
    description: "Payment based on provider's geographic region.",
    is_alert: false
  },
  N656: {
    code: 'N656',
    description:
      'An interest payment is being made because benefits are being paid outside the statutory requirement.',
    is_alert: false
  },
  N657: {
    code: 'N657',
    description: 'This should be billed with the appropriate code for these services.',
    is_alert: false
  },
  N658: {
    code: 'N658',
    description: 'The billed service(s) are not considered medical expenses.',
    is_alert: false
  },
  N659: {
    code: 'N659',
    description: 'This item is exempt from sales tax.',
    is_alert: false
  },
  N660: {
    code: 'N660',
    description: 'Sales tax has been included in the reimbursement.',
    is_alert: false
  },
  N661: {
    code: 'N661',
    description:
      'Documentation does not support that the services rendered were medically necessary.',
    is_alert: false
  },
  N662: {
    code: 'N662',
    description: 'Alert: Consideration of payment will be made upon receipt of a final bill.',
    is_alert: true
  },
  N663: {
    code: 'N663',
    description: 'Adjusted based on an agreed amount.',
    is_alert: false
  },
  N664: {
    code: 'N664',
    description: 'Adjusted based on a legal settlement.',
    is_alert: false
  },
  N665: {
    code: 'N665',
    description: 'Services by an unlicensed provider are not reimbursable.',
    is_alert: false
  },
  N666: {
    code: 'N666',
    description:
      'Only one evaluation and management code at this service level is covered during the course of care.',
    is_alert: false
  },
  N667: {
    code: 'N667',
    description: 'Missing prescription.',
    is_alert: false
  },
  N668: {
    code: 'N668',
    description: 'Incomplete/invalid prescription.',
    is_alert: false
  },
  N669: {
    code: 'N669',
    description: 'Adjusted based on the Medicare fee schedule.',
    is_alert: false
  },
  N670: {
    code: 'N670',
    description:
      'This service code has been identified as the primary procedure code subject to the Medicare Multiple Procedure Payment Reduction (MPPR) rule.',
    is_alert: false
  },
  N671: {
    code: 'N671',
    description: 'Payment based on a jurisdiction cost-charge ratio.',
    is_alert: false
  },
  N672: {
    code: 'N672',
    description: 'Alert: Amount applied to Health Insurance Offset.',
    is_alert: true
  },
  N673: {
    code: 'N673',
    description:
      'Reimbursement has been calculated based on an outpatient per diem or an outpatient factor and/or fee schedule amount.',
    is_alert: false
  },
  N674: {
    code: 'N674',
    description: 'Not covered unless a pre-requisite procedure/service has been provided.',
    is_alert: false
  },
  N675: {
    code: 'N675',
    description: 'Additional information is required from the injured party.',
    is_alert: false
  },
  N676: {
    code: 'N676',
    description: 'Service does not qualify for payment under the Outpatient Facility Fee Schedule.',
    is_alert: false
  },
  N677: {
    code: 'N677',
    description: 'Alert: Films/Images will not be returned.',
    is_alert: true
  },
  N678: {
    code: 'N678',
    description: 'Missing post-operative images/visual field results.',
    is_alert: false
  },
  N679: {
    code: 'N679',
    description: 'Incomplete/Invalid post-operative images/visual field results.',
    is_alert: false
  },
  N680: {
    code: 'N680',
    description: 'Missing/Incomplete/Invalid date of previous dental extractions.',
    is_alert: false
  },
  N681: {
    code: 'N681',
    description: 'Missing/Incomplete/Invalid full arch series.',
    is_alert: false
  },
  N682: {
    code: 'N682',
    description: 'Missing/Incomplete/Invalid history of prior periodontal therapy/maintenance.',
    is_alert: false
  },
  N683: {
    code: 'N683',
    description: 'Missing/Incomplete/Invalid prior treatment documentation.',
    is_alert: false
  },
  N684: {
    code: 'N684',
    description: 'Payment denied as this is a specialty claim submitted as a general claim.',
    is_alert: false
  },
  N685: {
    code: 'N685',
    description: 'Missing/Incomplete/Invalid Prosthesis, Crown or Inlay Code.',
    is_alert: false
  },
  N686: {
    code: 'N686',
    description:
      'Missing/incomplete/Invalid questionnaire needed to complete payment determination.',
    is_alert: false
  },
  N687: {
    code: 'N687',
    description: 'Alert: This reversal is due to a retroactive disenrollment.',
    is_alert: true
  },
  N688: {
    code: 'N688',
    description: 'Alert: This reversal is due to a medical or utilization review decision.',
    is_alert: true
  },
  N689: {
    code: 'N689',
    description: 'Alert: This reversal is due to a retroactive rate change.',
    is_alert: true
  },
  N690: {
    code: 'N690',
    description: 'Alert: This reversal is due to a provider submitted appeal.',
    is_alert: true
  },
  N691: {
    code: 'N691',
    description: 'Alert: This reversal is due to a patient submitted appeal.',
    is_alert: true
  },
  N692: {
    code: 'N692',
    description: 'Alert: This reversal is due to an incorrect rate on the initial adjudication.',
    is_alert: true
  },
  N693: {
    code: 'N693',
    description: 'Alert: This reversal is due to a cancellation of the claim by the provider.',
    is_alert: true
  },
  N694: {
    code: 'N694',
    description:
      'Alert: This reversal is due to a resubmission/change to the claim by the provider.',
    is_alert: true
  },
  N695: {
    code: 'N695',
    description:
      'Alert: This reversal is due to incorrect patient financial responsibility information on the initial adjudication.',
    is_alert: true
  },
  N696: {
    code: 'N696',
    description:
      'Alert: This reversal is due to a Coordination of Benefits or Third Party Liability Recovery retroactive adjustment.',
    is_alert: true
  },
  N697: {
    code: 'N697',
    description:
      "Alert: This reversal is due to a payer's retroactive contract incentive program adjustment.",
    is_alert: true
  },
  N698: {
    code: 'N698',
    description:
      'Alert: This reversal is due to non-payment of the health insurance premiums (Health Insurance Exchange or other) by the end of the premium payment grace period, resulting in loss of coverage.',
    is_alert: true
  },
  N699: {
    code: 'N699',
    description:
      'Payment adjusted based on the Physician Quality Reporting System (PQRS) Incentive Program.',
    is_alert: false
  },
  N700: {
    code: 'N700',
    description: 'Payment adjusted based on the Electronic Health Records (EHR) Incentive Program.',
    is_alert: false
  },
  N701: {
    code: 'N701',
    description: 'Payment adjusted based on the Value-based Payment Modifier.',
    is_alert: false
  },
  N702: {
    code: 'N702',
    description:
      'Decision based on review of previously adjudicated claims or for claims in process for the same/similar type of services.',
    is_alert: false
  },
  N703: {
    code: 'N703',
    description:
      'This service is incompatible with previously adjudicated claims or claims in process.',
    is_alert: false
  },
  N704: {
    code: 'N704',
    description:
      'Alert: You may not appeal this decision but can resubmit this claim/service with corrected information if warranted.',
    is_alert: true
  },
  N705: {
    code: 'N705',
    description: 'Incomplete/invalid documentation.',
    is_alert: false
  },
  N706: {
    code: 'N706',
    description: 'Missing documentation.',
    is_alert: false
  },
  N707: {
    code: 'N707',
    description: 'Incomplete/invalid orders.',
    is_alert: false
  },
  N708: {
    code: 'N708',
    description: 'Missing orders.',
    is_alert: false
  },
  N709: {
    code: 'N709',
    description: 'Incomplete/invalid notes.',
    is_alert: false
  },
  N710: {
    code: 'N710',
    description: 'Missing notes.',
    is_alert: false
  },
  N711: {
    code: 'N711',
    description: 'Incomplete/invalid summary.',
    is_alert: false
  },
  N712: {
    code: 'N712',
    description: 'Missing summary.',
    is_alert: false
  },
  N713: {
    code: 'N713',
    description: 'Incomplete/invalid report.',
    is_alert: false
  },
  N714: {
    code: 'N714',
    description: 'Missing report.',
    is_alert: false
  },
  N715: {
    code: 'N715',
    description: 'Incomplete/invalid chart.',
    is_alert: false
  },
  N716: {
    code: 'N716',
    description: 'Missing chart.',
    is_alert: false
  },
  N717: {
    code: 'N717',
    description: 'Incomplete/Invalid documentation of face-to-face examination.',
    is_alert: false
  },
  N718: {
    code: 'N718',
    description: 'Missing documentation of face-to-face examination.',
    is_alert: false
  },
  N719: {
    code: 'N719',
    description: 'Penalty applied based on plan requirements not being met.',
    is_alert: false
  },
  N720: {
    code: 'N720',
    description:
      "Alert: The patient overpaid you. You may need to issue the patient a refund for the difference between the patient's payment and the amount shown as patient responsibility on this notice.",
    is_alert: true
  },
  N721: {
    code: 'N721',
    description: 'This service is only covered when performed as part of a clinical trial.',
    is_alert: false
  },
  N722: {
    code: 'N722',
    description:
      "Patient must use Workers' Compensation Set-Aside (WCSA) funds to pay for the medical service or item.",
    is_alert: false
  },
  N723: {
    code: 'N723',
    description:
      'Patient must use Liability set-aside (LSA) funds to pay for the medical service or item.',
    is_alert: false
  },
  N724: {
    code: 'N724',
    description:
      'Patient must use No-Fault set-aside (NFSA) funds to pay for the medical service or item.',
    is_alert: false
  },
  N725: {
    code: 'N725',
    description:
      'A liability insurer has reported having ongoing responsibility for medical services (ORM) for this diagnosis.',
    is_alert: false
  },
  N726: {
    code: 'N726',
    description: 'A conditional payment is not allowed.',
    is_alert: false
  },
  N727: {
    code: 'N727',
    description:
      'A no-fault insurer has reported having ongoing responsibility for medical services (ORM) for this diagnosis.',
    is_alert: false
  },
  N728: {
    code: 'N728',
    description:
      "A workers' compensation insurer has reported having ongoing responsibility for medical services (ORM) for this diagnosis.",
    is_alert: false
  },
  N729: {
    code: 'N729',
    description: 'Missing patient medical/dental record for this service.',
    is_alert: false
  },
  N730: {
    code: 'N730',
    description: 'Incomplete/invalid patient medical/dental record for this service.',
    is_alert: false
  },
  N731: {
    code: 'N731',
    description: 'Incomplete/Invalid mental health assessment.',
    is_alert: false
  },
  N732: {
    code: 'N732',
    description: 'Services performed at an unlicensed facility are not reimbursable.',
    is_alert: false
  },
  N733: {
    code: 'N733',
    description: 'Regulatory surcharges are paid directly to the state.',
    is_alert: false
  },
  N734: {
    code: 'N734',
    description:
      'The patient is eligible for these medical services only when unable to work or perform normal activities due to an illness or injury.',
    is_alert: false
  },
  N735: {
    code: 'N735',
    description:
      'Adjustment without review of medical/dental record because the requested records were not received or were not received timely.',
    is_alert: false
  },
  N736: {
    code: 'N736',
    description: 'Incomplete/invalid Sleep Study Report.',
    is_alert: false
  },
  N737: {
    code: 'N737',
    description: 'Missing Sleep Study Report.',
    is_alert: false
  },
  N738: {
    code: 'N738',
    description: 'Incomplete/invalid Vein Study Report.',
    is_alert: false
  },
  N739: {
    code: 'N739',
    description: 'Missing Vein Study Report.',
    is_alert: false
  },
  N740: {
    code: 'N740',
    description:
      "The member's Consumer Spending Account does not contain sufficient funds to cover the member's liability for this claim/service.",
    is_alert: false
  },
  N741: {
    code: 'N741',
    description: 'This is a site neutral payment.',
    is_alert: false
  },
  N742: {
    code: 'N742',
    description:
      'Alert: This claim was processed based on one or more ICD-9 codes. The transition to ICD-10 is required by October 1, 2015, for health care providers, health plans, and clearinghouses. More information can be found at http://www.cms.gov/Medicare/Coding/ICD10/ProviderResources.html',
    is_alert: true
  },
  N743: {
    code: 'N743',
    description: 'Adjusted because the services may be related to an employment accident.',
    is_alert: false
  },
  N744: {
    code: 'N744',
    description: 'Adjusted because the services may be related to an auto/other accident.',
    is_alert: false
  },
  N745: {
    code: 'N745',
    description: 'Missing Ambulance Report.',
    is_alert: false
  },
  N746: {
    code: 'N746',
    description: 'Incomplete/invalid Ambulance Report.',
    is_alert: false
  },
  N747: {
    code: 'N747',
    description:
      'This is a misdirected claim/service. Submit the claim to the payer/plan where the patient resides.',
    is_alert: false
  },
  N748: {
    code: 'N748',
    description: 'Adjusted because the related hospital charges have not been received.',
    is_alert: false
  },
  N749: {
    code: 'N749',
    description: 'Missing Blood Gas Report.',
    is_alert: false
  },
  N750: {
    code: 'N750',
    description: 'Incomplete/invalid Blood Gas Report.',
    is_alert: false
  },
  N751: {
    code: 'N751',
    description: 'Adjusted because the patient is covered under a Medicare Part D plan.',
    is_alert: false
  },
  N752: {
    code: 'N752',
    description: 'Missing/incomplete/invalid HIPPS Treatment Authorization Code (TAC).',
    is_alert: false
  },
  N753: {
    code: 'N753',
    description: 'Missing/incomplete/invalid Attachment Control Number.',
    is_alert: false
  },
  N754: {
    code: 'N754',
    description:
      'Missing/incomplete/invalid Referring Provider or Other Source Qualifier on the 1500 Claim Form.',
    is_alert: false
  },
  N755: {
    code: 'N755',
    description: 'Missing/incomplete/invalid ICD Indicator.',
    is_alert: false
  },
  N756: {
    code: 'N756',
    description: 'Missing/incomplete/invalid point of drop-off address.',
    is_alert: false
  },
  N757: {
    code: 'N757',
    description: 'Adjusted based on the Federal Indian Fees schedule (MLR).',
    is_alert: false
  },
  N758: {
    code: 'N758',
    description: 'Adjusted based on the prior authorization decision.',
    is_alert: false
  },
  N759: {
    code: 'N759',
    description:
      'Payment adjusted based on the National Electrical Manufacturers Association (NEMA) Standard XR-29-2013.',
    is_alert: false
  },
  N760: {
    code: 'N760',
    description: 'This facility is not authorized to receive payment for the service(s).',
    is_alert: false
  },
  N761: {
    code: 'N761',
    description: 'This provider is not authorized to receive payment for the service(s).',
    is_alert: false
  },
  N762: {
    code: 'N762',
    description: 'This facility is not certified for Tomosynthesis (3-D) mammography.',
    is_alert: false
  },
  N763: {
    code: 'N763',
    description:
      'The demonstration code is not appropriate for this claim; resubmit without a demonstration code.',
    is_alert: false
  },
  N764: {
    code: 'N764',
    description: 'Missing/incomplete/invalid Hematocrit (HCT) value.',
    is_alert: false
  },
  N765: {
    code: 'N765',
    description: 'This payer does not cover coinsurance assessed by a previous payer.',
    is_alert: false
  },
  N766: {
    code: 'N766',
    description: 'This payer does not cover co-payment assessed by a previous payer.',
    is_alert: false
  },
  N767: {
    code: 'N767',
    description:
      "The Medicaid state requires provider to be enrolled in the member's Medicaid state program prior to any claim benefits being processed.",
    is_alert: false
  },
  N768: {
    code: 'N768',
    description: 'Incomplete/invalid initial evaluation report.',
    is_alert: false
  },
  N769: {
    code: 'N769',
    description: 'A lateral diagnosis is required.',
    is_alert: false
  },
  N770: {
    code: 'N770',
    description:
      'The adjustment request received from the provider has been processed. Your original claim has been adjusted based on the information received.',
    is_alert: false
  },
  N771: {
    code: 'N771',
    description: 'Alert: Under Federal law you cannot charge more than the limiting charge amount.',
    is_alert: true
  },
  N772: {
    code: 'N772',
    description: 'Alert: Rebill urgent/emergent and ancillary services separately.',
    is_alert: true
  },
  N773: {
    code: 'N773',
    description: 'Drug supplied not obtained from specialty vendor.',
    is_alert: false
  },
  N774: {
    code: 'N774',
    description:
      'Alert: Refer to your Third Party Processor Agreement for specific information on fees associated with this payment type.',
    is_alert: true
  },
  N775: {
    code: 'N775',
    description: 'Payment adjusted based on x-ray radiograph on film.',
    is_alert: false
  },
  N776: {
    code: 'N776',
    description: 'This service is not a covered Telehealth service.',
    is_alert: false
  },
  N777: {
    code: 'N777',
    description: 'Missing Assignment of Benefits Indicator.',
    is_alert: false
  },
  N778: {
    code: 'N778',
    description: 'Missing Primary Care Physician Information.',
    is_alert: false
  },
  N779: {
    code: 'N779',
    description:
      'Replacement/Void claims cannot be submitted until the original claim has finalized. Please resubmit once payment or denial is received.',
    is_alert: false
  },
  N780: {
    code: 'N780',
    description: 'Missing/incomplete/invalid end therapy date.',
    is_alert: false
  },
  N781: {
    code: 'N781',
    description:
      'Alert: Patient is a Medicaid/ Qualified Medicare Beneficiary. Review your records for any wrongfully collected deductible. This amount may be billed to a subsequent payer.',
    is_alert: true
  },
  N782: {
    code: 'N782',
    description:
      'Alert: Patient is a Medicaid/ Qualified Medicare Beneficiary. Review your records for any wrongfully collected coinsurance. This amount may be billed to a subsequent payer.',
    is_alert: true
  },
  N783: {
    code: 'N783',
    description:
      'Alert: Patient is a Medicaid/ Qualified Medicare Beneficiary. Review your records for any wrongfully collected copayment. This amount may be billed to a subsequent payer.',
    is_alert: true
  },
  N784: {
    code: 'N784',
    description: 'Missing comprehensive procedure code.',
    is_alert: false
  },
  N785: {
    code: 'N785',
    description: 'Missing current radiology film/images.',
    is_alert: false
  },
  N786: {
    code: 'N786',
    description:
      'Benefit limitation for the orthodontic active and/or retention phase of treatment.',
    is_alert: false
  },
  N787: {
    code: 'N787',
    description:
      'Alert: Under 42 CFR 410.43, an eligible Partial Hospitalization Program (PHP) patient/beneficiary requires a minimum of 20 hours of PHP services per week, as evidenced in the plan of care. PHP services must be furnished in accordance with the plan of care.',
    is_alert: true
  },
  N788: {
    code: 'N788',
    description:
      'Alert: The third-party administrator/review organization did not receive the required information.',
    is_alert: true
  },
  N789: {
    code: 'N789',
    description: 'Clinical Trial is not a covered benefit.',
    is_alert: false
  },
  N790: {
    code: 'N790',
    description: 'Provider/supplier not accredited for product/service.',
    is_alert: false
  },
  N791: {
    code: 'N791',
    description: 'Missing history & physical report.',
    is_alert: false
  },
  N792: {
    code: 'N792',
    description: 'Incomplete/invalid history & physical report.',
    is_alert: false
  },
  N793: {
    code: 'N793',
    description:
      'Alert: Starting January 1, 2020, Medicare will ONLY accept claims submitted with the Medicare Beneficiary Identifier (MBI). Medicare will reject any claims submitted with the Health Insurance Claim Number (HICN) with a few exceptions. Please see www.cms.gov/Medicare/New-Medicare-Card/index.html for more information.',
    is_alert: true
  },
  N794: {
    code: 'N794',
    description: 'Payment adjusted based on type of technology used.',
    is_alert: false
  },
  N795: {
    code: 'N795',
    description: 'Item must be resubmitted as a purchase.',
    is_alert: false
  },
  N796: {
    code: 'N796',
    description: 'Missing/incomplete/invalid Hemoglobin (Hb or Hgb) value.',
    is_alert: false
  },
  N797: {
    code: 'N797',
    description: 'Missing/incomplete/invalid date qualifier.',
    is_alert: false
  },
  N798: {
    code: 'N798',
    description: 'Submit a void request for the original claim and resubmit a new claim.',
    is_alert: false
  },
  N799: {
    code: 'N799',
    description: 'Submitted identifier must be an individual identifier, not group identifier.',
    is_alert: false
  },
  N800: {
    code: 'N800',
    description: 'Only one service date is allowed per claim.',
    is_alert: false
  },
  N801: {
    code: 'N801',
    description:
      'Services performed in a Medicare participating or CAH facility under a self-insured tribal Group Health Plan, in accordance with Federal Regulation 42 CFR 136.',
    is_alert: false
  },
  N802: {
    code: 'N802',
    description:
      'This claim/service is not payable under our service area. The claim must be filed to the Payer/Plan in whose service area the Rendering Physician is located.',
    is_alert: false
  },
  N803: {
    code: 'N803',
    description:
      'Submission of the claim for the service rendered is the responsibility of the Contracted Medical Group or Hospital.',
    is_alert: false
  },
  N804: {
    code: 'N804',
    description: 'Alert: The claim/service was processed through the Outpatient Code Editor (OCE).',
    is_alert: true
  },
  N805: {
    code: 'N805',
    description: 'Alert: The claim/service was processed through the Correct Code Editor (CCE).',
    is_alert: true
  },
  N806: {
    code: 'N806',
    description: 'Payment is included in the Global transplant allowance.',
    is_alert: false
  },
  N807: {
    code: 'N807',
    description: 'Payment adjustment based on the Merit-based Incentive Payment System (MIPS).',
    is_alert: false
  },
  N808: {
    code: 'N808',
    description: 'Not covered for this provider type / provider specialty.',
    is_alert: false
  },
  N809: {
    code: 'N809',
    description:
      'Alert: The fee schedule amount for this service was adjusted based on prior competitive bidding rates. For more information, contact your local contractor.',
    is_alert: true
  },
  N810: {
    code: 'N810',
    description:
      'Alert: Due to federal, state or local disaster declaration, this claim has been processed at the in-network level of benefit. At the conclusion or expiration of the disaster declaration, network payment rules will be reinstated.',
    is_alert: true
  },
  N811: {
    code: 'N811',
    description: 'Missing Federal Sequestration Reduction from Prior Payer.',
    is_alert: false
  },
  N812: {
    code: 'N812',
    description:
      'The start service date through end service date cannot span greater than 18 months.',
    is_alert: false
  },
  N815: {
    code: 'N815',
    description: 'Missing/Incomplete/Invalid NDC Unit Count',
    is_alert: false
  },
  N816: {
    code: 'N816',
    description: 'Missing/Incomplete/Invalid NDC Unit of Measure',
    is_alert: false
  },
  N817: {
    code: 'N817',
    description:
      'Alert: Applicable laboratories are required to collect and report private payor data and report that data to CMS between January 1, 2020 - March 31, 2020.',
    is_alert: true
  },
  N818: {
    code: 'N818',
    description: 'Claims Dates of Service do not match Electronic Visit Verification System.',
    is_alert: false
  },
  N819: {
    code: 'N819',
    description: 'Patient not enrolled in Electronic Visit Verification System.',
    is_alert: false
  },
  N820: {
    code: 'N820',
    description: 'Electronic Visit Verification System units do not meet requirements of visit.',
    is_alert: false
  },
  N821: {
    code: 'N821',
    description: 'Electronic Visit Verification System visit not found.',
    is_alert: false
  },
  N822: {
    code: 'N822',
    description: 'Missing procedure modifier(s).',
    is_alert: false
  },
  N823: {
    code: 'N823',
    description: 'Incomplete/Invalid procedure modifier(s).',
    is_alert: false
  },
  N824: {
    code: 'N824',
    description: 'Electronic Visit Verification (EVV) data must be submitted through EVV Vendor.',
    is_alert: false
  },
  N825: {
    code: 'N825',
    description: 'Early intervention guidelines were not met.',
    is_alert: false
  },
  N826: {
    code: 'N826',
    description:
      'Patient did not meet the inclusion criteria for the Medicare Shared Savings Program.',
    is_alert: false
  },
  N827: {
    code: 'N827',
    description: 'Missing/Incomplete/Invalid Federal Information Processing Standard (FIPS) Code.',
    is_alert: false
  },
  N828: {
    code: 'N828',
    description: 'Alert: Payment is suppressed due to a contracted funding.',
    is_alert: true
  },
  N829: {
    code: 'N829',
    description: 'Missing/incomplete/invalid Diagnostics Exchange Z-Code Identifier.',
    is_alert: false
  },
  N830: {
    code: 'N830',
    description:
      'Alert: The charge[s] for this service was processed in accordance with Federal/ State, Balance Billing/ No Surprise Billing regulations. As such, any amount identified with OA, CO, or PI cannot be collected from the member and may be considered provider liability or be billable to a subsequent payer. Any amount the provider collected over the identified PR amount must be refunded to the patient within applicable Federal/State timeframes. Payment amounts are eligible for dispute pursuant to any Federal/State documented appeal/grievance process(es).',
    is_alert: true
  },
  N831: {
    code: 'N831',
    description:
      'You have not responded to requests to revalidate your provider/supplier enrollment information.',
    is_alert: false
  },
  N832: {
    code: 'N832',
    description: 'Duplicate occurrence code/occurrence span code.',
    is_alert: false
  },
  N833: {
    code: 'N833',
    description: 'Patient share of cost waived.',
    is_alert: false
  },
  N834: {
    code: 'N834',
    description: 'Jurisdiction exempt from sales and health tax charges.',
    is_alert: false
  },
  N835: {
    code: 'N835',
    description:
      "Unrelated Service/procedure/treatment is reduced. The balance of this charge is the patient's responsibility.",
    is_alert: false
  },
  N836: {
    code: 'N836',
    description: 'Provider W9 or Payee Registration not on file.',
    is_alert: false
  },
  N837: {
    code: 'N837',
    description: 'Alert: Missing modifier was added.',
    is_alert: true
  },
  N838: {
    code: 'N838',
    description:
      'Alert: Service/procedure postponed due to a federal, state, or local mandate/disaster declaration. Any amounts applied to deductible or member liability will be applied to the prior plan year from which the procedure was cancelled.',
    is_alert: true
  },
  N839: {
    code: 'N839',
    description:
      'The procedure code was added/changed because the level of service exceeds the compensable condition(s).',
    is_alert: false
  },
  N840: {
    code: 'N840',
    description: "Worker's compensation claim filed with a different state.",
    is_alert: false
  },
  N841: {
    code: 'N841',
    description: 'Alert: North Dakota Administrative Rule 92-01-02-50.3.',
    is_alert: true
  },
  N842: {
    code: 'N842',
    description: 'Alert: Patient cannot be billed for charges.',
    is_alert: true
  },
  N843: {
    code: 'N843',
    description: 'Missing/incomplete/invalid Core-Based Statistical Area (CBSA) code.',
    is_alert: false
  },
  N844: {
    code: 'N844',
    description:
      'This claim, or a portion of this claim, was processed in accordance with the Nebraska Legislative LB997 July 24, 2020 - Out of Network Emergency Medical Care Act.',
    is_alert: false
  },
  N845: {
    code: 'N845',
    description:
      'Alert: Nebraska Legislative LB997 July 24, 2020 - Out of Network Emergency Medical Care Act.',
    is_alert: true
  },
  N846: {
    code: 'N846',
    description: 'National Drug Code (NDC) supplied does not correspond to the HCPCs/CPT billed.',
    is_alert: false
  },
  N847: {
    code: 'N847',
    description: 'National Drug Code (NDC) billed is obsolete.',
    is_alert: false
  },
  N848: {
    code: 'N848',
    description: 'National Drug Code (NDC) billed cannot be associated with a product.',
    is_alert: false
  },
  N849: {
    code: 'N849',
    description: 'Missing Tooth Clause: Tooth missing prior to the member effective date.',
    is_alert: false
  },
  N850: {
    code: 'N850',
    description:
      'Missing/incomplete/invalid narrative explaining/describing this service/treatment.',
    is_alert: false
  },
  N851: {
    code: 'N851',
    description: 'Payment reduced because services were furnished by a therapy assistant.',
    is_alert: false
  },
  N852: {
    code: 'N852',
    description: 'The pay-to and rendering provider tax identification numbers (TINs) do not match',
    is_alert: false
  },
  N853: {
    code: 'N853',
    description: 'The number of modalities performed per session exceeds our acceptable maximum.',
    is_alert: false
  },
  N854: {
    code: 'N854',
    description:
      'Alert: If you have primary other health insurance (OHI) coverage that has denied services, you must exhaust all appeal levels with your primary OHI before we can consider your claim for reimbursement.',
    is_alert: true
  },
  N855: {
    code: 'N855',
    description:
      'This coverage is subject to the exclusive jurisdiction of ERISA (1974), U.S.C. SEC 1001.',
    is_alert: false
  },
  N856: {
    code: 'N856',
    description:
      'This coverage is not subject to the exclusive jurisdiction of ERISA (1974), U.S.C. SEC 1001.',
    is_alert: false
  },
  N857: {
    code: 'N857',
    description:
      'This claim has been adjusted/reversed. Refund any collected copayment to the member.',
    is_alert: false
  },
  N858: {
    code: 'N858',
    description:
      "Alert: State regulations relating to an Out of Network Medical Emergency Care Act were applied to the processing of this claim. Payment amounts are eligible for dispute following the state's documented appeal/ grievance/ arbitration process.",
    is_alert: true
  },
  N859: {
    code: 'N859',
    description:
      'Alert: The Federal No Surprise Billing Act was applied to the processing of this claim. Payment amounts are eligible for dispute pursuant to any Federal documented appeal/ grievance/ dispute resolution process(es).',
    is_alert: true
  },
  N860: {
    code: 'N860',
    description:
      'Alert: The Federal No Surprise Billing Act Qualified Payment Amount (QPA) was used to calculate the member cost share(s).',
    is_alert: true
  },
  N861: {
    code: 'N861',
    description:
      'Alert: Mismatch between the submitted Patient Liability/Share of Cost and the amount on record for this recipient.',
    is_alert: true
  },
  N862: {
    code: 'N862',
    description:
      'Alert: Member cost share is in compliance with the No Surprises Act, and is calculated using the lesser of the QPA or billed charge.',
    is_alert: true
  },
  N863: {
    code: 'N863',
    description:
      'Alert: This claim is subject to the No Surprises Act (NSA). The amount paid is the final out-of-network rate and was calculated based on an All Payer Model Agreement, in accordance with the NSA.',
    is_alert: true
  },
  N864: {
    code: 'N864',
    description:
      'Alert: This claim is subject to the No Surprises Act provisions that apply to emergency services.',
    is_alert: true
  },
  N865: {
    code: 'N865',
    description:
      'Alert: This claim is subject to the No Surprises Act provisions that apply to nonemergency services furnished by nonparticipating providers during a patient visit to a participating facility.',
    is_alert: true
  },
  N866: {
    code: 'N866',
    description:
      'Alert: This claim is subject to the No Surprises Act provisions that apply to services furnished by nonparticipating providers of air ambulance services.',
    is_alert: true
  },
  N867: {
    code: 'N867',
    description:
      'Alert: Cost sharing was calculated based on a specified state law, in accordance with the No Surprises Act.',
    is_alert: true
  },
  N868: {
    code: 'N868',
    description:
      'Alert: Cost sharing was calculated based on an All-Payer Model Agreement, in accordance with the No Surprises Act.',
    is_alert: true
  },
  N869: {
    code: 'N869',
    description:
      'Alert: Cost sharing was calculated based on the qualifying payment amount, in accordance with the No Surprises Act.',
    is_alert: true
  },
  N870: {
    code: 'N870',
    description:
      'Alert: In accordance with the No Surprises Act, cost sharing was based on the billed amount because the billed amount was lower than the qualifying payment amount.',
    is_alert: true
  },
  N871: {
    code: 'N871',
    description:
      'Alert: This initial payment was calculated based on a specified state law, in accordance with the No Surprises Act.',
    is_alert: true
  },
  N872: {
    code: 'N872',
    description:
      'Alert: This final payment was calculated based on a specified state law, in accordance with the No Surprises Act.',
    is_alert: true
  },
  N873: {
    code: 'N873',
    description:
      'Alert: This final payment was calculated based on an All-Payer Model Agreement, in accordance with the No Surprises Act.',
    is_alert: true
  },
  N874: {
    code: 'N874',
    description:
      'Alert: This final payment was determined through open negotiation, in accordance with the No Surprises Act.',
    is_alert: true
  },
  N875: {
    code: 'N875',
    description:
      'Alert: This final payment equals the amount selected as the out-of-network rate by a Federal Independent Dispute Resolution Entity, in accordance with the No Surprises Act.',
    is_alert: true
  },
  N876: {
    code: 'N876',
    description:
      'Alert: This item or service is covered under the plan. This is a notice of denial of payment provided in accordance with the No Surprises Act. The provider or facility may initiate open negotiation if they desire to negotiate a higher out-of-network rate than the amount paid by the patient in cost sharing.',
    is_alert: true
  },
  N877: {
    code: 'N877',
    description:
      'Alert: This initial payment is provided in accordance with the No Surprises Act. The provider or facility may initiate open negotiation if they desire to negotiate a higher out-of-network rate.',
    is_alert: true
  },
  N878: {
    code: 'N878',
    description:
      'Alert: The provider or facility specified that notice was provided and consent to balance bill obtained, but notice and consent was not provided and obtained in a manner consistent with applicable Federal law. Thus, cost sharing and the total amount paid have been calculated based on the requirements under the No Surprises Act, and balance billing is prohibited.',
    is_alert: true
  },
  N879: {
    code: 'N879',
    description:
      'Alert: The notice and consent to balance bill, and to be charged out-of-network cost sharing, that was obtained from the patient with regard to the billed services, is not permitted for these services. Thus, cost sharing and the total amount paid have been calculated based on the requirements under the No Surprises Act, and balance billing is prohibited.',
    is_alert: true
  },
  N880: {
    code: 'N880',
    description:
      'Original claim closed due to changes in submitted data. Adjustment claim will be processed under a new claim number.',
    is_alert: false
  },
  N881: {
    code: 'N881',
    description:
      'Client Obligation, patient responsibility for Home & Community Based Services (HCBS)',
    is_alert: false
  },
  N882: {
    code: 'N882',
    description:
      "Alert: The out-of-network payment and cost sharing amounts were based on the plan's allowance because the provider or facility obtained the patient's consent to waive the balance billing protections under the No Surprises Act.",
    is_alert: true
  },
  N883: {
    code: 'N883',
    description: 'Alert: Processed according to state law',
    is_alert: true
  },
  N884: {
    code: 'N884',
    description:
      'Alert: The No Surprises Act may apply to this claim. Please contact payer for instructions on how to submit information regarding whether or not the item or service was furnished during a patient visit to a participating facility.',
    is_alert: true
  },
  N885: {
    code: 'N885',
    description:
      'Alert: This claim was not processed in accordance with the No Surprises Act cost-sharing or out-of-network payment requirements. The payer disagrees with your determination that those requirements apply. You may contact the payer to find out why it disagrees. You may appeal this adverse determination on behalf of the patient through the payer\u2019s internal appeals and external review processes.',
    is_alert: true
  }
};

export interface RemitFinancialInfoPayload {
  id?: number | null;
  remit_id?: number | null;
  check_amount?: string | null; // Required
  check_amount_in_cents?: number | null; // maximum: 2147483647, minimum: -2147483648
  check_or_eft_trace_number?: string | null; // maxLength: 100
  paid_at?: string | null; // Required, format: date-time
  transaction_set_control_no?: string | null; // maxLength: 100
  transaction_set_identifier_code?: string | null; // maxLength: 50
  payment_method?: string | null; // maxLength: 50
  routing_number?: string | null; // maxLength: 50
  payer_identifier?: string | null; // maxLength: 100
  payer_name?: string | null; // maxLength: 100
  payee_id?: string | null; // maxLength: 100
  payee_name?: string | null; // maxLength: 100
  originating_company_co_supplemental_code?: string | null; // maxLength: 100
}
export interface RemitClaimPayload {
  id?: number;
  remit?: number;
  remit_id?: number | null;
  financial_info_id?: number | null;
  patient_control_number?: string;
  person_id?: string;
  healthie_cms_1500?: string;
  payer_classification?: 'primary' | 'secondary' | 'tertiary' | 'unspecified' | null;
  facility_type_code?: string | null;
  claim_frequency_code?: string | null;
  status?: 1 | 2 | 3 | 4 | 19 | 20 | 21 | 22 | null;
  charge_amount?: string | null;
  paid_amount?: string | null;
  patient_responsibility_amount?: string | null;
  charge_amount_in_cents?: number | null;
  paid_amount_in_cents?: number | null;
  patient_responsibility_amount_in_cents?: number | null;
  claim_filing_indicator_code?: string | null;
  payer_claim_control_number?: string | null;
  drg_code?: string | null;
  claim_received_on?: string | null;
  claim_statement_period_start_on?: string | null;
  claim_statement_period_end_on?: string | null;
  claim_coverage_expiration_on?: string | null;
  claim_contract_code?: string | null;
  claim_coverage_amount?: string | null;
  claim_coverage_amount_in_cents?: number | null;
  corrected_insured_last_name?: string | null;
  corrected_insured_first_name?: string | null;
  corrected_insured_middle_name?: string | null;
  corrected_insured_prefix?: string | null;
  corrected_insured_suffix?: string | null;
  corrected_insured_id_qualifier?: string | null;
  corrected_insured_id?: string | null;
  provider_entity_type_qualifier?: string | null;
  provider_id_qualifier?: string | null;
  provider_id?: string | null;
  provider_name?: string | null;
  provider_first_name?: string | null;
  provider_middle_name?: string | null;
  provider_suffix?: string | null;
  provider_prefix?: string | null;
  _delete?: boolean;
}

export interface RemitServiceLinePayload {
  id: number | null; // Read-only
  remit_claim: number; // Required
  start_on: string; // Required, format: date
  end_on: string; // Required, format: date
  code: string; // Required, maxLength: 5, minLength: 1
  modifier: string | null | undefined; // Required, maxLength: 20, minLength: 1
  qualifier: string | null | undefined; // Required, maxLength: 2, minLength: 1
  allowed_units: number | null | undefined; // Required, maximum: 2147483647, minimum: -2147483648
  billed_units: number | null | undefined; // Required, maximum: 2147483647, minimum: -2147483648
  charge_amount_in_cents?: number | null | undefined; // maximum: 2147483647, minimum: -2147483648
  charge_amount?: string | null | undefined; // maxLength: 10
  paid_amount?: string | null | undefined; // maxLength: 10
  paid_amount_in_cents?: number | null | undefined; // maximum: 2147483647, minimum: -2147483648
  revenue_code?: string | null | undefined; // maxLength: 10
}
export interface ClaimRow {
  id: number;
  cms1500_cpt_code_id: string;
  remit_service_line_id: string;
  link_attributes: LinkAttributes;
  link_score: number;
  attempts: number;
  created_at: Date;
  updated_at: Date;
  deleted_at: null;
}

export interface LinkAttributes {
  code: number;
  start_on: number;
  charge_amount: number;
  similar_start_on: number;
  similar_charge_amount: number;
}

export interface RemitAdjustmentPayload {
  id?: number | null; // Read-only
  remit_service_line: number; // Required
  group_code: string; // Required
  reason_code: string; // Required
  amount?: number | null;
}

export interface RemitRemarkPayload {
  id?: number; // Read-only
  remit_service_line?: number | null;
  qualifier: string; // Required
  code: string; // Required
}
